/**
 * Crazy Cribbage - ModalManager
 * Encapsulates modal dialogs, overlays, closable behavior, and confirmation modals.
 */

import { DiscardTrainer } from '../trainer/DiscardTrainer.js';
import { PeggingTrainer, PEGGING_CATEGORIES } from '../trainer/PeggingTrainer.js';
import { CountingTrainer } from '../trainer/CountingTrainer.js';
import { CoachPostMortem } from '../coach/CoachPostMortem.js';
import { DailyPuzzleManager } from '../trainer/DailyPuzzleManager.js';
import { HandScoreTracker } from '../stats/HandScoreTracker.js';
import { HandOfGloryCard } from './HandOfGloryCard.js';
import { appStorage } from '../storage/IndexedDBStorage.js';
import { sanitizeUserInput } from '../utils/Sanitizer.js';
import { RANK_NAME, SUIT_SYM } from '../scoring/ScoringConstants.js';

function getStorage() {
  return (typeof window !== 'undefined' && window.appStorage) || appStorage || (typeof localStorage !== 'undefined' ? localStorage : null);
}

export class ModalManager {
  constructor(options = {}) {
    this.options = options;
    this.currentTheme = options.theme || 'tournament';
  }

  setTheme(theme) {
    this.currentTheme = theme;
  }

  /**
   * Displays a modal dialog with HTML content.
   * @param {string|object} html - HTML string or configuration object
   * @param {boolean|object} closable - Whether the modal has a visible close button
   */
  showModal(html, closable = false) {
    if (typeof document === 'undefined') return;

    let shouldShowClose = false;
    let onCloseCallback = null;
    if (typeof closable === 'boolean') {
      shouldShowClose = closable;
    } else if (typeof closable === 'object' && closable !== null) {
      shouldShowClose = Boolean(
        closable.showClose ?? closable.closable ?? closable.closeButton ?? closable.showCloseButton ?? closable.close
      );
      if (typeof closable.onClose === 'function') {
        onCloseCallback = closable.onClose;
      }
    } else if (typeof html === 'boolean') {
      shouldShowClose = html;
      html = '';
    } else if (typeof html === 'object' && html !== null && !('nodeType' in html)) {
      if ('showClose' in html || 'closable' in html || 'closeButton' in html || 'showCloseButton' in html) {
        shouldShowClose = Boolean(html.showClose ?? html.closable ?? html.closeButton ?? html.showCloseButton);
        html = html.html || html.content || '';
      }
      if (typeof html.onClose === 'function') {
        onCloseCallback = html.onClose;
      }
    }
    this._activeModalOnClose = onCloseCallback;

    const modal = document.getElementById('modal');
    const overlay = document.getElementById('overlay');
    const modalBar = document.getElementById('modalBar');
    const modalClose = document.getElementById('modalClose');

    const activeTheme = (this && this.currentTheme) || (typeof window !== 'undefined' && window.theme) || 'tournament';

    if (modal) {
      modal.setAttribute('data-theme', activeTheme);
      modal.innerHTML = html != null ? html : '';
      const innerCloseBtns = modal.querySelectorAll('button[onclick*="hideModal"], button[onclick*="closeModal"], .modal-btn-close, [data-modal-close]');
      innerCloseBtns.forEach(btn => {
        btn.onclick = (e) => {
          if (e && e.stopPropagation) e.stopPropagation();
          if (this && typeof this.hideModal === 'function') {
            this.hideModal();
          } else if (typeof window !== 'undefined' && typeof window.hideModal === 'function') {
            window.hideModal();
          } else if (typeof ModalManager !== 'undefined' && typeof ModalManager.hideModal === 'function') {
            ModalManager.hideModal();
          }
        };
      });
    }

    if (overlay) {
      const fsElem = (typeof document !== 'undefined') ? (document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement) : null;
      const b3dElem = (typeof document !== 'undefined' && typeof document.querySelector === 'function')
        ? document.querySelector('.board-3d-container.is-fullscreen, .board-3d-container:fullscreen')
        : null;
      const targetParent = fsElem || b3dElem;
      if (targetParent && overlay.parentElement !== targetParent) {
        targetParent.appendChild(overlay);
      }
      overlay.setAttribute('data-theme', activeTheme);
      overlay.style.display = 'flex';
      if (html && typeof html === 'string' && (html.includes('signs-modal-wrap') || html.includes('muggins-count-box') || html.includes('stolen-crib-box') || html.includes('guess-starter-box') || html.includes('show-counter-modal'))) {
        overlay.classList.add('signs-modal-center');
      } else {
        overlay.classList.remove('signs-modal-center');
      }
      if (html && typeof html === 'string' && html.includes('coach-post-mortem-modal')) {
        overlay.classList.add('has-coach-post-mortem');
        if (modal) modal.classList.add('coach-post-mortem-container');
      } else {
        overlay.classList.remove('has-coach-post-mortem');
        if (modal) modal.classList.remove('coach-post-mortem-container');
      }
      if (html && typeof html === 'string' && html.includes('dropdown-info-modal')) {
        overlay.classList.add('has-dropdown-info');
        overlay.style.zIndex = '2500';
      } else {
        overlay.classList.remove('has-dropdown-info');
        overlay.style.zIndex = '';
      }
      if (shouldShowClose) {
        overlay.classList.add('closable');
      } else {
        overlay.classList.remove('closable');
      }
      overlay.onclick = (e) => {
        if (e && e.target === overlay && overlay.classList.contains('closable')) {
          if (this && typeof this.hideModal === 'function') {
            this.hideModal();
          } else if (typeof window !== 'undefined' && typeof window.hideModal === 'function') {
            window.hideModal();
          }
        }
      };
    }

    if (modalBar) {
      modalBar.style.display = shouldShowClose ? 'flex' : 'none';
    }
    if (modalClose) {
      modalClose.style.display = shouldShowClose ? 'inline-flex' : 'none';
    }
  }

  /**
   * Hides the active modal and overlay.
   * @param {object} state - Optional game state to clear passingTo
   */
  hideModal(state = null) {
    if (this._activeModalOnClose) {
      const onCloseCb = this._activeModalOnClose;
      this._activeModalOnClose = null;
      onCloseCb();
      return;
    }

    if (typeof document === 'undefined') return;

    if (state && state.passingTo) {
      state.passingTo = null;
    } else if (typeof window !== 'undefined' && window.state && window.state.passingTo) {
      window.state.passingTo = null;
    }

    const overlay = document.getElementById('overlay');
    if (overlay) {
      const fsElem = (typeof document !== 'undefined') ? (document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement) : null;
      const b3dElem = (typeof document !== 'undefined' && typeof document.querySelector === 'function')
        ? document.querySelector('.board-3d-container.is-fullscreen, .board-3d-container:fullscreen')
        : null;
      if (overlay.parentElement !== document.body && !fsElem && !b3dElem) {
        document.body.appendChild(overlay);
      }
      overlay.style.display = 'none';
      overlay.classList.remove('closable');
      overlay.classList.remove('signs-modal-center');
      overlay.classList.remove('has-coach-post-mortem');
      overlay.classList.remove('has-dropdown-info');
      overlay.style.zIndex = '';

      const gloryOverlay = document.getElementById('gloryOverlay');
      if (gloryOverlay) {
        gloryOverlay.style.display = 'none';
        if (gloryOverlay.parentElement !== document.body && !fsElem && !b3dElem && document.body) {
          document.body.appendChild(gloryOverlay);
        }
      }

      const board3DModal = document.getElementById('board3DModal');
      if (board3DModal) {
        board3DModal.style.display = 'none';
        if (board3DModal.parentElement !== document.body && !fsElem && !b3dElem && document.body) {
          document.body.appendChild(board3DModal);
        }
      }

      const avatarModalOverlay = document.getElementById('avatarModalOverlay');
      if (avatarModalOverlay) {
        avatarModalOverlay.style.display = 'none';
        if (avatarModalOverlay.parentElement !== document.body && !fsElem && !b3dElem && document.body) {
          document.body.appendChild(avatarModalOverlay);
        }
      }
    }
    const modal = document.getElementById('modal');
    if (modal) {
      modal.classList.remove('coach-post-mortem-container');
    }
    const modalBar = document.getElementById('modalBar');
    if (modalBar) {
      modalBar.style.display = 'none';
    }
    const modalClose = document.getElementById('modalClose');
    if (modalClose) {
      modalClose.style.display = 'none';
    }
  }

  showOverlay(html, closable = false) {
    return this.showModal(html, closable);
  }

  hideOverlay() {
    return this.hideModal();
  }

  closeModal() {
    return this.hideModal();
  }

  closeOverlay() {
    return this.hideModal();
  }

  /**
   * Displays the dedicated Start Game Quick-Setup Modal.
   * @param {object} [context]
   */
  showStartGameModal(context = {}) {
    const getAppBrand = (context && typeof context.getAppBrand === 'function')
      ? context.getAppBrand
      : (typeof window !== 'undefined' && typeof window.getAppBrand === 'function')
        ? window.getAppBrand
        : () => 'crazy';
    const isAgeVerified = context.isAgeVerified || (typeof window !== 'undefined' && window.isAgeVerified ? window.isAgeVerified : ((b) => (b !== 'pints' && b !== 'adult')));
    const showAgeGateModal = context.showAgeGateModal || (typeof window !== 'undefined' ? window.showAgeGateModal : () => {});
    const brand = getAppBrand();
    if ((brand === 'pints' || brand === 'adult') && !isAgeVerified(brand)) {
      showAgeGateModal(brand);
      return;
    }

    const overlay = document.getElementById('startGameOverlay');
    if (!overlay) return;

    const fsElem = (typeof document !== 'undefined') ? (document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement) : null;
    if (fsElem && overlay.parentElement !== fsElem) {
      fsElem.appendChild(overlay);
    }

    const syncPlayerCountUi = context.syncPlayerCountUi || (typeof window !== 'undefined' ? window.syncPlayerCountUi : null);
    if (typeof syncPlayerCountUi === 'function') {
      syncPlayerCountUi();
    }

    const titleEl = document.getElementById('startGameModalTitle');
    const badgeEl = document.getElementById('startGameModalBadge');
    const btnTextEl = document.getElementById('btnConfirmStartGameText');
    if (brand === 'clash') {
      if (titleEl) titleEl.textContent = 'Cribbage Clash';
      if (badgeEl) badgeEl.textContent = '⚔️ BATTLE SETUP';
      if (btnTextEl) btnTextEl.textContent = 'BEGIN BATTLE';
    } else if (brand === 'pints') {
      if (titleEl) titleEl.textContent = 'Pints & Pegs';
      if (badgeEl) badgeEl.textContent = '🍺 PUB SETUP';
      if (btnTextEl) btnTextEl.textContent = 'PLAY PINTS';
    } else if (brand === 'adult') {
      if (titleEl) titleEl.textContent = 'Skunked & Stripped';
      if (badgeEl) badgeEl.textContent = '🔥 INTIMATE SETUP';
      if (btnTextEl) btnTextEl.textContent = 'PLAY SKUNKED';
    } else {
      if (titleEl) titleEl.textContent = 'Crazy Cribbage';
      if (badgeEl) badgeEl.textContent = '🎮 QUICK SETUP';
      if (btnTextEl) btnTextEl.textContent = 'START GAME';
    }

    const optBoardViewModal = document.getElementById('optBoardViewModal');
    const optBoardView = document.getElementById('optBoardView');
    const curMode = (window.settings && window.settings.boardViewMode && window.settings.boardViewMode !== '3d' && window.settings.boardViewMode !== 'room3d')
      ? window.settings.boardViewMode
      : ((optBoardViewModal && optBoardViewModal.value) || (optBoardView && optBoardView.value) || (window.settings && window.settings.boardViewMode) || 'room3d-fullscreen');
    if (optBoardViewModal) {
      optBoardViewModal.value = curMode;
    }
    if (optBoardView) {
      optBoardView.value = curMode;
    }
    if (typeof window !== 'undefined' && window.settings) {
      window.settings.boardViewMode = curMode;
    }

    overlay.style.display = 'flex';
    overlay.setAttribute('data-brand', brand);
  }

  /**
   * Hides the Start Game Quick-Setup Modal.
   */
  hideStartGameModal() {
    if (typeof document === 'undefined') return;
    const overlay = document.getElementById('startGameOverlay');
    if (overlay) {
      if (overlay.parentElement !== document.body && !document.fullscreenElement) {
        document.body.appendChild(overlay);
      }
      overlay.style.display = 'none';
    }
  }

  /**
   * Displays a stylized confirmation modal.
   * @param {object} opts
   */
  showConfirmModal(opts = {}) {
    const {
      title = 'Start a New Game?',
      message = 'Your current match progress will be lost. Are you sure you want to return to the main menu and start a new game?',
      confirmText = 'Start New Game',
      cancelText = 'Keep Playing',
      danger = false,
      icon = null,
      onConfirm = () => {},
      onCancel = () => {}
    } = opts;

    const defaultIcon = danger
      ? `<svg class="confirm-icon-svg" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        </svg>`
      : `<svg class="confirm-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9" stroke="var(--gold, #f0c64a)"/>
          <path d="M12 8v5" stroke="var(--gold, #f0c64a)"/>
          <circle cx="12" cy="16.5" r="1" fill="var(--gold, #f0c64a)" stroke="none"/>
          <path d="M21 3v6h-6" stroke="var(--gold, #f0c64a)"/>
        </svg>`;

    const confirmBtnClass = danger ? 'btn-danger' : 'btn-gold';

    const modalHtml = `
      <div class="fancy-confirm-modal" role="dialog" aria-labelledby="confirmModalTitle" aria-describedby="confirmModalMsg">
        <div class="confirm-icon-ring ${danger ? 'danger' : ''}" aria-hidden="true">
          ${icon || defaultIcon}
        </div>
        <h2 class="confirm-title" id="confirmModalTitle">${title}</h2>
        <p class="confirm-message" id="confirmModalMsg">${message}</p>
        <div class="confirm-actions">
          <button type="button" class="${confirmBtnClass} confirm-btn-yes" id="btnFancyConfirmYes">${confirmText}</button>
          <button type="button" class="btn-ghost confirm-btn-no" id="btnFancyConfirmNo">${cancelText}</button>
        </div>
      </div>
    `;

    this.showModal(modalHtml, true);

    if (typeof document !== 'undefined') {
      const btnYes = document.getElementById('btnFancyConfirmYes');
      const btnNo = document.getElementById('btnFancyConfirmNo');

      if (btnYes) {
        btnYes.onclick = () => {
          this.hideModal();
          onConfirm();
        };
      }
      if (btnNo) {
        btnNo.onclick = () => {
          this.hideModal();
          onCancel();
        };
      }
    }
  }

  /**
   * Returns to the main menu from an active or completed game.
   * @param {Object} [context]
   */
  static backToMenu(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const hideModal = context.hideModal || (typeof window !== 'undefined' ? window.hideModal : () => {});
    const showConfirmModal = context.showConfirmModal || (typeof window !== 'undefined' ? window.showConfirmModal : () => {});
    const renderRecordBox = context.renderRecordBox || (typeof window !== 'undefined' ? window.renderRecordBox : () => {});

    const navigate = () => {
      hideModal();
      try {
        const camMgr = (typeof window !== 'undefined' && (window._ccCameraManager || (window.CrazyCribbage && window.CrazyCribbage.CameraManager && window.CrazyCribbage.CameraManager.getInstance())));
        if (camMgr && camMgr.isBroadcasting) {
          camMgr.stopBroadcast();
        }
        const chatMgr = (typeof window !== 'undefined' && (window._ccChatManager || (window.CrazyCribbage && window.CrazyCribbage.ChatManager && window.CrazyCribbage.ChatManager.getInstance())));
        if (chatMgr) {
          chatMgr.setChatVisibility(false);
          chatMgr.clearChat();
        }
      } catch (e) {}
      if (typeof document !== 'undefined') {
        const game = document.getElementById('game');
        const menu = document.getElementById('menu');
        if (game) game.classList.add('hidden');
        if (menu) menu.classList.remove('hidden');
      }
      renderRecordBox();
    };

    if (state && state.over) {
      navigate();
      return;
    }

    showConfirmModal({
      title: 'Start a New Game?',
      message: 'Your current match progress will be lost. Are you sure you want to return to the main menu and start a new game?',
      confirmText: 'Start New Game',
      cancelText: 'Keep Playing',
      onConfirm: navigate
    });
  }

  /**
   * Returns to the main menu via the brand logo button.
   * @param {Object} [context]
   */
  static returnToMenu(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const hideModal = context.hideModal || (typeof window !== 'undefined' ? window.hideModal : () => {});
    const showConfirmModal = context.showConfirmModal || (typeof window !== 'undefined' ? window.showConfirmModal : () => {});
    const renderRecordBox = context.renderRecordBox || (typeof window !== 'undefined' ? window.renderRecordBox : () => {});

    const navigate = () => {
      hideModal();
      try {
        if (typeof window !== 'undefined' && window._currentRenderer3D && typeof window._currentRenderer3D.exitFullScreen === 'function') {
          window._currentRenderer3D.exitFullScreen();
        }
      } catch (e) {}
      if (typeof document !== 'undefined') {
        try {
          if (document.body) document.body.classList.remove('is-3d-fullscreen');
          const b3d = document.getElementById('board3DContainer');
          if (b3d) b3d.classList.remove('is-fullscreen');
        } catch (e) {}
      }
      try {
        const camMgr = (typeof window !== 'undefined' && (window._ccCameraManager || (window.CrazyCribbage && window.CrazyCribbage.CameraManager && window.CrazyCribbage.CameraManager.getInstance())));
        if (camMgr && camMgr.isBroadcasting) {
          camMgr.stopBroadcast();
        }
        const chatMgr = (typeof window !== 'undefined' && (window._ccChatManager || (window.CrazyCribbage && window.CrazyCribbage.ChatManager && window.CrazyCribbage.ChatManager.getInstance())));
        if (chatMgr) {
          chatMgr.setChatVisibility(false);
          chatMgr.clearChat();
        }
      } catch (e) {}
      if (typeof document !== 'undefined') {
        const game = document.getElementById('game');
        const menu = document.getElementById('menu');
        if (game) game.classList.add('hidden');
        if (menu) menu.classList.remove('hidden');
      }
      renderRecordBox();
    };

    if (state && state.over) {
      navigate();
      return;
    }

    showConfirmModal({
      title: 'Return to Main Menu?',
      message: 'Your current match progress will be lost. Are you sure you want to return to the main menu and start a new game?',
      confirmText: 'Return to Main Menu',
      cancelText: 'Keep Playing',
      onConfirm: navigate
    });
  }

  backToMenu(context) {
    return ModalManager.backToMenu(context);
  }

  returnToMenu(context) {
    return ModalManager.returnToMenu(context);
  }

  /**
   * Displays the social and peer sharing modal.
   * @param {Object} [context]
   */
  showShareModal(context = {}) {
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null) || {};
    const net = context.net || (typeof window !== 'undefined' ? window.net : null) || {};
    const getJoinUrl = context.getJoinUrl || (typeof window !== 'undefined' ? window.getJoinUrl : null);

    const isOnlineHost = settings.opp === 'online' && net.room;
    const shareTitle = 'Crazy Cribbage';
    const shareText = isOnlineHost
      ? `Join my Crazy Cribbage online room! Room code: ${net.room}`
      : 'Play Crazy Cribbage — the wild twist on classic cribbage! 50+ point hands, jokers, and custom boards!';
    const shareUrl = isOnlineHost && typeof getJoinUrl === 'function'
      ? getJoinUrl(net.room)
      : (typeof window !== 'undefined' && window.location ? window.location.href.split('#')[0] : 'https://crazycribbage.com');

    const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedText = encodeURIComponent(shareText);
    const encodedTitle = encodeURIComponent(shareTitle);

    const platforms = [
      {
        name: 'X (Twitter)',
        color: '#000000',
        border: 'rgba(255,255,255,0.25)',
        icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>',
        url: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`
      },
      {
        name: 'Facebook',
        color: '#1877F2',
        border: 'rgba(24,119,242,0.5)',
        icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>',
        url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`
      },
      {
        name: 'WhatsApp',
        color: '#25D366',
        border: 'rgba(37,211,102,0.5)',
        icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12.031 2c-5.508 0-9.984 4.476-9.984 9.984 0 1.76.459 3.478 1.333 4.992L2 22.062l5.244-1.375c1.464.799 3.116 1.22 4.787 1.22h.005c5.508 0 9.984-4.476 9.984-9.984 0-2.668-1.039-5.176-2.925-7.062A9.92 9.92 0 0 0 12.031 2zm0 18.291h-.004a8.28 8.28 0 0 1-4.22-1.154l-.303-.18-3.136.822.837-3.056-.197-.314a8.26 8.26 0 0 1-1.267-4.425c0-4.57 3.719-8.289 8.29-8.289 2.215 0 4.297.863 5.863 2.43 1.566 1.566 2.428 3.649 2.427 5.864 0 4.571-3.719 8.29-8.29 8.29z"/></svg>',
        url: `https://api.whatsapp.com/send?text=${encodedText}%20${encodedUrl}`
      },
      {
        name: 'Reddit',
        color: '#FF4500',
        border: 'rgba(255,69,0,0.5)',
        icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="10"/><path fill="#fff" d="M12 4.5c.8 0 1.5.7 1.5 1.5 0 .2-.05.4-.13.58l2.06 1.03c.53-.4 1.2-.61 1.92-.61 1.74 0 3.15 1.41 3.15 3.15 0 .73-.24 1.4-.66 1.94.05.27.08.55.08.83 0 2.87-3.58 5.2-8 5.2s-8-2.33-8-5.2c0-.28.03-.56.08-.83-.42-.54-.66-1.21-.66-1.94 0-1.74 1.41-3.15 3.15-3.15.72 0 1.39.21 1.92.61l2.06-1.03c-.08-.18-.13-.38-.13-.58 0-.8.7-1.5 1.5-1.5zm-3 8a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5zm6 0a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5zm-5.5 2.5c.67.67 1.5 1 2.5 1s1.83-.33 2.5-1"/></svg>',
        url: `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`
      },
      {
        name: 'LinkedIn',
        color: '#0A66C2',
        border: 'rgba(10,102,194,0.5)',
        icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 10.9v8.37H9.2V10.9H6.46M7.83 6.45c-.9 0-1.63.73-1.63 1.63 0 .9.73 1.63 1.63 1.63.9 0 1.63-.73 1.63-1.63 0-.9-.73-1.63-1.63-1.63z"/></svg>',
        url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`
      },
      {
        name: 'Telegram',
        color: '#229ED9',
        border: 'rgba(34,158,217,0.5)',
        icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 0 0-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/></svg>',
        url: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`
      },
      {
        name: 'Email',
        color: '#374151',
        border: 'rgba(255,255,255,0.25)',
        icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>',
        url: `mailto:?subject=${encodedTitle}&body=${encodedText}%0A%0A${encodedUrl}`
      }
    ];

    const gridHtml = platforms.map(p => `
      <a href="${p.url}" target="_blank" rel="noopener noreferrer" class="social-share-btn" style="--btn-color:${p.color};--btn-border:${p.border};">
        <span class="social-share-icon">${p.icon}</span>
        <span class="social-share-name">${p.name}</span>
      </a>
    `).join('');

    const modalHtml = `
      <div class="share-modal-content">
        <h2>Share Crazy Cribbage</h2>
        <div style="color:#d7e8dc;font-size:0.9rem;margin:6px 0 16px;">
          ${isOnlineHost ? `Join my Crazy Cribbage online room! Room code: ${net.room}` : 'Share the fun with friends or challenge other Cribbage players!'}
        </div>
        
        <div class="share-copy-box">
          <input type="text" readonly value="${shareUrl}" id="shareLinkInput" class="share-link-input" />
          <button type="button" class="btn-gold" id="btnCopyShareLink">Copy Link</button>
        </div>

        <div class="social-share-grid">
          ${gridHtml}
        </div>

        ${canNativeShare ? `
          <div style="margin-top:14px;">
            <button type="button" class="btn-gold" id="btnNativeShare" style="width:100%;display:inline-flex;align-items:center;justify-content:center;gap:8px;">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/></svg>
              <span>More Options (Device Share)</span>
            </button>
          </div>
        ` : ''}

        <div style="margin-top:18px;">
          <button type="button" class="btn-ghost modal-btn-close" id="btnCloseShare">Close</button>
        </div>
      </div>
    `;

    this.showModal(modalHtml, true);

    if (typeof document !== 'undefined') {
      const input = document.getElementById('shareLinkInput') || document.getElementById('shareUrlInput');
      if (input) {
        input.focus();
        input.select();
      }
      const copyBtn = document.getElementById('btnCopyShareLink');
      if (copyBtn) {
        copyBtn.addEventListener('click', () => {
          if (typeof copyShareLink === 'function') copyShareLink();
          else ModalManager.copyShareLink();
        });
      }
      const nativeBtn = document.getElementById('btnNativeShare');
      if (nativeBtn) {
        nativeBtn.addEventListener('click', () => {
          if (typeof triggerNativeShare === 'function') triggerNativeShare();
          else ModalManager.triggerNativeShare();
        });
      }
    }
  }

  /**
   * Copies the share link to the user's clipboard and displays temporary confirmation text.
   */
  static copyShareLink() {
    if (typeof document === 'undefined') return;
    const input = document.getElementById('shareLinkInput') || document.getElementById('shareUrlInput');
    const btn = document.getElementById('btnCopyShareLink') || document.getElementById('btnCopyShare');
    if (input) {
      if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        navigator.clipboard.writeText(input.value).then(() => {
          if (btn) {
            const orig = btn.textContent;
            btn.textContent = 'Copied!';
            btn.style.filter = 'brightness(1.2)';
            setTimeout(() => {
              if (btn) {
                btn.textContent = orig;
                btn.style.filter = '';
              }
            }, 2000);
          }
        }).catch(() => {
          input.select();
          try {
            document.execCommand('copy');
            if (btn) {
              const orig = btn.textContent;
              btn.textContent = 'Copied!';
              setTimeout(() => { if (btn) btn.textContent = orig; }, 2000);
            }
          } catch (e) {}
        });
      } else {
        input.select();
        try {
          document.execCommand('copy');
          if (btn) {
            const orig = btn.textContent;
            btn.textContent = 'Copied!';
            setTimeout(() => { if (btn) btn.textContent = orig; }, 2000);
          }
        } catch (e) {}
      }
    }
  }

  copyShareLink() {
    return ModalManager.copyShareLink();
  }

  /**
   * Displays the Host & Join Online Game modal with 1-click shareable invite link generator.
   * @param {Object} context
   */
  showJoinModal(context = {}) {
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null) || {};
    const net = context.net || (typeof window !== 'undefined' ? window.net : null) || {};
    const joinRoomFn = context.joinRoom || (typeof window !== 'undefined' ? window.joinRoom : null);
    const hostRoomFn = context.hostRoom || (typeof window !== 'undefined' ? window.hostRoom : null);
    const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : null);
    const hideModalFn = () => this.hideModal();

    let savedName = '';
    const s = getStorage();
    if (s) {
      try { savedName = s.getItem('cc_player_name') || ''; } catch (e) {}
    }
    if (!savedName && typeof document !== 'undefined') {
      const optNameEl = document.getElementById('optPlayerName');
      if (optNameEl && optNameEl.value) savedName = optNameEl.value.trim();
    }
    if (!savedName) savedName = 'Player';

    const getJoinUrl = context.getJoinUrl || (typeof window !== 'undefined' && typeof window.getJoinUrl === 'function' ? window.getJoinUrl : (typeof NetworkManager !== 'undefined' ? NetworkManager.getJoinUrl : (c => `?room=${c}`)));
    const generateCode = (typeof NetworkManager !== 'undefined' && typeof NetworkManager.generateRoomCode === 'function')
      ? NetworkManager.generateRoomCode
      : (() => 'CC' + Math.floor(1000 + Math.random() * 9000));

    let hostCode = (net && net.room) || (typeof document !== 'undefined' && document.getElementById('optRoom') && document.getElementById('optRoom').value) || generateCode();
    hostCode = hostCode.trim().toUpperCase();
    let hostInviteUrl = getJoinUrl(hostCode);

    const modalHtml = `
      <div class="online-table-modal" style="max-width: 460px; margin: 0 auto; text-align: center;">
        <div style="display: flex; justify-content: center; gap: 8px; margin-bottom: 16px; border-bottom: 1px solid rgba(240, 198, 74, 0.2); padding-bottom: 10px;">
          <button type="button" id="tabHostBtn" class="online-modal-tab is-active" style="flex: 1; padding: 10px 14px; font-size: 0.95rem; font-weight: 800; border-radius: 8px; border: 1px solid #f0c64a; background: rgba(240, 198, 74, 0.18); color: #f0c64a; cursor: pointer;">
            👑 HOST TABLE
          </button>
          <button type="button" id="tabJoinBtn" class="online-modal-tab" style="flex: 1; padding: 10px 14px; font-size: 0.95rem; font-weight: 800; border-radius: 8px; border: 1px solid rgba(255,255,255,0.15); background: rgba(0,0,0,0.3); color: #d6d3d1; cursor: pointer;">
            🌐 JOIN TABLE
          </button>
        </div>

        <!-- HOST VIEW -->
        <div id="hostViewSection">
          <h2 style="font-size: 1.35rem; font-weight: 800; color: #f0c64a; margin: 0 0 6px;">Host Online Table</h2>
          <p style="font-size: 0.88rem; color: #d6d3d1; margin: 0 0 16px; line-height: 1.4;">
            Share this 1-click link with a friend on phone or desktop. When they click, they connect automatically!
          </p>

          <div style="background: rgba(0,0,0,0.5); border: 1px solid rgba(240,198,74,0.35); border-radius: 10px; padding: 14px; margin-bottom: 16px; text-align: left;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
              <span style="font-size: 0.78rem; font-weight: 700; color: #f0c64a; text-transform: uppercase; letter-spacing: 0.06em;">Room Code</span>
              <button type="button" id="btnRefreshHostCode" style="background: transparent; border: none; color: #f0c64a; font-size: 0.8rem; font-weight: 600; cursor: pointer;" title="Generate new code">🎲 New Code</button>
            </div>
            <div id="hostCodeDisplay" style="font-family: monospace; font-size: 1.5rem; font-weight: 900; color: #f0c64a; letter-spacing: 0.16em; text-align: center; margin-bottom: 12px; padding: 6px; background: rgba(240,198,74,0.08); border-radius: 6px;">
              ${hostCode}
            </div>

            <label for="hostDirectInviteInput" style="display: block; font-size: 0.78rem; font-weight: 700; color: #d6d3d1; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px;">1-Click Invite Link</label>
            <div style="display: flex; gap: 8px;">
              <input id="hostDirectInviteInput" type="text" readonly value="${hostInviteUrl}"
                     style="flex: 1; box-sizing: border-box; padding: 10px 12px; font-size: 0.86rem; font-family: monospace; border-radius: 8px; border: 1px solid rgba(240, 198, 74, 0.4); background: #1a1208; color: #f4ead4; outline: none;" />
              <button type="button" class="btn-gold" id="btnCopyDirectInvite" style="padding: 10px 16px; font-size: 0.88rem; font-weight: 800; white-space: nowrap; display: inline-flex; align-items: center; gap: 6px;">
                <span>📋 Copy Link</span>
              </button>
            </div>
          </div>

          <div style="display: flex; gap: 10px;">
            <button type="button" class="btn-gold" id="btnHostStartTable" style="flex: 1; padding: 12px 18px; font-size: 1.05rem; font-weight: 800; letter-spacing: 0.03em;">
              👑 START HOSTING
            </button>
            <button type="button" class="btn-ghost modal-btn-close" style="padding: 12px 16px;">Cancel</button>
          </div>
        </div>

        <!-- JOIN VIEW -->
        <div id="joinViewSection" style="display: none;">
          <h2 style="font-size: 1.35rem; font-weight: 800; color: #f0c64a; margin: 0 0 6px;">Join Table</h2>
          <p style="font-size: 0.88rem; color: #d6d3d1; margin: 0 0 16px; line-height: 1.4;">
            Enter the room code or paste the invite link shared by the host:
          </p>

          <form id="joinModalForm" style="display: flex; flex-direction: column; gap: 14px; text-align: left; margin-bottom: 20px;">
            <div>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
                <label for="joinModalRoomCode" style="font-size: 0.8rem; font-weight: 700; color: #f0c64a; text-transform: uppercase; letter-spacing: 0.05em;">Room Code or Link</label>
                <button type="button" id="btnPasteFromClipboard" style="background: transparent; border: none; color: #f0c64a; font-size: 0.8rem; font-weight: 600; cursor: pointer;">📋 Paste</button>
              </div>
              <input id="joinModalRoomCode" type="text" maxlength="80" placeholder="e.g. CC4819 or paste link" autocomplete="off" autocapitalize="characters" spellcheck="false"
                     style="width: 100%; box-sizing: border-box; padding: 12px 14px; font-size: 1.15rem; font-family: monospace; font-weight: 800; letter-spacing: 0.1em; text-align: center; border-radius: 10px; border: 2px solid #f0c64a; background: #1a1208; color: #f0c64a; outline: none;" />
              <div id="joinModalError" style="display: none; color: #f87171; font-size: 0.82rem; font-weight: 600; margin-top: 6px; text-align: center;">Please enter a valid room code.</div>
            </div>

            <div>
              <label for="joinModalPlayerName" style="display: block; font-size: 0.8rem; font-weight: 700; color: #d6d3d1; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px;">Your Name</label>
              <input id="joinModalPlayerName" type="text" maxlength="16" placeholder="Your Name" value="${savedName.replace(/"/g, '&quot;')}" pattern="[a-zA-Z0-9]+"
                     style="width: 100%; box-sizing: border-box; padding: 10px 14px; font-size: 1rem; border-radius: 8px; border: 1px solid rgba(240, 198, 74, 0.4); background: rgba(0,0,0,0.5); color: #f4ead4; outline: none;" />
            </div>

            <div style="display: flex; gap: 10px; margin-top: 6px;">
              <button type="button" class="btn-gold" id="btnJoinModalSubmit" style="flex: 1; padding: 12px 20px; font-size: 1.05rem; font-weight: 800; letter-spacing: 0.04em; display: inline-flex; align-items: center; justify-content: center; gap: 8px;">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.65 10C11.83 7.67 9.61 6 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6c2.61 0 4.83-1.67 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/></svg>
                <span>JOIN TABLE</span>
              </button>
              <button type="button" class="btn-ghost modal-btn-close" style="padding: 12px 18px;">Cancel</button>
            </div>
          </form>
        </div>
      </div>
    `;

    this.showModal(modalHtml, true);

    if (typeof document !== 'undefined') {
      const tabHostBtn = document.getElementById('tabHostBtn');
      const tabJoinBtn = document.getElementById('tabJoinBtn');
      const hostView = document.getElementById('hostViewSection');
      const joinView = document.getElementById('joinViewSection');

      const setTab = (isHost) => {
        if (tabHostBtn && tabJoinBtn && hostView && joinView) {
          if (isHost) {
            tabHostBtn.style.background = 'rgba(240, 198, 74, 0.18)';
            tabHostBtn.style.color = '#f0c64a';
            tabHostBtn.style.borderColor = '#f0c64a';
            tabJoinBtn.style.background = 'rgba(0,0,0,0.3)';
            tabJoinBtn.style.color = '#d6d3d1';
            tabJoinBtn.style.borderColor = 'rgba(255,255,255,0.15)';
            hostView.style.display = 'block';
            joinView.style.display = 'none';
          } else {
            tabJoinBtn.style.background = 'rgba(240, 198, 74, 0.18)';
            tabJoinBtn.style.color = '#f0c64a';
            tabJoinBtn.style.borderColor = '#f0c64a';
            tabHostBtn.style.background = 'rgba(0,0,0,0.3)';
            tabHostBtn.style.color = '#d6d3d1';
            tabHostBtn.style.borderColor = 'rgba(255,255,255,0.15)';
            hostView.style.display = 'none';
            joinView.style.display = 'block';
            const codeInput = document.getElementById('joinModalRoomCode');
            if (codeInput) codeInput.focus();
          }
        }
      };

      if (tabHostBtn) tabHostBtn.onclick = () => setTab(true);
      if (tabJoinBtn) tabJoinBtn.onclick = () => setTab(false);

      // Copy invite link button
      const copyBtn = document.getElementById('btnCopyDirectInvite');
      const inviteInp = document.getElementById('hostDirectInviteInput');
      if (copyBtn && inviteInp) {
        copyBtn.onclick = () => {
          const val = inviteInp.value;
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(val).then(() => {
              const orig = copyBtn.innerHTML;
              copyBtn.innerHTML = '<span>Copied! ✓</span>';
              if (sfx && typeof sfx.snap === 'function') sfx.snap();
              setTimeout(() => { if (copyBtn) copyBtn.innerHTML = orig; }, 2200);
            }).catch(() => {
              inviteInp.select();
              document.execCommand('copy');
            });
          } else {
            inviteInp.select();
            try { document.execCommand('copy'); } catch (e) {}
          }
        };
      }

      // Refresh host code button
      const refreshBtn = document.getElementById('btnRefreshHostCode');
      const codeDisplay = document.getElementById('hostCodeDisplay');
      if (refreshBtn && codeDisplay && inviteInp) {
        refreshBtn.onclick = () => {
          hostCode = generateCode();
          hostInviteUrl = getJoinUrl(hostCode);
          codeDisplay.textContent = hostCode;
          inviteInp.value = hostInviteUrl;
          const optRoomEl = document.getElementById('optRoom');
          if (optRoomEl) optRoomEl.value = hostCode;
        };
      }

      // Start hosting button
      const btnHostStart = document.getElementById('btnHostStartTable');
      if (btnHostStart) {
        btnHostStart.onclick = () => {
          const optRoomEl = document.getElementById('optRoom');
          if (optRoomEl) optRoomEl.value = hostCode;
          const optOppEl = document.getElementById('optOpp');
          if (optOppEl) {
            optOppEl.value = 'online';
            optOppEl.dispatchEvent(new Event('change'));
          }
          if (settings) {
            settings.opp = 'online';
            settings.netRole = 'host';
          }
          hideModalFn();
          if (typeof hostRoomFn === 'function') {
            hostRoomFn();
          } else if (typeof window !== 'undefined' && typeof window.hostRoom === 'function') {
            window.hostRoom();
          }
        };
      }

      // Join code parsing and submission
      const codeInput = document.getElementById('joinModalRoomCode');
      const nameInput = document.getElementById('joinModalPlayerName');
      const submitBtn = document.getElementById('btnJoinModalSubmit');
      const errorDiv = document.getElementById('joinModalError');

      const parseCodeFromInput = (str) => {
        let val = sanitizeUserInput(str || '');
        const match = /[?&](?:room|join)=([a-zA-Z0-9_-]+)/i.exec(val);
        if (match && match[1]) {
          val = match[1];
        }
        return val.toUpperCase();
      };

      if (codeInput) {
        codeInput.addEventListener('input', () => {
          if (errorDiv) errorDiv.style.display = 'none';
          codeInput.value = parseCodeFromInput(codeInput.value);
        });
        codeInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (submitBtn) submitBtn.click();
          }
        });
      }

      if (nameInput) {
        nameInput.addEventListener('input', () => {
          nameInput.value = sanitizeUserInput(nameInput.value).replace(/[^a-zA-Z0-9]/g, '');
        });
      }

      const joinForm = document.getElementById('joinModalForm');
      if (joinForm) {
        joinForm.addEventListener('submit', (e) => e.preventDefault());
      }

      // Paste from clipboard button
      const pasteBtn = document.getElementById('btnPasteFromClipboard');
      if (pasteBtn && codeInput) {
        pasteBtn.onclick = async () => {
          try {
            if (navigator.clipboard && navigator.clipboard.readText) {
              const text = await navigator.clipboard.readText();
              if (text) {
                codeInput.value = parseCodeFromInput(text);
                if (errorDiv) errorDiv.style.display = 'none';
              }
            }
          } catch (e) {}
        };
      }

      if (submitBtn) {
        submitBtn.onclick = (e) => {
          if (e && e.preventDefault) e.preventDefault();
          const code = parseCodeFromInput(codeInput ? codeInput.value : '');

          if (!code) {
            if (errorDiv) {
              errorDiv.textContent = 'Please enter a valid room code or link.';
              errorDiv.style.display = 'block';
            }
            if (codeInput) codeInput.focus();
            return;
          }

          const rawEnteredName = nameInput ? nameInput.value : '';
          const enteredName = sanitizeUserInput(rawEnteredName).replace(/[^a-zA-Z0-9]/g, '').trim();
          if (enteredName) {
            try {
              const s = getStorage();
              if (s) s.setItem('cc_player_name', enteredName);
            } catch (err) {}
          }

          const optNameEl = document.getElementById('optPlayerName');
          if (optNameEl && enteredName) optNameEl.value = enteredName;

          const optRoomEl = document.getElementById('optRoom');
          if (optRoomEl) optRoomEl.value = code;

          const optOppEl = document.getElementById('optOpp');
          if (optOppEl) {
            optOppEl.value = 'online';
            optOppEl.dispatchEvent(new Event('change'));
          }

          if (settings) {
            settings.opp = 'online';
            settings.netRole = 'guest';
          }

          hideModalFn();

          if (typeof joinRoomFn === 'function') {
            joinRoomFn(code, { playerName: enteredName });
          } else if (typeof window !== 'undefined' && typeof window.joinRoom === 'function') {
            window.joinRoom(code, { playerName: enteredName });
          }
        };
      }
    }
  }

  /**
   * Displays the interactive Discard Trainer & Hand Analyzer modal.
   * @param {Object} [context]
   */
  showDiscardTrainerModal(context = {}) {
    if (typeof document === 'undefined') return;

    let trainerHand = context.hand || DiscardTrainer.dealRandomHand();
    let isDealer = context.isDealer !== undefined ? context.isDealer : true;
    let selectedIndices = new Set();
    let analysisResult = null;
    let evalResult = null;

    const suitSymbols = { H: '♥', D: '♦', S: '♠', C: '♣' };

    const renderTrainer = () => {
      analysisResult = DiscardTrainer.analyzeHand(trainerHand, isDealer);
      const isSelected = (idx) => selectedIndices.has(idx);

      const cardsHtml = trainerHand.map((c, idx) => {
        const isRed = c.suit === 'H' || c.suit === 'D';
        const sel = isSelected(idx);
        const textColor = isRed ? '#dc2626 !important' : '#111827 !important';
        return `
          <button type="button" class="trainer-card-btn ${sel ? 'selected' : ''} ${isRed ? 'card-red' : 'card-black'}" data-idx="${idx}"
                  style="position: relative; width: 74px; height: 106px; box-sizing: border-box; background: #ffffff !important; border: ${sel ? '3px solid #eab308 !important' : '2px solid #cbd5e1 !important'}; border-radius: 8px; cursor: pointer; display: flex; flex-direction: column; justify-content: space-between; padding: 6px 7px; box-shadow: ${sel ? '0 0 0 2px #ca8a04, 0 8px 18px rgba(234,179,8,0.45) !important' : '0 4px 10px rgba(0,0,0,0.35), 0 1px 3px rgba(0,0,0,0.15) !important'}; transform: ${sel ? 'translateY(-3px)' : 'none'}; transition: all 0.15s ease;"
                  title="${c.rankName || c.rank}${c.suit} - Click to toggle discard">
            <div style="display: flex; align-items: baseline; justify-content: flex-start; gap: 2px; line-height: 1;">
              <span class="card-val" style="font-size: 1.25rem; font-weight: 900; color: ${textColor}; -webkit-text-fill-color: ${textColor}; line-height: 1;">${c.rankName || c.rank}</span>
              <span class="card-val" style="font-size: 0.95rem; font-weight: 700; color: ${textColor}; -webkit-text-fill-color: ${textColor}; line-height: 1;">${suitSymbols[c.suit] || c.suit}</span>
            </div>
            <div class="card-val" style="font-size: 2.1rem; color: ${textColor}; -webkit-text-fill-color: ${textColor}; line-height: 1; text-align: center; margin: 2px 0;">${suitSymbols[c.suit] || c.suit}</div>
            <span class="trainer-discard-badge" style="font-size: 0.6rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.03em; background: ${sel ? '#ca8a04 !important' : 'transparent'}; color: ${sel ? '#ffffff !important' : 'transparent'}; -webkit-text-fill-color: ${sel ? '#ffffff !important' : 'transparent'}; border-radius: 4px; padding: 2px 0; line-height: 1.1; text-align: center; display: block; width: 100%; box-sizing: border-box; visibility: ${sel ? 'visible' : 'hidden'};">${sel ? 'DISCARD' : 'DISCARD'}</span>
          </button>
        `;
      }).join('');

      const career = DiscardTrainer.getCareerStats(context.storage) || {
        handsPlayed: 0,
        optimalCount: 0,
        currentStreak: 0,
        bestStreak: 0,
        averageLossEV: 0,
        accuracyPct: 0
      };

      let resultsHtml = '';
      if (evalResult) {
        const gradeBadgeColors = {
          optimal: '#10b981',
          great: '#06b6d4',
          good: '#f59e0b',
          suboptimal: '#ef4444'
        };
        const badgeColor = gradeBadgeColors[evalResult.grade] || '#f0c64a';

        const tableRowsHtml = analysisResult.combos.map((combo) => {
          const isUserPick = combo.id === evalResult.chosen.id;
          const isOptimalPick = combo.rank === 1;
          let rowBg = 'transparent';
          if (isUserPick && isOptimalPick) rowBg = 'rgba(16, 185, 129, 0.18)';
          else if (isUserPick) rowBg = 'rgba(240, 198, 74, 0.15)';
          else if (isOptimalPick) rowBg = 'rgba(16, 185, 129, 0.12)';

          const discMiniHtml = combo.disc.map(c => {
            const red = c.suit === 'H' || c.suit === 'D';
            const color = red ? '#dc2626 !important' : '#111827 !important';
            return `<span class="trainer-mini-card ${red ? 'card-red' : 'card-black'}" style="display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: 800; font-size: 0.82rem; background: #ffffff !important; color: ${color}; -webkit-text-fill-color: ${color}; border: 1px solid #cbd5e1; box-shadow: 0 1px 3px rgba(0,0,0,0.2); margin-right: 4px;">${c.rankName || c.rank}${suitSymbols[c.suit] || c.suit}</span>`;
          }).join('');

          const keepMiniHtml = combo.keep.map(c => {
            const red = c.suit === 'H' || c.suit === 'D';
            const color = red ? '#dc2626 !important' : '#111827 !important';
            return `<span class="trainer-mini-card ${red ? 'card-red' : 'card-black'}" style="display: inline-block; padding: 2px 5px; border-radius: 4px; font-weight: 700; font-size: 0.78rem; background: #ffffff !important; color: ${color}; -webkit-text-fill-color: ${color}; border: 1px solid #cbd5e1; box-shadow: 0 1px 2px rgba(0,0,0,0.15); margin-right: 3px;">${c.rankName || c.rank}${suitSymbols[c.suit] || c.suit}</span>`;
          }).join('');

          return `
            <tr style="background: ${rowBg}; border-bottom: 1px solid rgba(255,255,255,0.06); font-size: 0.82rem;">
              <td style="padding: 6px 8px; font-weight: 800; color: ${isOptimalPick ? '#10b981' : (isUserPick ? '#f0c64a' : '#a8a29e')}; text-align: center;">
                ${combo.rank}${isOptimalPick ? ' ★' : ''}${isUserPick ? ' 👈' : ''}
              </td>
              <td style="padding: 6px 8px; white-space: nowrap;">${discMiniHtml}</td>
              <td style="padding: 6px 8px; white-space: nowrap;">${keepMiniHtml}</td>
              <td style="padding: 6px 8px; text-align: center; color: #e7e5e4;">
                <span style="font-weight: 700;">${combo.handEV.toFixed(1)}</span>
                <span style="font-size: 0.72rem; color: #78716c; margin-left: 4px;">(${combo.handMin}–${combo.handMax})</span>
              </td>
              <td style="padding: 6px 8px; text-align: center; color: #a8a29e;">${combo.cribEV.toFixed(1)}</td>
              <td style="padding: 6px 8px; text-align: center; font-weight: 800; color: ${isOptimalPick ? '#10b981' : '#f4ead4'};">
                ${combo.netEV > 0 ? '+' : ''}${combo.netEV.toFixed(1)}
              </td>
              <td style="padding: 6px 8px; text-align: center; font-size: 0.76rem; color: ${combo.evDiff === 0 ? '#10b981' : '#f87171'};">
                ${combo.evDiff === 0 ? '0.0' : `-${combo.evDiff.toFixed(1)}`}
              </td>
            </tr>
          `;
        }).join('');

        resultsHtml = `
          <div style="margin-top: 16px; padding: 14px; background: rgba(0,0,0,0.35); border-radius: 10px; border: 1px solid rgba(255,255,255,0.12);">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; flex-wrap: wrap; gap: 8px;">
              <div>
                <span style="display: inline-block; padding: 4px 10px; border-radius: 6px; font-weight: 800; font-size: 0.88rem; background: ${badgeColor}; color: #000; letter-spacing: 0.04em;">
                  ${evalResult.gradeLabel}
                </span>
                <span style="font-size: 0.9rem; color: #e7e5e4; margin-left: 8px; font-weight: 600;">
                  Your Pick: Rank #${evalResult.rank} of 15
                </span>
              </div>
              <div style="display: flex; gap: 6px; align-items: center; flex-wrap: wrap;">
                <div style="font-size: 0.78rem; color: #a8a29e; background: rgba(0,0,0,0.4); padding: 4px 8px; border-radius: 6px;">
                  Session: ${evalResult.stats.optimalCount}/${evalResult.stats.handsPlayed} (${evalResult.stats.accuracyPct}%)
                </div>
                ${evalResult.stats.career ? `
                  <div style="font-size: 0.78rem; color: #34d399; background: rgba(16,185,129,0.15); border: 1px solid rgba(16,185,129,0.3); padding: 4px 8px; border-radius: 6px; font-weight: 700;">
                    🌐 Career: ${evalResult.stats.career.optimalCount}/${evalResult.stats.career.handsPlayed} (${evalResult.stats.career.accuracyPct}%) · Streak: ${evalResult.stats.career.currentStreak > 0 ? `🔥 ${evalResult.stats.career.currentStreak}` : '0'}
                  </div>
                ` : ''}
              </div>
            </div>

            ${evalResult.tips.length ? `
              <div style="margin-bottom: 12px; padding: 8px 12px; background: rgba(240,198,74,0.08); border-left: 3px solid #f0c64a; border-radius: 4px; font-size: 0.82rem; color: #fef08a; line-height: 1.45;">
                ${evalResult.tips.map(t => `<div>${t}</div>`).join('')}
              </div>
            ` : ''}

            <div style="max-height: 240px; overflow-y: auto; border: 1px solid rgba(255,255,255,0.08); border-radius: 8px;">
              <table style="width: 100%; border-collapse: collapse; text-align: left; font-family: inherit;">
                <thead>
                  <tr style="background: rgba(0,0,0,0.6); border-bottom: 1px solid rgba(255,255,255,0.15); font-size: 0.74rem; text-transform: uppercase; letter-spacing: 0.05em; color: #a8a29e;">
                    <th style="padding: 6px 8px; text-align: center;">Rank</th>
                    <th style="padding: 6px 8px;">Discard (2)</th>
                    <th style="padding: 6px 8px;">Keep (4)</th>
                    <th style="padding: 6px 8px; text-align: center;">Hand EV (Min–Max)</th>
                    <th style="padding: 6px 8px; text-align: center;">Crib EV</th>
                    <th style="padding: 6px 8px; text-align: center;">Net Score</th>
                    <th style="padding: 6px 8px; text-align: center;">Loss</th>
                  </tr>
                </thead>
                <tbody>
                  ${tableRowsHtml}
                </tbody>
              </table>
            </div>
          </div>
        `;
      }

      const modalHtml = `
        <div class="discard-trainer-wrap" style="max-width: 620px; width: 100%; margin: 0 auto; color: #f4ead4; font-family: inherit;">
          <div style="text-align: center; margin-bottom: 12px;">
            <div style="display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 50%; background: linear-gradient(135deg, #f0c64a, #d4a017); color: #1a1208; font-size: 1.6rem; margin-bottom: 6px;">🎯</div>
            <h2 style="margin: 0; font-size: 1.35rem; font-weight: 800; color: #f0c64a; letter-spacing: 0.04em;">CRIBBAGE DISCARD TRAINER</h2>
            <p style="margin: 4px 0 0; font-size: 0.82rem; color: #a8a29e;">Exact Hand Expected Value (EV) across all 46 cut cards &amp; crib impact.</p>
            <div style="margin-top: 6px; display: inline-flex; align-items: center; gap: 8px; padding: 3px 10px; border-radius: 12px; background: rgba(16,185,129,0.12); border: 1px solid rgba(16,185,129,0.3); font-size: 0.74rem; color: #34d399; font-weight: 700;">
              <span>🌐 Global Career: ${career.optimalCount}/${career.handsPlayed} Optimal (${career.accuracyPct}%)</span>
              <span>·</span>
              <span>Streak: ${career.currentStreak > 0 ? `🔥 ${career.currentStreak}` : '0'}</span>
              <span>·</span>
              <span>Avg Loss: -${career.averageLossEV.toFixed(2)} EV</span>
            </div>
          </div>

          <!-- Role Toggle -->
          <div style="display: flex; justify-content: center; gap: 8px; margin-bottom: 14px;">
            <button type="button" id="btnTrainerDealer" class="btn-ghost"
                    style="padding: 6px 14px; font-size: 0.82rem; font-weight: 700; border-radius: 20px; border: 1px solid ${isDealer ? '#f0c64a' : 'rgba(255,255,255,0.18)'}; background: ${isDealer ? 'rgba(240,198,74,0.2)' : 'transparent'}; color: ${isDealer ? '#f0c64a' : '#a8a29e'}; cursor: pointer;">
              👑 Dealer (Your Crib)
            </button>
            <button type="button" id="btnTrainerPone" class="btn-ghost"
                    style="padding: 6px 14px; font-size: 0.82rem; font-weight: 700; border-radius: 20px; border: 1px solid ${!isDealer ? '#f0c64a' : 'rgba(255,255,255,0.18)'}; background: ${!isDealer ? 'rgba(240,198,74,0.2)' : 'transparent'}; color: ${!isDealer ? '#f0c64a' : '#a8a29e'}; cursor: pointer;">
              🛡️ Pone (Opponent's Crib)
            </button>
          </div>

          <!-- Card Hand -->
          <div style="text-align: center; margin-bottom: 8px; font-size: 0.85rem; font-weight: 600; color: #d6d3d1;">
            Select 2 cards to toss to the crib (${selectedIndices.size}/2 selected):
          </div>
          <div id="trainerCardsRow" style="display: flex; justify-content: center; gap: 8px; flex-wrap: wrap; margin-bottom: 14px;">
            ${cardsHtml}
          </div>

          <!-- Action Buttons -->
          <div style="display: flex; gap: 8px; justify-content: center; flex-wrap: wrap;">
            <button type="button" id="btnTrainerSubmit" class="btn-gold" ${selectedIndices.size !== 2 ? 'disabled' : ''}
                    style="padding: 10px 18px; font-weight: 800; font-size: 0.92rem; border-radius: 8px; cursor: ${selectedIndices.size === 2 ? 'pointer' : 'not-allowed'}; opacity: ${selectedIndices.size === 2 ? '1' : '0.5'};">
              📊 Analyze &amp; Score Discard
            </button>
            <button type="button" id="btnTrainerDeal" class="btn-secondary" style="padding: 10px 14px; font-weight: 700; font-size: 0.88rem; border-radius: 8px; background: linear-gradient(135deg, #2a1b08, #3b2810); color: #f0c64a; border: 1.5px solid rgba(240, 198, 74, 0.6); cursor: pointer; box-shadow: 0 2px 8px rgba(0,0,0,0.4);">
              🎲 Deal New Hand
            </button>
            <button type="button" id="btnTrainerCustom" class="btn-ghost" style="padding: 10px 12px; font-size: 0.85rem; border-radius: 8px; background: rgba(255,255,255,0.08); color: #e7e5e4; border: 1.5px solid rgba(255,255,255,0.2); cursor: pointer;">
              ✏️ Custom Hand
            </button>
            <button type="button" class="btn-ghost modal-btn-close" style="padding: 10px 14px; font-size: 0.85rem; border-radius: 8px; background: rgba(255,255,255,0.08); color: #d6d3d1; border: 1.5px solid rgba(255,255,255,0.2); cursor: pointer;">
              Close
            </button>
          </div>

          <!-- Output Container -->
          <div id="trainerOutputWrap">
            ${resultsHtml}
          </div>
        </div>
      `;

      this.showModal(modalHtml, true);
      bindTrainerEvents();
    };

    const bindTrainerEvents = () => {
      const cardBtns = document.querySelectorAll('.trainer-card-btn');
      cardBtns.forEach(btn => {
        btn.onclick = () => {
          const idx = parseInt(btn.dataset.idx, 10);
          if (selectedIndices.has(idx)) {
            selectedIndices.delete(idx);
          } else {
            if (selectedIndices.size >= 2) {
              // Replace oldest selection if already 2 selected
              const first = selectedIndices.values().next().value;
              selectedIndices.delete(first);
            }
            selectedIndices.add(idx);
          }
          evalResult = null;
          renderTrainer();
        };
      });

      const btnDealer = document.getElementById('btnTrainerDealer');
      if (btnDealer) {
        btnDealer.onclick = () => {
          if (!isDealer) {
            isDealer = true;
            evalResult = null;
            renderTrainer();
          }
        };
      }

      const btnPone = document.getElementById('btnTrainerPone');
      if (btnPone) {
        btnPone.onclick = () => {
          if (isDealer) {
            isDealer = false;
            evalResult = null;
            renderTrainer();
          }
        };
      }

      const btnSubmit = document.getElementById('btnTrainerSubmit');
      if (btnSubmit) {
        btnSubmit.onclick = () => {
          if (selectedIndices.size !== 2) return;
          const userDiscards = Array.from(selectedIndices).map(i => trainerHand[i]);
          evalResult = DiscardTrainer.evaluateUserChoice(analysisResult, userDiscards);
          renderTrainer();
        };
      }

      const btnDeal = document.getElementById('btnTrainerDeal');
      if (btnDeal) {
        btnDeal.onclick = () => {
          trainerHand = DiscardTrainer.dealRandomHand();
          selectedIndices.clear();
          evalResult = null;
          renderTrainer();
        };
      }

      const btnCustom = document.getElementById('btnTrainerCustom');
      if (btnCustom) {
        btnCustom.onclick = () => {
          const input = prompt('Enter 6 cards separated by spaces (e.g. 5H 5D JC QS 7S 8C):', trainerHand.map(c => `${c.rankName || c.rank}${c.suit}`).join(' '));
          if (input) {
            const parsed = DiscardTrainer.parseHand(input);
            if (parsed) {
              trainerHand = parsed;
              selectedIndices.clear();
              evalResult = null;
              renderTrainer();
            } else {
              alert('Invalid cards entered! Please provide exactly 6 distinct cards like: 5H 5D JC QS 7S 8C');
            }
          }
        };
      }
    };

    renderTrainer();
  }

  /**
   * Displays the interactive Pegging Tactics Trainer modal.
   * Gives players tactical scenarios (baiting 15s, sequence defense, maneuvering to 31, etc.)
   * and provides instant feedback on optimal plays vs traps.
   * @param {Object} [context]
   */
  showPeggingTrainerModal(context = {}) {
    if (typeof document === 'undefined') return;

    let activeCategory = context.category || 'all';
    let currentScenario = context.scenario || PeggingTrainer.generateScenario(activeCategory);
    let evalResult = null;

    const suitSymbols = { H: '♥', D: '♦', S: '♠', C: '♣' };

    const renderPeggingTrainer = () => {
      const statsObj = PeggingTrainer.getStats(context.storage);
      const session = statsObj.session;
      const career = statsObj.career;

      // Render category selector options
      const categoryOptionsHtml = PEGGING_CATEGORIES.map(cat => `
        <option value="${cat.id}" ${cat.id === activeCategory ? 'selected' : ''}>
          ${cat.name}
        </option>
      `).join('');

      // Pile cards display
      const pileCardsHtml = (currentScenario.pile && currentScenario.pile.length)
        ? currentScenario.pile.map(c => {
          const isRed = c.suit === 'H' || c.suit === 'D';
          const textColor = isRed ? '#dc2626 !important' : '#111827 !important';
          return `
            <div class="pegging-mini-card ${isRed ? 'card-red' : 'card-black'}"
                 style="display: inline-flex; flex-direction: column; align-items: center; justify-content: center; width: 44px; height: 62px; background: #ffffff !important; border: 1.5px solid #cbd5e1; border-radius: 6px; box-shadow: 0 2px 6px rgba(0,0,0,0.3); padding: 2px;">
              <span style="font-size: 0.92rem; font-weight: 800; color: ${textColor}; -webkit-text-fill-color: ${textColor}; line-height: 1;">${c.rankName || c.rank}</span>
              <span style="font-size: 1.15rem; color: ${textColor}; -webkit-text-fill-color: ${textColor}; line-height: 1;">${suitSymbols[c.suit] || c.suit}</span>
            </div>
          `;
        }).join('')
        : '<span style="color:#94a3b8; font-style:italic;">Pile is empty (Lead card)</span>';

      // Player hand buttons
      const handCardsHtml = currentScenario.hand.map((c, idx) => {
        const isRed = c.suit === 'H' || c.suit === 'D';
        const textColor = isRed ? '#dc2626 !important' : '#111827 !important';
        const isLegal = (currentScenario.count + VAL(c.rank)) <= 31;
        const isChosen = evalResult && evalResult.chosen && evalResult.chosen.card.id === c.id;

        let borderStyle = isLegal ? '2px solid #cbd5e1 !important' : '2px dashed #64748b !important';
        let boxShadow = isLegal ? '0 4px 10px rgba(0,0,0,0.35)' : 'none';
        if (isChosen) {
          borderStyle = evalResult.isOptimal ? '3px solid #10b981 !important' : '3px solid #f59e0b !important';
          boxShadow = evalResult.isOptimal ? '0 0 0 2px #10b981, 0 8px 18px rgba(16,185,129,0.45) !important' : '0 0 0 2px #f59e0b, 0 8px 18px rgba(245,158,11,0.45) !important';
        }

        return `
          <button type="button" class="pegging-hand-card-btn ${isLegal ? '' : 'illegal-card'} ${isRed ? 'card-red' : 'card-black'}" data-card-id="${c.id}" ${(!isLegal || evalResult) ? 'disabled' : ''}
                  style="position: relative; width: 72px; height: 104px; box-sizing: border-box; background: #ffffff !important; border: ${borderStyle}; border-radius: 8px; cursor: ${isLegal && !evalResult ? 'pointer' : 'default'}; opacity: ${isLegal ? '1' : '0.45'}; display: flex; flex-direction: column; justify-content: space-between; padding: 6px 7px; box-shadow: ${boxShadow}; transform: ${isChosen ? 'translateY(-4px)' : 'none'}; transition: all 0.15s ease;"
                  title="${c.rankName || c.rank}${c.suit} ${isLegal ? '(Click to play)' : '(Exceeds 31)'}">
            <div style="display: flex; align-items: baseline; justify-content: flex-start; gap: 2px; line-height: 1;">
              <span class="card-val" style="font-size: 1.22rem; font-weight: 900; color: ${textColor}; -webkit-text-fill-color: ${textColor}; line-height: 1;">${c.rankName || c.rank}</span>
              <span class="card-val" style="font-size: 0.92rem; font-weight: 700; color: ${textColor}; -webkit-text-fill-color: ${textColor}; line-height: 1;">${suitSymbols[c.suit] || c.suit}</span>
            </div>
            <div class="card-val" style="font-size: 2rem; color: ${textColor}; -webkit-text-fill-color: ${textColor}; line-height: 1; text-align: center; margin: 2px 0;">${suitSymbols[c.suit] || c.suit}</div>
            <span style="font-size: 0.6rem; font-weight: 800; text-transform: uppercase; background: ${isLegal ? 'rgba(0,0,0,0.06)' : 'rgba(239,68,68,0.1)'}; color: ${isLegal ? '#475569' : '#dc2626'}; border-radius: 4px; padding: 1px 0; text-align: center; display: block; width: 100%;">
              ${isChosen ? 'PLAYED' : (isLegal ? `+${VAL(c.rank)}` : 'OVER 31')}
            </span>
          </button>
        `;
      }).join('');

      // Results Breakdown if a card was chosen
      let resultSectionHtml = '';
      if (evalResult) {
        const gradeBadgeColors = {
          optimal: '#10b981',
          great: '#06b6d4',
          risky: '#f59e0b',
          blunder: '#ef4444'
        };
        const badgeColor = gradeBadgeColors[evalResult.grade] || '#f0c64a';

        const candidateRowsHtml = evalResult.scenario.analysis.legalMoves.map(m => {
          const isUserPick = m.card.id === evalResult.chosen.card.id;
          const isOptimalPick = m.rank === 1;
          const isRed = m.card.suit === 'H' || m.card.suit === 'D';
          const cardTextColor = isRed ? '#dc2626 !important' : '#111827 !important';

          let rowBg = 'transparent';
          if (isUserPick && isOptimalPick) rowBg = 'rgba(16, 185, 129, 0.18)';
          else if (isUserPick) rowBg = 'rgba(240, 198, 74, 0.15)';
          else if (isOptimalPick) rowBg = 'rgba(16, 185, 129, 0.12)';

          return `
            <tr style="background: ${rowBg}; border-bottom: 1px solid rgba(255,255,255,0.06); font-size: 0.82rem;">
              <td style="padding: 6px 8px; font-weight: 800; color: ${isOptimalPick ? '#10b981' : (isUserPick ? '#f0c64a' : '#a8a29e')}; text-align: center;">
                #${m.rank}${isOptimalPick ? ' ★' : ''}${isUserPick ? ' 👈' : ''}
              </td>
              <td style="padding: 6px 8px; white-space: nowrap;">
                <span style="display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: 800; font-size: 0.84rem; background: #ffffff !important; color: ${cardTextColor}; -webkit-text-fill-color: ${cardTextColor}; border: 1px solid #cbd5e1;">
                  ${m.card.rankName || m.card.rank}${suitSymbols[m.card.suit] || m.card.suit}
                </span>
              </td>
              <td style="padding: 6px 8px; text-align: center; font-weight: 700; color: #e2e8f0;">
                ${currentScenario.count + VAL(m.card.rank)}
              </td>
              <td style="padding: 6px 8px; text-align: center; font-weight: 800; color: ${m.directPts > 0 ? '#10b981' : '#a8a29e'};">
                ${m.directPts > 0 ? `+${m.directPts}` : '0'}
              </td>
              <td style="padding: 6px 8px; text-align: center; font-size: 0.78rem; color: ${m.diff === 0 ? '#10b981' : '#f87171'}; font-weight: 700;">
                ${m.diff === 0 ? '0.0 (Best)' : `-${m.diff.toFixed(1)}`}
              </td>
              <td style="padding: 6px 8px; font-size: 0.78rem; color: #cbd5e1;">
                ${m.reasons.length ? m.reasons.join(' · ') : 'Safe tempo play'}
              </td>
            </tr>
          `;
        }).join('');

        resultSectionHtml = `
          <div style="margin-top: 16px; padding: 14px; background: rgba(0,0,0,0.38); border-radius: 10px; border: 1.5px solid ${badgeColor}; text-align: left;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; flex-wrap: wrap; gap: 8px;">
              <div style="display: flex; align-items: center; gap: 10px;">
                <span style="display: inline-block; padding: 4px 12px; border-radius: 6px; font-weight: 800; font-size: 0.92rem; background: ${badgeColor}; color: #000; letter-spacing: 0.04em;">
                  ${evalResult.gradeLabel}
                </span>
                <span style="font-size: 0.95rem; color: #e7e5e4; font-weight: 700;">
                  Card: ${evalResult.chosen.card.rankName || evalResult.chosen.card.rank}${evalResult.chosen.card.suit}
                </span>
              </div>
              <div style="font-size: 0.78rem; color: #34d399; background: rgba(16,185,129,0.15); border: 1px solid rgba(16,185,129,0.3); padding: 4px 10px; border-radius: 6px; font-weight: 700;">
                Session: ${session.optimalCount}/${session.scenariosPlayed} (${session.accuracyPct}%) · Streak: ${session.currentStreak > 0 ? `🔥 ${session.currentStreak}` : '0'}
              </div>
            </div>

            <!-- Master Lesson Explanation -->
            <div style="margin-bottom: 12px; padding: 10px 14px; background: rgba(240,198,74,0.1); border-left: 3.5px solid #f0c64a; border-radius: 4px; font-size: 0.85rem; color: #fef08a; line-height: 1.45;">
              <strong>💡 Tactical Lesson:</strong> ${evalResult.lesson}
            </div>

            <!-- All Move Options Comparison Table -->
            <div style="max-height: 200px; overflow-y: auto; border: 1px solid rgba(255,255,255,0.08); border-radius: 8px;">
              <table style="width: 100%; border-collapse: collapse; text-align: left; font-family: inherit;">
                <thead>
                  <tr style="background: rgba(0,0,0,0.6); border-bottom: 1px solid rgba(255,255,255,0.15); font-size: 0.74rem; text-transform: uppercase; letter-spacing: 0.05em; color: #a8a29e;">
                    <th style="padding: 6px 8px; text-align: center;">Rank</th>
                    <th style="padding: 6px 8px;">Card</th>
                    <th style="padding: 6px 8px; text-align: center;">Count</th>
                    <th style="padding: 6px 8px; text-align: center;">Points</th>
                    <th style="padding: 6px 8px; text-align: center;">Gap</th>
                    <th style="padding: 6px 8px;">Tactical Rationale</th>
                  </tr>
                </thead>
                <tbody>
                  ${candidateRowsHtml}
                </tbody>
              </table>
            </div>
          </div>
        `;
      }

      const modalHtml = `
        <div class="pegging-trainer-wrap" style="max-width: 660px; width: 100%; margin: 0 auto; color: #f4ead4; font-family: inherit;">
          <!-- Top Header -->
          <div style="text-align: center; margin-bottom: 12px;">
            <div style="display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 50%; background: linear-gradient(135deg, #38bdf8, #0284c7); color: #0f172a; font-size: 1.6rem; margin-bottom: 6px;">⚔️</div>
            <h2 style="margin: 0; font-size: 1.35rem; font-weight: 800; color: #38bdf8; letter-spacing: 0.04em;">PEGGING TACTICS TRAINER</h2>
            <p style="margin: 4px 0 0; font-size: 0.82rem; color: #94a3b8;">Master 15-baiting, sequence defense, pair traps, and safe maneuvers toward 31.</p>
            <div style="margin-top: 6px; display: inline-flex; align-items: center; gap: 8px; padding: 3px 12px; border-radius: 12px; background: rgba(56,189,248,0.12); border: 1px solid rgba(56,189,248,0.3); font-size: 0.75rem; color: #38bdf8; font-weight: 700;">
              <span>🌐 Global Career: ${career.optimalCount}/${career.scenariosPlayed} Optimal (${career.accuracyPct}%)</span>
              <span>·</span>
              <span>Streak: ${career.currentStreak > 0 ? `🔥 ${career.currentStreak}` : '0'}</span>
            </div>
          </div>

          <!-- Category Selector & Navigation -->
          <div style="display: flex; justify-content: space-between; align-items: center; gap: 10px; margin-bottom: 12px; flex-wrap: wrap;">
            <div style="display: flex; align-items: center; gap: 8px;">
              <label for="selPeggingCat" style="font-size: 0.8rem; font-weight: 700; color: #cbd5e1;">Topic:</label>
              <select id="selPeggingCat" style="background: rgba(15,23,42,0.9); color: #f8fafc; border: 1px solid rgba(56,189,248,0.5); padding: 5px 10px; border-radius: 6px; font-size: 0.82rem; font-weight: 600; outline: none; cursor: pointer;">
                ${categoryOptionsHtml}
              </select>
            </div>
            <button type="button" id="btnNextScenario" class="btn-ghost" style="padding: 6px 14px; font-size: 0.82rem; font-weight: 700; border-radius: 6px; border: 1px solid rgba(56,189,248,0.5); background: rgba(56,189,248,0.15); color: #38bdf8; cursor: pointer;">
              🎲 Next Scenario
            </button>
          </div>

          <!-- Scenario Board Box -->
          <div style="background: rgba(0,0,0,0.35); border: 1.5px solid rgba(56,189,248,0.25); border-radius: 12px; padding: 14px 16px; margin-bottom: 14px; text-align: left;">
            <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 6px; flex-wrap: wrap; gap: 8px;">
              <span style="font-size: 1rem; font-weight: 800; color: #facc15;">${currentScenario.title}</span>
              <div style="display: flex; align-items: center; gap: 8px;">
                <span style="font-size: 0.75rem; text-transform: uppercase; font-weight: 800; padding: 2px 8px; border-radius: 4px; background: rgba(56,189,248,0.2); color: #38bdf8;">
                  Count: ${currentScenario.count} / 31
                </span>
              </div>
            </div>
            <p style="margin: 0 0 10px; font-size: 0.86rem; color: #e2e8f0; line-height: 1.4;">
              ${currentScenario.description}
            </p>

            <!-- Board Pile -->
            <div style="display: flex; align-items: center; gap: 12px; padding: 8px 12px; background: rgba(255,255,255,0.03); border-radius: 8px; border: 1px solid rgba(255,255,255,0.06);">
              <span style="font-size: 0.78rem; font-weight: 700; color: #94a3b8; text-transform: uppercase;">Cards on Table:</span>
              <div style="display: flex; gap: 6px; align-items: center; flex-wrap: wrap;">
                ${pileCardsHtml}
              </div>
            </div>
          </div>

          <!-- Hand Choice Prompt -->
          <div style="text-align: center; margin-bottom: 10px; font-size: 0.88rem; font-weight: 700; color: #cbd5e1;">
            ${evalResult ? 'Scenario Completed! Review breakdown below or pick Next Scenario.' : 'Choose a card from your hand to play:'}
          </div>
          <div id="peggingHandRow" style="display: flex; justify-content: center; gap: 8px; flex-wrap: wrap; margin-bottom: 14px;">
            ${handCardsHtml}
          </div>

          <!-- Results Section Container -->
          <div id="peggingResultsWrap">
            ${resultSectionHtml}
          </div>

          <!-- Footer Controls -->
          <div style="display: flex; gap: 8px; justify-content: center; margin-top: 16px; flex-wrap: wrap;">
            <button type="button" id="btnPeggingNextAction" class="btn-gold" style="padding: 9px 18px; font-weight: 800; font-size: 0.9rem; border-radius: 8px; cursor: pointer;">
              🎲 Next Scenario
            </button>
            <button type="button" class="btn-ghost" id="btnGoToDiscardTrainer" style="padding: 9px 14px; font-size: 0.85rem; border-radius: 8px; border: 1px solid rgba(240,198,74,0.4); color: #f0c64a; cursor: pointer;">
              🎯 Discard Trainer
            </button>
            <button type="button" class="btn-ghost modal-btn-close" style="padding: 9px 16px; font-size: 0.85rem; border-radius: 8px; border: 1.5px solid rgba(255,255,255,0.2); color: #d6d3d1; cursor: pointer;">
              Close
            </button>
          </div>
        </div>
      `;

      this.showModal(modalHtml, true);
      bindPeggingEvents();
    };

    const bindPeggingEvents = () => {
      const btnDiscard = document.getElementById('btnGoToDiscardTrainer');
      if (btnDiscard) {
        btnDiscard.addEventListener('click', () => {
          if (typeof showDiscardTrainerModal === 'function') showDiscardTrainerModal();
          else if (typeof window !== 'undefined' && typeof window.showDiscardTrainerModal === 'function') window.showDiscardTrainerModal();
        });
      }

      // Category switch
      const selCat = document.getElementById('selPeggingCat');
      if (selCat) {
        selCat.onchange = () => {
          activeCategory = selCat.value;
          currentScenario = PeggingTrainer.generateScenario(activeCategory);
          evalResult = null;
          renderPeggingTrainer();
        };
      }

      // Next scenario button
      const btnNext = document.getElementById('btnNextScenario');
      const btnNextAction = document.getElementById('btnPeggingNextAction');
      const loadNext = () => {
        currentScenario = PeggingTrainer.generateScenario(activeCategory);
        evalResult = null;
        renderPeggingTrainer();
      };
      if (btnNext) btnNext.onclick = loadNext;
      if (btnNextAction) btnNextAction.onclick = loadNext;

      // Card play clicks
      const cardBtns = document.querySelectorAll('.pegging-hand-card-btn:not([disabled])');
      cardBtns.forEach(btn => {
        btn.onclick = () => {
          const cardId = btn.getAttribute('data-card-id');
          const chosenCard = currentScenario.hand.find(c => c.id === cardId);
          if (chosenCard) {
            evalResult = PeggingTrainer.evaluateChoice(currentScenario, chosenCard, { storage: context.storage });
            renderPeggingTrainer();
          }
        };
      });
    };

    renderPeggingTrainer();
  }

  /**
   * Displays the Counting Trainer modal for practicing cribbage hand scoring.
   * Supports 6-card (discard 2, keep 4) and 9-card (discard 3, keep 6) modes,
   * random cut starter card, live total calculation, green checkmarks / red X's,
   * stats tracking with accuracy percentage, custom hand entry, sharing,
   * and expandable point-by-point details with card chips.
   * @param {Object} [context]
   */
  showCountingTrainerModal(context = {}) {
    if (typeof document === 'undefined') return;

    // Auto-decode URL query parameters if no hand/cards/starter specified
    if ((!context || (!context.cards && !context.hand && !context.kept && !context.starter)) && typeof window !== 'undefined' && window.location && window.location.search) {
      try {
        const decoded = CountingTrainer.decodeShareUrl(window.location.search);
        if (decoded) {
          context = { ...context, ...decoded };
        }
      } catch (_) {}
    }

    const rankMap = { A: 1, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, T: 10, J: 11, Q: 12, K: 13 };
    const suitMap = { S: 'S', H: 'H', D: 'D', C: 'C', '♠': 'S', '♥': 'H', '♦': 'D', '♣': 'C' };

    const normalizeCardToken = (c) => {
      if (!c) return null;
      if (typeof c === 'object' && (c.rank !== undefined || c.r !== undefined) && (c.suit !== undefined || c.s !== undefined)) {
        return (c instanceof Card) ? c : new Card(c);
      }
      if (typeof c === 'string') {
        const m = c.trim().toUpperCase().match(/^([2-9]|10|[TJQKA]|1)([SHDC♠♥♦♣])$/);
        if (m) {
          const rank = rankMap[m[1]];
          const suit = suitMap[m[2]];
          return new Card({ r: rank, s: suit, id: `${m[1]}${suit}` });
        }
      }
      return null;
    };

    let mode = context.mode === 9 ? 9 : 6;
    let starterCard = normalizeCardToken(context.starter);
    let keptHand = null;
    let dealtHand = null;

    if (starterCard) {
      const rawCards = context.kept || context.cards || context.hand || null;
      if (Array.isArray(rawCards) && rawCards.length > 0) {
        keptHand = CountingTrainer.sortCards(rawCards.map(normalizeCardToken).filter(Boolean));
      }
    } else {
      const rawCards = context.hand || context.cards || null;
      if (Array.isArray(rawCards) && rawCards.length > 0) {
        dealtHand = CountingTrainer.sortCards(rawCards.map(normalizeCardToken).filter(Boolean));
      }
    }

    let phase = (keptHand && starterCard) ? 'counting' : 'discard';
    let selectedIndices = new Set();
    let userInputs = { fifteens: 0, pairs: 0, runs: 0, flush: 0, nobs: 0 };
    let evalResult = null;
    let actualBreakdown = null;
    let showDetails = false;
    let showCustomForm = false;
    let customError = '';
    let toastMessage = '';
    let userDiscardedCards = null;
    let originalDealtHand = dealtHand ? dealtHand.slice() : null;
    let discardEvalResult = null;
    let showEvTable = false;
    let showSharePanel = false;
    let shareCopiedNotice = '';
    let currentShareText = '';
    let currentShareUrl = '';

    const copyTextToClipboard = async (str) => {
      if (!str) return false;
      let ok = false;
      if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        try {
          await navigator.clipboard.writeText(str);
          ok = true;
        } catch (_) {}
      }
      if (!ok && typeof document !== 'undefined') {
        try {
          const ta = document.createElement('textarea');
          ta.value = str;
          ta.setAttribute('readonly', '');
          ta.style.position = 'fixed';
          ta.style.top = '-9999px';
          ta.style.left = '-9999px';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          ok = Boolean(document.execCommand('copy'));
          document.body.removeChild(ta);
        } catch (_) {}
      }
      return ok;
    };

    const getRequiredDiscards = (m) => (m === 9 ? 3 : 2);

    const startNewHand = (targetMode = mode) => {
      mode = targetMode;
      dealtHand = CountingTrainer.dealRandomHand(mode);
      originalDealtHand = dealtHand ? dealtHand.slice() : null;
      userDiscardedCards = null;
      discardEvalResult = null;
      showEvTable = false;
      showSharePanel = false;
      shareCopiedNotice = '';
      keptHand = null;
      starterCard = null;
      selectedIndices.clear();
      userInputs = { fifteens: 0, pairs: 0, runs: 0, flush: 0, nobs: 0 };
      evalResult = null;
      actualBreakdown = null;
      showDetails = false;
      showCustomForm = false;
      customError = '';
      phase = 'discard';
      renderCountingTrainer();
    };

    if (!dealtHand && !keptHand) {
      dealtHand = CountingTrainer.dealRandomHand(mode);
    } else if (dealtHand && !keptHand && !starterCard) {
      phase = 'discard';
    }

    if (phase === 'counting' && keptHand && starterCard) {
      actualBreakdown = CountingTrainer.scoreCountedHand(keptHand, starterCard, false);
    }

    const suitSymbols = { H: '♥', D: '♦', S: '♠', C: '♣' };

    const renderCountingTrainer = () => {
      const statsObj = CountingTrainer.getStats(context.storage);
      const session = statsObj.session;
      const career = statsObj.career;
      const requiredDiscards = getRequiredDiscards(mode);

      const currentLiveTotal = (Number(userInputs.fifteens) || 0) +
                               (Number(userInputs.pairs) || 0) +
                               (Number(userInputs.runs) || 0) +
                               (Number(userInputs.flush) || 0) +
                               (Number(userInputs.nobs) || 0);

      // Render cards display
      let cardsDisplayHtml = '';
      if (phase === 'discard') {
        const discardCount = selectedIndices.size;
        const canCut = discardCount === requiredDiscards;

        const cardBtnsHtml = dealtHand.map((c, idx) => {
          const su = String((c && (c.suit || c.s || c.rawSuit)) || '').toUpperCase();
          const isRed = su === 'H' || su === 'D' || su === '♥' || su === '♦';
          const isSelected = selectedIndices.has(idx);
          const textColor = isRed ? '#dc2626 !important' : '#111827 !important';
          const rVal = c ? (c.rank !== undefined ? c.rank : (c.r !== undefined ? c.r : 0)) : 0;
          const rName = (c && c.rankName) || (RANK_NAME && RANK_NAME[rVal]) || (rVal === 1 ? 'A' : rVal === 11 ? 'J' : rVal === 12 ? 'Q' : rVal === 13 ? 'K' : String(rVal || ''));
          const sSym = (suitSymbols && suitSymbols[su]) || (SUIT_SYM && SUIT_SYM[su]) || (su === 'H' ? '♥' : su === 'D' ? '♦' : su === 'S' ? '♠' : su === 'C' ? '♣' : su);
          return `
            <button type="button" class="counting-card-btn ${isSelected ? 'selected' : ''} ${isRed ? 'card-red' : 'card-black'}" data-idx="${idx}"
                    style="position: relative; width: 72px; height: 104px; box-sizing: border-box; background: #ffffff !important; border: ${isSelected ? '3px solid #ef4444 !important' : '2px solid #cbd5e1 !important'}; border-radius: 8px; cursor: pointer; display: flex; flex-direction: column; justify-content: space-between; padding: 6px 7px; box-shadow: ${isSelected ? '0 0 0 2px #dc2626, 0 8px 18px rgba(239,68,68,0.45) !important' : '0 4px 10px rgba(0,0,0,0.35) !important'}; transform: ${isSelected ? 'translateY(-4px)' : 'none'}; transition: all 0.15s ease;"
                    title="${rName}${sSym} - Click to toggle discard">
              <div style="display: flex; align-items: baseline; justify-content: flex-start; gap: 2px; line-height: 1;">
                <span class="card-val" style="font-size: 1.22rem; font-weight: 900; color: ${textColor}; -webkit-text-fill-color: ${textColor}; line-height: 1;">${rName}</span>
                <span class="card-val" style="font-size: 0.92rem; font-weight: 700; color: ${textColor}; -webkit-text-fill-color: ${textColor}; line-height: 1;">${sSym}</span>
              </div>
              <div class="card-val" style="font-size: 2rem; color: ${textColor}; -webkit-text-fill-color: ${textColor}; line-height: 1; text-align: center; margin: 2px 0;">${sSym}</div>
              <span style="font-size: 0.6rem; font-weight: 800; text-transform: uppercase; background: ${isSelected ? '#ef4444 !important' : 'transparent'}; color: ${isSelected ? '#ffffff !important' : 'transparent'}; -webkit-text-fill-color: ${isSelected ? '#ffffff !important' : 'transparent'}; border-radius: 4px; padding: 1px 0; text-align: center; display: block; width: 100%;">
                ${isSelected ? 'DISCARD' : 'KEEP'}
              </span>
            </button>
          `;
        }).join('');

        cardsDisplayHtml = `
          <div style="background: rgba(0,0,0,0.25); border-radius: 12px; padding: 14px; margin-bottom: 16px; border: 1px solid rgba(255,255,255,0.08);">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; flex-wrap: wrap; gap: 8px;">
              <span style="font-size: 0.92rem; font-weight: 700; color: #f1f5f9;">
                🃏 Dealt Hand (${dealtHand.length} cards) — Select <strong style="color: #f59e0b;">${requiredDiscards}</strong> cards to discard:
              </span>
              <span style="font-size: 0.82rem; font-weight: 800; padding: 3px 10px; border-radius: 12px; background: ${canCut ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}; color: ${canCut ? '#34d399' : '#f87171'}; border: 1px solid ${canCut ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.4)'};">
                ${discardCount} / ${requiredDiscards} Discarded
              </span>
            </div>
            <div style="display: flex; justify-content: center; gap: 8px; flex-wrap: wrap;">
              ${cardBtnsHtml}
            </div>
            <div style="margin-top: 14px; text-align: center;">
              <button type="button" id="btnCountingCutStarter" class="btn-primary" ${canCut ? '' : 'disabled'}
                      style="padding: 10px 22px; font-weight: 800; font-size: 0.95rem; border-radius: 8px; background: ${canCut ? 'linear-gradient(135deg, #10b981, #059669)' : 'rgba(255,255,255,0.1)'}; color: ${canCut ? '#fff' : '#94a3b8'}; cursor: ${canCut ? 'pointer' : 'not-allowed'}; border: none; box-shadow: ${canCut ? '0 4px 12px rgba(16,185,129,0.4)' : 'none'}; transition: all 0.2s ease;">
                ✂️ Cut Starter & Count Hand
              </button>
            </div>
          </div>
        `;
      } else {
        // Counting phase
        const renderCardBox = (c, isCut = false) => {
          const su = String((c && (c.suit || c.s || c.rawSuit)) || '').toUpperCase();
          const isRed = su === 'H' || su === 'D' || su === '♥' || su === '♦';
          const textColor = isRed ? '#dc2626 !important' : '#111827 !important';
          const rVal = c ? (c.rank !== undefined ? c.rank : (c.r !== undefined ? c.r : 0)) : 0;
          const rName = (c && c.rankName) || (RANK_NAME && RANK_NAME[rVal]) || (rVal === 1 ? 'A' : rVal === 11 ? 'J' : rVal === 12 ? 'Q' : rVal === 13 ? 'K' : String(rVal || ''));
          const sSym = (suitSymbols && suitSymbols[su]) || (SUIT_SYM && SUIT_SYM[su]) || (su === 'H' ? '♥' : su === 'D' ? '♦' : su === 'S' ? '♠' : su === 'C' ? '♣' : su);
          const border = isCut ? '3px solid #f59e0b !important' : '2px solid #cbd5e1 !important';
          const shadow = isCut ? '0 0 16px rgba(245,158,11,0.6), 0 4px 12px rgba(0,0,0,0.4) !important' : '0 4px 10px rgba(0,0,0,0.35) !important';
          return `
            <div class="counting-display-card ${isRed ? 'card-red' : 'card-black'}"
                 style="position: relative; width: 72px; height: 104px; box-sizing: border-box; background: #ffffff !important; border: ${border}; border-radius: 8px; display: flex; flex-direction: column; justify-content: space-between; padding: 6px 7px; box-shadow: ${shadow};">
              <div style="display: flex; align-items: baseline; justify-content: flex-start; gap: 2px; line-height: 1;">
                <span class="card-val" style="font-size: 1.22rem; font-weight: 900; color: ${textColor}; -webkit-text-fill-color: ${textColor}; line-height: 1;">${rName}</span>
                <span class="card-val" style="font-size: 0.92rem; font-weight: 700; color: ${textColor}; -webkit-text-fill-color: ${textColor}; line-height: 1;">${sSym}</span>
              </div>
              <div class="card-val" style="font-size: 2rem; color: ${textColor}; -webkit-text-fill-color: ${textColor}; line-height: 1; text-align: center; margin: 2px 0;">${sSym}</div>
              <span class="counting-card-badge" style="font-size: 0.58rem; font-weight: 800; text-transform: uppercase; background: ${isCut ? 'rgba(245,158,11,0.22)' : 'rgba(0,0,0,0.06)'}; color: #000000 !important; -webkit-text-fill-color: #000000 !important; border-radius: 4px; padding: 2px 0; text-align: center; display: block; width: 100%; line-height: 1.1;">
                ${isCut ? 'CUT CARD' : 'KEPT'}
              </span>
            </div>
          `;
        };

        const keptCardsHtml = (keptHand || []).map(c => renderCardBox(c, false)).join('');
        const starterCardHtml = starterCard ? renderCardBox(starterCard, true) : '';

        cardsDisplayHtml = `
          <div style="background: rgba(0,0,0,0.25); border-radius: 12px; padding: 14px; margin-bottom: 16px; border: 1px solid rgba(255,255,255,0.08);">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; flex-wrap: wrap; gap: 6px;">
              <span style="font-size: 0.88rem; font-weight: 800; color: #cbd5e1; text-transform: uppercase; letter-spacing: 0.05em;">
                Hand to Count (${(keptHand || []).length} Kept + Cut Starter)
              </span>
            </div>
            <div style="display: flex; align-items: center; justify-content: center; gap: 14px; flex-wrap: wrap;">
              <div style="display: flex; gap: 8px; flex-wrap: wrap; justify-content: center;">
                ${keptCardsHtml}
              </div>
              <div style="display: flex; flex-direction: column; align-items: center; gap: 4px;">
                <span style="font-size: 0.72rem; font-weight: 800; color: #f59e0b; text-transform: uppercase; letter-spacing: 0.05em;">✂️ Starter</span>
                ${starterCardHtml}
              </div>
            </div>
          </div>
        `;
      }

      // Helper to render category input row
      const renderCategoryRow = (id, label, icon, step = 2) => {
        const val = userInputs[id] !== undefined ? userInputs[id] : 0;
        let badgeHtml = '';
        if (evalResult) {
          const res = evalResult[id];
          if (res && res.correct) {
            badgeHtml = `<span style="color: #10b981 !important; font-weight: 800; font-size: 1rem; display: inline-flex; align-items: center; gap: 4px;">✔ ${res.actual} pts</span>`;
          } else if (res) {
            badgeHtml = `<span style="color: #ef4444 !important; font-weight: 800; font-size: 0.85rem; display: inline-flex; align-items: center; gap: 4px;">✖ Entered ${res.user} (Actual: ${res.actual})</span>`;
          }
        }

        return `
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: rgba(0,0,0,0.2); border-radius: 8px; border: 1px solid rgba(255,255,255,0.05); gap: 10px; flex-wrap: wrap;">
            <div style="display: flex; align-items: center; gap: 8px; min-width: 110px;">
              <span style="font-size: 1.15rem;">${icon}</span>
              <span style="font-weight: 800; font-size: 0.95rem; color: #f1f5f9;">${label}</span>
            </div>
            <div style="display: flex; align-items: center; gap: 6px;">
              <button type="button" class="count-step-btn" data-cat="${id}" data-delta="-${step}" ${evalResult ? 'disabled' : ''}
                      style="width: 32px; height: 32px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.2); background: rgba(255,255,255,0.1); color: #fff; font-weight: 800; font-size: 1.1rem; cursor: pointer; display: flex; align-items: center; justify-content: center; line-height: 1;">-</button>
              <input type="number" id="inputCat_${id}" class="count-input-field" data-cat="${id}" min="0" max="40" step="${step === 1 ? 1 : 2}" value="${val}" ${evalResult ? 'disabled' : ''}
                     style="width: 58px; height: 32px; border-radius: 6px; border: 1.5px solid rgba(255,255,255,0.2); background: rgba(15,23,42,0.8); color: #f8fafc; font-weight: 800; font-size: 1.05rem; text-align: center; box-sizing: border-box;" />
              <button type="button" class="count-step-btn" data-cat="${id}" data-delta="${step}" ${evalResult ? 'disabled' : ''}
                      style="width: 32px; height: 32px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.2); background: rgba(255,255,255,0.1); color: #fff; font-weight: 800; font-size: 1.1rem; cursor: pointer; display: flex; align-items: center; justify-content: center; line-height: 1;">+</button>
            </div>
            <div style="min-width: 140px; text-align: right;">
              ${badgeHtml}
            </div>
          </div>
        `;
      };

      // Point Details
      let detailsHtml = '';
      if (showDetails && actualBreakdown) {
        if (!actualBreakdown.lines || !actualBreakdown.lines.length) {
          detailsHtml = `
            <div style="margin-top: 14px; padding: 12px; background: rgba(0,0,0,0.3); border-radius: 8px; border: 1px dashed rgba(255,255,255,0.15); text-align: center; color: #94a3b8; font-style: italic;">
              Zero points in this hand (19-hand).
            </div>
          `;
        } else {
          const detailRows = actualBreakdown.lines.map((ln) => {
            const cardsHtml = (ln.cards || []).map(c => {
              const su = String((c && (c.suit || c.s || c.rawSuit)) || '').toUpperCase();
              const isRed = su === 'H' || su === 'D' || su === '♥' || su === '♦';
              const col = isRed ? '#dc2626' : '#111827';
              const rVal = c ? (c.rank !== undefined ? c.rank : (c.r !== undefined ? c.r : 0)) : 0;
              const rName = (c && c.rankName) || (RANK_NAME && RANK_NAME[rVal]) || (rVal === 1 ? 'A' : rVal === 11 ? 'J' : rVal === 12 ? 'Q' : rVal === 13 ? 'K' : String(rVal || ''));
              const sSym = (suitSymbols && suitSymbols[su]) || (SUIT_SYM && SUIT_SYM[su]) || (su === 'H' ? '♥' : su === 'D' ? '♦' : su === 'S' ? '♠' : su === 'C' ? '♣' : su);
              return `
                <span class="trainer-mini-card ${isRed ? 'card-red' : 'card-black'}"
                      style="display: inline-flex; align-items: center; justify-content: center; gap: 3px; padding: 2px 7px; background: #ffffff !important; color: ${col} !important; -webkit-text-fill-color: ${col} !important; border-radius: 5px; font-weight: 800; font-size: 0.88rem; border: 1.5px solid #cbd5e1; box-shadow: 0 1px 3px rgba(0,0,0,0.25); margin-right: 5px; vertical-align: middle;">
                  <span class="card-val card-rank" style="color: ${col} !important; -webkit-text-fill-color: ${col} !important; font-weight: 900; line-height: 1;">${rName}</span>
                  <span class="card-val card-suit" style="color: ${col} !important; -webkit-text-fill-color: ${col} !important; font-size: 1.05rem; line-height: 1;">${sSym}</span>
                </span>
              `;
            }).join('');

            return `
              <div style="display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; background: rgba(255,255,255,0.04); border-radius: 6px; margin-bottom: 6px; flex-wrap: wrap; gap: 6px;">
                <div style="display: flex; align-items: center; gap: 6px;">
                  <span style="font-weight: 800; color: #f59e0b; font-size: 0.88rem;">+${ln.n} pts</span>
                  <span style="color: #f1f5f9; font-weight: 600; font-size: 0.86rem;">${ln.why}</span>
                </div>
                <div style="display: flex; align-items: center; flex-wrap: wrap; gap: 4px;">${cardsHtml}</div>
              </div>
            `;
          }).join('');

          detailsHtml = `
            <div style="margin-top: 14px; padding: 14px; background: rgba(0,0,0,0.35); border-radius: 8px; border: 1px solid rgba(245,158,11,0.3);">
              <div style="font-size: 0.88rem; font-weight: 800; color: #f59e0b; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.04em;">
                🔍 Point-by-Point Score Breakdown (Total: ${actualBreakdown.total} pts)
              </div>
              ${detailRows}
            </div>
          `;
        }
      }

      // EV Table Breakdown
      let evTableHtml = '';
      if (showEvTable && discardEvalResult && discardEvalResult.analysis) {
        const { analysis, evaluation, isCorrect } = discardEvalResult;
        const totalCombos = analysis.combos.length;
        const reqDisc = getRequiredDiscards(mode);

        const renderMiniCards = (cardList) => {
          return (cardList || []).map(c => {
            const su = String((c && (c.suit || c.s || c.rawSuit)) || '').toUpperCase();
            const isRed = su === 'H' || su === 'D' || su === '♥' || su === '♦';
            const col = isRed ? '#dc2626' : '#111827';
            const rVal = c ? (c.rank !== undefined ? c.rank : (c.r !== undefined ? c.r : 0)) : 0;
            const rName = (c && c.rankName) || (RANK_NAME && RANK_NAME[rVal]) || (rVal === 1 ? 'A' : rVal === 11 ? 'J' : rVal === 12 ? 'Q' : rVal === 13 ? 'K' : String(rVal || ''));
            const sSym = (suitSymbols && suitSymbols[su]) || (SUIT_SYM && SUIT_SYM[su]) || (su === 'H' ? '♥' : su === 'D' ? '♦' : su === 'S' ? '♠' : su === 'C' ? '♣' : su);
            return `
              <span class="trainer-mini-card ${isRed ? 'card-red' : 'card-black'}"
                    style="display: inline-flex; align-items: center; justify-content: center; gap: 3px; padding: 2px 7px; background: #ffffff !important; color: ${col} !important; -webkit-text-fill-color: ${col} !important; border-radius: 5px; font-weight: 800; font-size: 0.84rem; border: 1.5px solid #cbd5e1; box-shadow: 0 1px 3px rgba(0,0,0,0.25); margin-right: 4px; vertical-align: middle;">
                <span class="card-val card-rank" style="color: ${col} !important; -webkit-text-fill-color: ${col} !important; font-weight: 900; line-height: 1;">${rName}</span>
                <span class="card-val card-suit" style="color: ${col} !important; -webkit-text-fill-color: ${col} !important; font-size: 1rem; line-height: 1;">${sSym}</span>
              </span>
            `;
          }).join('');
        };

        const optimalMiniCards = renderMiniCards(evaluation.optimal.disc);
        const userMiniCards = renderMiniCards(evaluation.chosen.disc);

        const bannerHtml = isCorrect ? `
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; background: rgba(16,185,129,0.16); border: 1.5px solid rgba(16,185,129,0.45); border-radius: 8px; margin-bottom: 12px; flex-wrap: wrap; gap: 10px;">
            <div style="display: flex; align-items: center; gap: 10px;">
              <span style="font-size: 1.6rem; line-height: 1;">🏆</span>
              <div>
                <div style="font-size: 1rem; font-weight: 900; color: #34d399; letter-spacing: 0.02em;">
                  Optimal Discard! You Discarded Correctly
                </div>
                <div style="font-size: 0.82rem; color: #d1fae5; margin-top: 2px;">
                  Your discard ${userMiniCards} achieved highest Net Expected Value (${evaluation.chosen.netEV > 0 ? '+' : ''}${evaluation.chosen.netEV.toFixed(1)} pts).
                </div>
              </div>
            </div>
            <span style="padding: 4px 12px; border-radius: 6px; background: #10b981; color: #000; font-weight: 900; font-size: 0.86rem; letter-spacing: 0.03em;">
              Rank #1 of ${totalCombos}
            </span>
          </div>
        ` : `
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; background: rgba(239,68,68,0.16); border: 1.5px solid rgba(239,68,68,0.45); border-radius: 8px; margin-bottom: 12px; flex-wrap: wrap; gap: 10px;">
            <div style="display: flex; align-items: center; gap: 10px;">
              <span style="font-size: 1.6rem; line-height: 1;">⚠️</span>
              <div>
                <div style="font-size: 1rem; font-weight: 900; color: #f87171; letter-spacing: 0.02em;">
                  Sub-Optimal Discard (-${Number(evaluation.evDiff ?? evaluation.diff ?? 0).toFixed(1)} EV Loss)
                </div>
                <div style="font-size: 0.82rem; color: #fee2e2; margin-top: 2px;">
                  You discarded: ${userMiniCards} (Rank #${evaluation.rank} of ${totalCombos}). Optimal discard was: ${optimalMiniCards} (${evaluation.optimal.netEV > 0 ? '+' : ''}${evaluation.optimal.netEV.toFixed(1)} Net EV).
                </div>
              </div>
            </div>
            <span style="padding: 4px 12px; border-radius: 6px; background: #ef4444; color: #fff; font-weight: 900; font-size: 0.86rem; letter-spacing: 0.03em;">
              Rank #${evaluation.rank} of ${totalCombos}
            </span>
          </div>
        `;

        const tableRowsHtml = analysis.combos.map((combo) => {
          const isUserPick = combo.id === evaluation.chosen.id;
          const isOptimalPick = combo.rank === 1;
          let rowBg = 'transparent';
          if (isUserPick && isOptimalPick) rowBg = 'rgba(16, 185, 129, 0.2)';
          else if (isUserPick) rowBg = 'rgba(240, 198, 74, 0.18)';
          else if (isOptimalPick) rowBg = 'rgba(16, 185, 129, 0.12)';

          const discMiniHtml = renderMiniCards(combo.disc);
          const keepMiniHtml = renderMiniCards(combo.keep);

          return `
            <tr style="background: ${rowBg}; border-bottom: 1px solid rgba(255,255,255,0.06); font-size: 0.82rem;">
              <td style="padding: 6px 8px; font-weight: 800; color: ${isOptimalPick ? '#10b981' : (isUserPick ? '#f0c64a' : '#a8a29e')}; text-align: center;">
                ${combo.rank}${isOptimalPick ? ' ★' : ''}${isUserPick ? ' 👈' : ''}
              </td>
              <td style="padding: 6px 8px; white-space: nowrap;">${discMiniHtml}</td>
              <td style="padding: 6px 8px; white-space: nowrap;">${keepMiniHtml}</td>
              <td style="padding: 6px 8px; text-align: center; color: #e7e5e4;">
                <span style="font-weight: 700;">${combo.handEV.toFixed(1)}</span>
                <span style="font-size: 0.72rem; color: #78716c; margin-left: 4px;">(${combo.handMin}–${combo.handMax})</span>
              </td>
              <td style="padding: 6px 8px; text-align: center; color: #a8a29e;">${combo.cribEV.toFixed(1)}</td>
              <td style="padding: 6px 8px; text-align: center; font-weight: 800; color: ${isOptimalPick ? '#10b981' : '#f4ead4'};">
                ${combo.netEV > 0 ? '+' : ''}${combo.netEV.toFixed(1)}
              </td>
              <td style="padding: 6px 8px; text-align: center; font-size: 0.76rem; color: ${combo.evDiff === 0 ? '#10b981' : '#f87171'};">
                ${combo.evDiff === 0 ? '0.0' : `-${combo.evDiff.toFixed(1)}`}
              </td>
            </tr>
          `;
        }).join('');

        evTableHtml = `
          <div style="margin-top: 14px; padding: 14px; background: rgba(0,0,0,0.38); border-radius: 10px; border: 1.5px solid rgba(245,158,11,0.35);">
            <div style="font-size: 0.88rem; font-weight: 800; color: #f59e0b; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.04em;">
              📊 Statistical Discard Expected Value (EV) Table (${totalCombos} Combinations)
            </div>
            ${bannerHtml}
            ${evaluation.tips && evaluation.tips.length ? `
              <div style="margin-bottom: 12px; padding: 8px 12px; background: rgba(240,198,74,0.08); border-left: 3px solid #f0c64a; border-radius: 4px; font-size: 0.82rem; color: #fef08a; line-height: 1.45;">
                ${evaluation.tips.map(t => `<div>${t}</div>`).join('')}
              </div>
            ` : ''}
            <div style="max-height: 250px; overflow-y: auto; border: 1px solid rgba(255,255,255,0.08); border-radius: 8px;">
              <table style="width: 100%; border-collapse: collapse; text-align: left; font-family: inherit;">
                <thead>
                  <tr style="background: rgba(0,0,0,0.6); border-bottom: 1px solid rgba(255,255,255,0.15); font-size: 0.74rem; text-transform: uppercase; letter-spacing: 0.05em; color: #a8a29e;">
                    <th style="padding: 6px 8px; text-align: center;">Rank</th>
                    <th style="padding: 6px 8px;">Discard (${reqDisc})</th>
                    <th style="padding: 6px 8px;">Keep (${mode - reqDisc})</th>
                    <th style="padding: 6px 8px; text-align: center;">Hand EV (Min–Max)</th>
                    <th style="padding: 6px 8px; text-align: center;">Crib EV</th>
                    <th style="padding: 6px 8px; text-align: center;">Net Score</th>
                    <th style="padding: 6px 8px; text-align: center;">Loss</th>
                  </tr>
                </thead>
                <tbody>
                  ${tableRowsHtml}
                </tbody>
              </table>
            </div>
          </div>
        `;
      }

      // Custom Hand Form
      let customFormHtml = '';
      if (showCustomForm) {
        customFormHtml = `
          <div style="margin-bottom: 16px; padding: 14px; background: rgba(15,23,42,0.85); border-radius: 10px; border: 1px solid rgba(56,189,248,0.4);">
            <div style="font-weight: 800; font-size: 0.92rem; color: #38bdf8; margin-bottom: 6px;">
              ✏️ Enter Custom Hand to Count
            </div>
            <div style="font-size: 0.82rem; color: #cbd5e1; margin-bottom: 8px; line-height: 1.45;">
              Enter <strong>5 cards</strong> (4 hand cards + 1 cut card) or <strong>7 cards</strong> (6 hand cards + 1 cut card).<br/>
              The <strong>last card</strong> is always the starter cut card.<br/>
              <span style="color: #94a3b8; font-size: 0.78rem;">Examples: <code>5H 5D 5S JC 5C</code> or <code>5H 5D 5S JC 7H 8D 5C</code> (optional <code>+</code> before cut card also accepted)</span>
            </div>
            <div style="display: flex; gap: 8px; flex-wrap: wrap;">
              <input type="text" id="countingCustomHandInput" placeholder="e.g. 5H 5D 5S JC 5C"
                     style="flex: 1; min-width: 200px; padding: 8px 12px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.2); background: rgba(0,0,0,0.4); color: #fff; font-family: monospace; font-size: 0.95rem;" />
              <button type="button" id="btnCountingLoadCustom" class="btn-primary" style="padding: 8px 16px; font-weight: 800; font-size: 0.88rem; border-radius: 6px; background: #0284c7; color: #fff; border: none; cursor: pointer;">
                Count Hand
              </button>
              <button type="button" id="btnCountingCancelCustom" style="padding: 8px 12px; font-weight: 700; font-size: 0.85rem; border-radius: 6px; background: transparent; border: 1px solid rgba(255,255,255,0.2); color: #cbd5e1; cursor: pointer;">
                Cancel
              </button>
            </div>
            ${customError ? `<div style="color: #ef4444; font-size: 0.82rem; font-weight: 700; margin-top: 6px;">${customError}</div>` : ''}
          </div>
        `;
      }

      // Result Feedback Banner
      let bannerHtml = '';
      if (evalResult) {
        if (evalResult.isAllCorrect) {
          bannerHtml = `
            <div style="margin-bottom: 14px; padding: 12px 16px; border-radius: 8px; background: rgba(16,185,129,0.2); border: 1px solid rgba(16,185,129,0.5); display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px;">
              <div style="display: flex; align-items: center; gap: 8px;">
                <span style="font-size: 1.4rem;">🎉</span>
                <span style="font-weight: 800; color: #34d399; font-size: 1rem;">Perfect Count! All categories correct (+${evalResult.total.actual} pts).</span>
              </div>
              <span style="font-size: 0.82rem; font-weight: 800; padding: 3px 8px; border-radius: 12px; background: #10b981; color: #000;">STREAK: ${career.currentStreak} 🔥</span>
            </div>
          `;
        } else {
          bannerHtml = `
            <div style="margin-bottom: 14px; padding: 12px 16px; border-radius: 8px; background: rgba(239,68,68,0.2); border: 1px solid rgba(239,68,68,0.5); display: flex; align-items: center; gap: 8px;">
              <span style="font-size: 1.4rem;">❌</span>
              <span style="font-weight: 800; color: #f87171; font-size: 0.95rem;">Not quite! Review the red marks below. Actual Total: ${evalResult.total.actual} pts.</span>
            </div>
          `;
        }
      }

      // Live Total Row
      let totalBadgeHtml = '';
      if (evalResult) {
        if (evalResult.total.correct) {
          totalBadgeHtml = `<span style="color: #10b981 !important; font-weight: 900; font-size: 1.15rem;">✔ ${evalResult.total.actual} pts</span>`;
        } else {
          totalBadgeHtml = `<span style="color: #ef4444 !important; font-weight: 900; font-size: 0.95rem;">✖ Entered ${evalResult.total.user} (Actual: ${evalResult.total.actual})</span>`;
        }
      }

      const totalRowHtml = `
        <div style="margin-top: 12px; padding: 12px 16px; background: rgba(15,23,42,0.6); border-radius: 8px; border: 2px solid ${evalResult ? (evalResult.total.correct ? '#10b981' : '#ef4444') : 'rgba(52,211,153,0.4)'}; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 1.25rem;">🧮</span>
            <span style="font-weight: 900; font-size: 1.1rem; color: #f8fafc; letter-spacing: 0.03em;">TOTAL:</span>
            <span id="countingLiveTotalDisplay" style="font-weight: 900; font-size: 1.35rem; color: #34d399; margin-left: 4px;">${currentLiveTotal}</span>
            <span style="font-size: 0.85rem; color: #94a3b8; font-weight: 600;">pts</span>
          </div>
          <div>
            ${totalBadgeHtml}
          </div>
        </div>
      `;

      // Toast feedback
      const toastHtml = toastMessage ? `
        <div style="margin-bottom: 12px; padding: 8px 14px; background: rgba(56,189,248,0.2); border: 1px solid rgba(56,189,248,0.5); border-radius: 6px; color: #38bdf8; font-weight: 700; font-size: 0.85rem; text-align: center;">
          ${toastMessage}
        </div>
      ` : '';

      // Share text and challenge panel
      const cardsToShare = (phase === 'counting' && keptHand) ? keptHand : (dealtHand || []);
      const starterToShare = (phase === 'counting') ? starterCard : null;
      currentShareText = (cardsToShare && cardsToShare.length)
        ? CountingTrainer.generateShareText(cardsToShare, starterToShare, mode)
        : '';
      currentShareUrl = (cardsToShare && cardsToShare.length)
        ? CountingTrainer.encodeShareUrl(cardsToShare, starterToShare, mode)
        : '';

      let sharePanelHtml = '';
      if (showSharePanel && currentShareText) {
        const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
        sharePanelHtml = `
          <div id="countingSharePanel" style="margin-top: 14px; padding: 14px; background: rgba(15,23,42,0.92); border-radius: 10px; border: 1.5px solid rgba(56,189,248,0.45); box-shadow: 0 4px 14px rgba(0,0,0,0.4);">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
              <div style="font-weight: 800; font-size: 0.95rem; color: #38bdf8; display: flex; align-items: center; gap: 6px;">
                🔗 Share Counting Challenge
              </div>
              <button type="button" id="btnCountingCloseShare" style="background: none; border: none; color: #94a3b8; font-size: 1.1rem; cursor: pointer; padding: 2px 6px;" title="Close share panel">✕</button>
            </div>

            <div style="font-size: 0.82rem; color: #cbd5e1; margin-bottom: 12px; line-height: 1.45;">
              Share this challenge link with friends or your cribbage club! Anyone opening the link will load this exact hand to count.
            </div>

            <div style="margin-bottom: 12px;">
              <label style="display: block; font-size: 0.74rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; margin-bottom: 4px;">Challenge URL</label>
              <div style="display: flex; gap: 8px;">
                <input type="text" readonly id="countingShareUrlInput" value="${currentShareUrl}"
                       style="flex: 1; min-width: 0; padding: 8px 10px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.2); background: rgba(0,0,0,0.5); color: #38bdf8; font-family: monospace; font-size: 0.84rem;" />
                <button type="button" id="btnCountingCopyUrl" style="padding: 8px 14px; font-weight: 800; font-size: 0.82rem; border-radius: 6px; background: #0284c7; color: #fff; border: none; cursor: pointer; white-space: nowrap;">
                  📋 Copy Link
                </button>
              </div>
            </div>

            <div style="margin-bottom: 10px;">
              <label style="display: block; font-size: 0.74rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; margin-bottom: 4px;">Formatted Challenge Message</label>
              <div style="display: flex; flex-direction: column; gap: 8px;">
                <textarea readonly id="countingShareTextInput" rows="4"
                          style="width: 100%; box-sizing: border-box; padding: 8px 10px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.2); background: rgba(0,0,0,0.5); color: #e2e8f0; font-size: 0.82rem; resize: none; font-family: inherit; line-height: 1.4;">${currentShareText}</textarea>
                <div style="display: flex; gap: 8px; justify-content: flex-end; flex-wrap: wrap;">
                  ${canNativeShare ? `
                    <button type="button" id="btnCountingNativeShare" style="padding: 8px 14px; font-weight: 800; font-size: 0.82rem; border-radius: 6px; background: rgba(56,189,248,0.2); border: 1px solid #38bdf8; color: #38bdf8; cursor: pointer;">
                      📱 Device Share...
                    </button>
                  ` : ''}
                  <button type="button" id="btnCountingCopyText" style="padding: 8px 14px; font-weight: 800; font-size: 0.82rem; border-radius: 6px; background: #0284c7; color: #fff; border: none; cursor: pointer; white-space: nowrap;">
                    📋 Copy Message
                  </button>
                </div>
              </div>
            </div>

            ${shareCopiedNotice ? `
              <div style="padding: 7px 12px; background: rgba(16,185,129,0.2); border: 1px solid rgba(16,185,129,0.5); border-radius: 6px; color: #34d399; font-weight: 700; font-size: 0.84rem; text-align: center; margin-top: 8px;">
                ${shareCopiedNotice}
              </div>
            ` : ''}
          </div>
        `;
      }

      const modalHtml = `
        <div class="counting-trainer-modal" style="max-width: 660px; width: 100%; box-sizing: border-box; font-family: inherit; color: #f8fafc;">
          <!-- Header -->
          <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1.5px solid rgba(255,255,255,0.1); padding-bottom: 12px; margin-bottom: 14px; flex-wrap: wrap; gap: 10px;">
            <div style="display: flex; align-items: center; gap: 10px;">
              <span style="font-size: 1.6rem;">🧮</span>
              <div>
                <h2 style="margin: 0; font-size: 1.3rem; font-weight: 900; color: #34d399; letter-spacing: 0.03em; line-height: 1.2;">
                  COUNTING TRAINER
                </h2>
                <span style="font-size: 0.78rem; color: #94a3b8; font-weight: 600;">
                  Master hand scoring under pressure
                </span>
              </div>
            </div>
            <!-- Mode Switcher -->
            <div style="display: flex; gap: 4px; background: rgba(0,0,0,0.4); padding: 3px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1);">
              <button type="button" id="btnCountingMode6" style="padding: 5px 12px; font-weight: 800; font-size: 0.78rem; border-radius: 6px; border: none; cursor: pointer; background: ${mode === 6 ? '#34d399' : 'transparent'}; color: ${mode === 6 ? '#0f172a' : '#cbd5e1'};">
                6 Cards (Keep 4)
              </button>
              <button type="button" id="btnCountingMode9" style="padding: 5px 12px; font-weight: 800; font-size: 0.78rem; border-radius: 6px; border: none; cursor: pointer; background: ${mode === 9 ? '#34d399' : 'transparent'}; color: ${mode === 9 ? '#0f172a' : '#cbd5e1'};">
                9 Cards (Keep 6)
              </button>
            </div>
          </div>

          <!-- Stats Bar -->
          <div style="display: flex; justify-content: space-between; align-items: center; background: rgba(0,0,0,0.35); border-radius: 8px; padding: 8px 14px; margin-bottom: 14px; border: 1px solid rgba(255,255,255,0.08); flex-wrap: wrap; gap: 8px;">
            <div style="display: flex; gap: 14px; align-items: center; flex-wrap: wrap; font-size: 0.82rem;">
              <span>Hands: <strong style="color: #fff;">${career.handsPlayed}</strong></span>
              <span>Correct: <strong style="color: #34d399;">${career.handsCorrect}</strong></span>
              <span>Wrong: <strong style="color: #f87171;">${career.handsWrong}</strong></span>
              <span>Accuracy: <strong style="color: #38bdf8;">${career.accuracyPct}%</strong></span>
              <span>Streak: <strong style="color: #f59e0b;">🔥 ${career.currentStreak}</strong> (Best: ${career.bestStreak})</span>
            </div>
            <button type="button" id="btnCountingResetStats" style="background: none; border: none; color: #94a3b8; font-size: 0.74rem; cursor: pointer; text-decoration: underline;">
              Reset Stats
            </button>
          </div>

          ${toastHtml}
          ${customFormHtml}
          ${bannerHtml}
          ${cardsDisplayHtml}

          <!-- Counting Phase Form -->
          ${phase === 'counting' ? `
            <div style="background: rgba(0,0,0,0.25); border-radius: 12px; padding: 14px; border: 1px solid rgba(255,255,255,0.08); margin-bottom: 14px;">
              <div style="font-size: 0.88rem; font-weight: 800; color: #cbd5e1; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.04em;">
                Enter Your Point Counts:
              </div>
              <div style="display: flex; flex-direction: column; gap: 8px;">
                ${renderCategoryRow('fifteens', "15's", '🎯', 2)}
                ${renderCategoryRow('pairs', 'Pairs', '👥', 2)}
                ${renderCategoryRow('runs', 'Runs', '🏃', 1)}
                ${renderCategoryRow('flush', 'Flush', '🌊', 1)}
                ${renderCategoryRow('nobs', 'Nobs', '👑', 1)}
              </div>
              ${totalRowHtml}
            </div>

            <!-- Action Controls -->
            <div style="display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 12px;">
              <div style="display: flex; gap: 8px; flex-wrap: wrap;">
                ${!evalResult ? `
                  <button type="button" id="btnCountingCheckScore" class="btn-primary"
                          style="padding: 10px 22px; font-weight: 800; font-size: 0.95rem; border-radius: 8px; background: linear-gradient(135deg, #10b981, #059669); color: #fff; border: none; cursor: pointer; box-shadow: 0 4px 12px rgba(16,185,129,0.4);">
                    🔍 Check Score
                  </button>
                ` : `
                  <button type="button" id="btnCountingNextHand" class="btn-primary"
                          style="padding: 10px 22px; font-weight: 800; font-size: 0.95rem; border-radius: 8px; background: linear-gradient(135deg, #3b82f6, #2563eb); color: #fff; border: none; cursor: pointer; box-shadow: 0 4px 12px rgba(59,130,246,0.4);">
                    ➡️ Next Hand
                  </button>
                `}
                <button type="button" id="btnCountingToggleDetails"
                        style="padding: 9px 14px; font-weight: 700; font-size: 0.86rem; border-radius: 8px; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.18); color: #cbd5e1; cursor: pointer;">
                  ${showDetails ? '🙈 Hide Point Details' : '👁️ Show Point Details'}
                </button>
                ${discardEvalResult ? `
                  <button type="button" id="btnCountingToggleEvTable"
                          style="padding: 9px 14px; font-weight: 700; font-size: 0.86rem; border-radius: 8px; background: ${showEvTable ? 'rgba(245,158,11,0.22)' : 'rgba(255,255,255,0.08)'}; border: 1px solid ${showEvTable ? '#f59e0b' : 'rgba(255,255,255,0.18)'}; color: ${showEvTable ? '#fbbf24' : '#cbd5e1'}; cursor: pointer; display: inline-flex; align-items: center; gap: 6px;">
                    ${showEvTable ? '📊 Hide EV Table' : '📊 Show EV Table'}
                    <span style="font-size: 0.72rem; padding: 2px 7px; border-radius: 10px; font-weight: 900; background: ${discardEvalResult.isCorrect ? '#10b981' : '#ef4444'}; color: #fff;">
                      ${discardEvalResult.isCorrect ? '✔ Optimal' : `✖ Rank #${discardEvalResult.evaluation.rank}`}
                    </span>
                  </button>
                ` : ''}
              </div>

              <div style="display: flex; gap: 6px;">
                <button type="button" id="btnCountingShareHand" title="Share this hand challenge"
                        style="padding: 9px 13px; font-weight: 700; font-size: 0.84rem; border-radius: 8px; background: rgba(56,189,248,0.12); border: 1px solid rgba(56,189,248,0.35); color: #38bdf8; cursor: pointer; display: inline-flex; align-items: center; gap: 4px;">
                  🔗 Share
                </button>
                <button type="button" id="btnCountingToggleCustom" title="Enter custom hand"
                        style="padding: 9px 13px; font-weight: 700; font-size: 0.84rem; border-radius: 8px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.18); color: #cbd5e1; cursor: pointer;">
                  ✏️ Custom Hand
                </button>
              </div>
            </div>
            ${detailsHtml}
            ${evTableHtml}
            ${sharePanelHtml}
          ` : `
            <!-- In Discard Phase, secondary options -->
            <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 10px; flex-wrap: wrap;">
              <button type="button" id="btnCountingShareHand" title="Share this hand challenge"
                      style="padding: 8px 14px; font-weight: 700; font-size: 0.84rem; border-radius: 8px; background: rgba(56,189,248,0.12); border: 1px solid rgba(56,189,248,0.35); color: #38bdf8; cursor: pointer; display: inline-flex; align-items: center; gap: 4px;">
                🔗 Share
              </button>
              <button type="button" id="btnCountingNewDeal"
                      style="padding: 8px 14px; font-weight: 700; font-size: 0.84rem; border-radius: 8px; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.18); color: #cbd5e1; cursor: pointer;">
                🎲 Redeal
              </button>
              <button type="button" id="btnCountingToggleCustom"
                      style="padding: 8px 14px; font-weight: 700; font-size: 0.84rem; border-radius: 8px; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.18); color: #cbd5e1; cursor: pointer;">
                ✏️ Custom Hand
              </button>
            </div>
            ${sharePanelHtml}
          `}
        </div>
      `;

      this.showModal(modalHtml, true);
      bindCountingEvents();
    };

    const bindCountingEvents = () => {
      const modalEl = document.getElementById('modal') || document;
      const getEl = (id) => modalEl.querySelector('#' + id);

      // Mode buttons
      const btnMode6 = getEl('btnCountingMode6');
      const btnMode9 = getEl('btnCountingMode9');
      if (btnMode6) btnMode6.onclick = () => { if (mode !== 6) startNewHand(6); };
      if (btnMode9) btnMode9.onclick = () => { if (mode !== 9) startNewHand(9); };

      // Reset Stats
      const btnResetStats = getEl('btnCountingResetStats');
      if (btnResetStats) {
        btnResetStats.onclick = () => {
          if (confirm('Reset your Counting Trainer statistics?')) {
            CountingTrainer.resetStats({ career: true, storage: context.storage });
            renderCountingTrainer();
          }
        };
      }

      // Discard Phase card toggling
      if (phase === 'discard') {
        const requiredDiscards = getRequiredDiscards(mode);
        const cardBtns = modalEl.querySelectorAll('.counting-card-btn');
        cardBtns.forEach(btn => {
          btn.onclick = () => {
            const idx = parseInt(btn.dataset.idx, 10);
            if (selectedIndices.has(idx)) {
              selectedIndices.delete(idx);
            } else {
              if (selectedIndices.size >= requiredDiscards) {
                const first = selectedIndices.values().next().value;
                selectedIndices.delete(first);
              }
              selectedIndices.add(idx);
            }
            renderCountingTrainer();
          };
        });

        const btnCut = getEl('btnCountingCutStarter');
        if (btnCut) {
          btnCut.onclick = () => {
            if (selectedIndices.size !== requiredDiscards) return;
            userDiscardedCards = CountingTrainer.sortCards(dealtHand.filter((_, idx) => selectedIndices.has(idx)));
            originalDealtHand = dealtHand.slice();
            keptHand = CountingTrainer.sortCards(dealtHand.filter((_, idx) => !selectedIndices.has(idx)));
            starterCard = CountingTrainer.cutStarterCard(dealtHand);
            phase = 'counting';
            actualBreakdown = CountingTrainer.scoreCountedHand(keptHand, starterCard, false);
            evalResult = null;
            userInputs = { fifteens: 0, pairs: 0, runs: 0, flush: 0, nobs: 0 };
            showDetails = false;
            showEvTable = false;
            showSharePanel = false;
            shareCopiedNotice = '';
            if (originalDealtHand && userDiscardedCards) {
              discardEvalResult = CountingTrainer.analyzeDiscards(originalDealtHand, userDiscardedCards, true);
            }
            renderCountingTrainer();
          };
        }

        const btnNewDeal = getEl('btnCountingNewDeal');
        if (btnNewDeal) btnNewDeal.onclick = () => startNewHand(mode);
      }

      // Counting Phase form handling
      if (phase === 'counting') {
        const updateLiveTotal = () => {
          const tot = (Number(userInputs.fifteens) || 0) +
                      (Number(userInputs.pairs) || 0) +
                      (Number(userInputs.runs) || 0) +
                      (Number(userInputs.flush) || 0) +
                      (Number(userInputs.nobs) || 0);
          const liveTotEl = getEl('countingLiveTotalDisplay');
          if (liveTotEl) liveTotEl.textContent = String(tot);
        };

        // Stepper buttons
        const stepBtns = modalEl.querySelectorAll('.count-step-btn');
        stepBtns.forEach(btn => {
          btn.onclick = () => {
            const cat = btn.dataset.cat;
            const delta = parseInt(btn.dataset.delta, 10) || 0;
            const cur = Number(userInputs[cat]) || 0;
            const next = Math.max(0, cur + delta);
            userInputs[cat] = next;
            const inp = getEl(`inputCat_${cat}`);
            if (inp) inp.value = String(next);
            updateLiveTotal();
          };
        });

        // Numeric inputs
        const inputs = modalEl.querySelectorAll('.count-input-field');
        inputs.forEach(inp => {
          inp.oninput = () => {
            const cat = inp.dataset.cat;
            const v = Math.max(0, parseInt(inp.value, 10) || 0);
            userInputs[cat] = v;
            updateLiveTotal();
          };
        });

        // Check Score
        const btnCheck = getEl('btnCountingCheckScore');
        if (btnCheck) {
          btnCheck.onclick = () => {
            evalResult = CountingTrainer.evaluateUserCount(actualBreakdown, userInputs);
            CountingTrainer.recordPlay({ isCorrect: evalResult.isAllCorrect }, context.storage);
            renderCountingTrainer();
          };
        }

        // Next Hand
        const btnNext = getEl('btnCountingNextHand');
        if (btnNext) btnNext.onclick = () => startNewHand(mode);

        // Toggle Details
        const btnDetails = getEl('btnCountingToggleDetails');
        if (btnDetails) {
          btnDetails.onclick = () => {
            showDetails = !showDetails;
            renderCountingTrainer();
          };
        }

        // Toggle EV Table
        const btnToggleEv = getEl('btnCountingToggleEvTable');
        if (btnToggleEv) {
          btnToggleEv.onclick = () => {
            showEvTable = !showEvTable;
            renderCountingTrainer();
          };
        }
      }

      // Share Hand action button (available in both phases)
      const btnShare = getEl('btnCountingShareHand');
      if (btnShare) {
        btnShare.onclick = async () => {
          showSharePanel = !showSharePanel;
          if (showSharePanel) {
            const ok = await copyTextToClipboard(currentShareText);
            shareCopiedNotice = ok ? '✔ Challenge copied to clipboard!' : 'Challenge ready in Share Panel below.';
            toastMessage = ok ? '✔ Challenge copied to clipboard!' : 'Challenge ready in Share Panel below.';
          } else {
            shareCopiedNotice = '';
            toastMessage = '';
          }
          renderCountingTrainer();
        };
      }

      // Share Panel inner controls
      const btnCloseShare = getEl('btnCountingCloseShare');
      if (btnCloseShare) {
        btnCloseShare.onclick = () => {
          showSharePanel = false;
          shareCopiedNotice = '';
          renderCountingTrainer();
        };
      }

      const btnCopyUrl = getEl('btnCountingCopyUrl');
      if (btnCopyUrl) {
        btnCopyUrl.onclick = async () => {
          const input = getEl('countingShareUrlInput');
          const val = input ? input.value : currentShareUrl;
          if (input) input.select();
          await copyTextToClipboard(val);
          btnCopyUrl.textContent = '✔ Copied!';
          setTimeout(() => { if (btnCopyUrl) btnCopyUrl.textContent = '📋 Copy Link'; }, 2000);
        };
      }

      const btnCopyText = getEl('btnCountingCopyText');
      if (btnCopyText) {
        btnCopyText.onclick = async () => {
          const ta = getEl('countingShareTextInput');
          const val = ta ? ta.value : currentShareText;
          if (ta) ta.select();
          await copyTextToClipboard(val);
          btnCopyText.textContent = '✔ Copied!';
          setTimeout(() => { if (btnCopyText) btnCopyText.textContent = '📋 Copy Message'; }, 2000);
        };
      }

      const btnNativeShare = getEl('btnCountingNativeShare');
      if (btnNativeShare) {
        btnNativeShare.onclick = () => {
          if (typeof navigator !== 'undefined' && navigator.share) {
            navigator.share({
              title: 'Crazy Cribbage Counting Challenge',
              text: currentShareText,
              url: currentShareUrl
            }).catch(() => {});
          }
        };
      }

      // Toggle Custom Hand
      const btnToggleCust = getEl('btnCountingToggleCustom');
      if (btnToggleCust) {
        btnToggleCust.onclick = () => {
          showCustomForm = !showCustomForm;
          customError = '';
          renderCountingTrainer();
        };
      }

      const btnCancelCust = getEl('btnCountingCancelCustom');
      if (btnCancelCust) {
        btnCancelCust.onclick = () => {
          showCustomForm = false;
          customError = '';
          renderCountingTrainer();
        };
      }

      const btnLoadCust = getEl('btnCountingLoadCustom');
      if (btnLoadCust) {
        btnLoadCust.onclick = () => {
          const inp = getEl('countingCustomHandInput');
          const val = inp ? inp.value.trim() : '';
          const parsed = CountingTrainer.parseCustomHand(val, mode);
          if (!parsed || !parsed.isComplete) {
            customError = 'Please enter 5 cards (4 in hand + 1 cut card) or 7 cards (6 in hand + 1 cut card). The last card will be the cut card. E.g. 5H 5D 5S JC 5C';
            renderCountingTrainer();
            return;
          }
          mode = parsed.mode;
          keptHand = parsed.cards;
          starterCard = parsed.starter;
          phase = 'counting';
          actualBreakdown = CountingTrainer.scoreCountedHand(keptHand, starterCard, false);
          userDiscardedCards = null;
          originalDealtHand = null;
          discardEvalResult = null;
          showEvTable = false;
          showCustomForm = false;
          customError = '';
          evalResult = null;
          userInputs = { fifteens: 0, pairs: 0, runs: 0, flush: 0, nobs: 0 };
          showDetails = false;
          renderCountingTrainer();
        };
      }
    };

    renderCountingTrainer();
  }

  /**
   * Displays the End-of-Game "Coach's Post-Mortem" & Match Accuracy Rating modal.
   * @param {Object} [context]
   */
  showCoachPostMortemModal(context = {}) {
    const report = CoachPostMortem.analyzeMatch(context);
    const html = CoachPostMortem.buildPostMortemHtml(report);
    this.showModal(html, {
      showClose: true,
      onClose: () => {
        if (typeof window !== 'undefined' && typeof window.showGameWonModal === 'function' && window.showGameWonModal()) {
          return;
        }
        if (typeof this.showGameWonModal === 'function' && this.showGameWonModal()) {
          return;
        }
        this.hideModal();
      }
    });
    if (typeof CoachPostMortem !== 'undefined' && typeof CoachPostMortem.bindEvents === 'function' && typeof document !== 'undefined') {
      const modalEl = document.getElementById('modal');
      CoachPostMortem.bindEvents(modalEl || document);
    }
  }

  /**
   * Displays the Coach's Match Post-Mortem Share Dialog.
   * @param {Object} [report]
   */
  showCoachPostMortemShareModal(report) {
    return CoachPostMortem.openShareDialog(report);
  }

  shareCoachPostMortem(report) {
    return CoachPostMortem.openShareDialog(report);
  }

  /**
   * Displays the Daily Puzzle ("Hand of the Day") modal.
   * Features:
   * 1. Daily Discard Challenge (Seeded 6-card hand, EV evaluation against optimal).
   * 2. Daily Pegging Scenario (Tactical endgame problem with count explanation).
   * 3. Streaks & Stats (Consecutive day counter, win %, Wordle-style share).
   */
  showDailyPuzzleModal(context = {}) {
    if (typeof document === 'undefined') return;

    const dateKey = context.dateKey || DailyPuzzleManager.getDailyDateKey();
    const dayNumber = DailyPuzzleManager.getDayNumber(dateKey);
    const dateFormatted = new Date(`${dateKey}T12:00:00`).toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    const { hand: dailyHand, isDealer: dailyIsDealer } = DailyPuzzleManager.getDailyHand(dateKey);
    const peggingScenario = DailyPuzzleManager.getDailyPeggingScenario(dateKey);

    let stats = DailyPuzzleManager.loadStats();
    let todayHistory = stats.history[dateKey] || {};

    let activeTab = context.tab || (todayHistory.discard && !todayHistory.pegging ? 'pegging' : 'discard');

    // Discard State
    let selectedDiscardIndices = new Set();
    let discardEvalResult = todayHistory.discard || null;

    // Pegging State
    let selectedPeggingCardId = todayHistory.pegging ? todayHistory.pegging.chosenCardId : null;
    let peggingEvalResult = todayHistory.pegging || null;

    let copyToastMessage = '';

    const suitSymbols = { H: '♥', D: '♦', S: '♠', C: '♣' };

    const render = () => {
      stats = DailyPuzzleManager.loadStats();
      todayHistory = stats.history[dateKey] || {};

      const tabsHtml = `
        <div class="daily-puzzle-tabs" style="display: flex; gap: 8px; justify-content: center; margin-bottom: 18px; border-bottom: 1px solid rgba(240,198,74,0.25); padding-bottom: 12px; flex-wrap: wrap;">
          <button type="button" id="tabBtnDiscard" class="daily-tab-btn ${activeTab === 'discard' ? 'active' : ''}"
                  style="display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 20px; font-weight: 800; font-size: 0.88rem; cursor: pointer; border: 1.5px solid ${activeTab === 'discard' ? '#f0c64a' : 'rgba(255,255,255,0.18)'}; background: ${activeTab === 'discard' ? 'rgba(240,198,74,0.2)' : 'rgba(255,255,255,0.05)'}; color: ${activeTab === 'discard' ? '#f0c64a' : '#d6d3d1'}; transition: all 0.15s ease;">
            <span>🃏 Daily Discard</span>
            ${todayHistory.discard ? '<span style="color: #22c55e; font-size: 0.85rem;">✔</span>' : ''}
          </button>
          <button type="button" id="tabBtnPegging" class="daily-tab-btn ${activeTab === 'pegging' ? 'active' : ''}"
                  style="display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 20px; font-weight: 800; font-size: 0.88rem; cursor: pointer; border: 1.5px solid ${activeTab === 'pegging' ? '#f0c64a' : 'rgba(255,255,255,0.18)'}; background: ${activeTab === 'pegging' ? 'rgba(240,198,74,0.2)' : 'rgba(255,255,255,0.05)'}; color: ${activeTab === 'pegging' ? '#f0c64a' : '#d6d3d1'}; transition: all 0.15s ease;">
            <span>⚡ Daily Pegging</span>
            ${todayHistory.pegging ? (todayHistory.pegging.isCorrect ? '<span style="color: #22c55e; font-size: 0.85rem;">✔</span>' : '<span style="color: #ef4444; font-size: 0.85rem;">❌</span>') : ''}
          </button>
          <button type="button" id="tabBtnStats" class="daily-tab-btn ${activeTab === 'stats' ? 'active' : ''}"
                  style="display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 20px; font-weight: 800; font-size: 0.88rem; cursor: pointer; border: 1.5px solid ${activeTab === 'stats' ? '#f0c64a' : 'rgba(255,255,255,0.18)'}; background: ${activeTab === 'stats' ? 'rgba(240,198,74,0.2)' : 'rgba(255,255,255,0.05)'}; color: ${activeTab === 'stats' ? '#f0c64a' : '#d6d3d1'}; transition: all 0.15s ease;">
            <span>🏆 Streaks &amp; Stats</span>
            ${stats.currentStreak > 0 ? `<span style="background: rgba(240,198,74,0.25); color: #f0c64a; padding: 1px 6px; border-radius: 10px; font-size: 0.75rem;">${stats.currentStreak}🔥</span>` : ''}
          </button>
        </div>
      `;

      let tabContentHtml = '';

      if (activeTab === 'discard') {
        const roleBadge = dailyIsDealer
          ? `<span style="background: rgba(240,198,74,0.18); color: #f0c64a; border: 1px solid rgba(240,198,74,0.4); padding: 4px 12px; border-radius: 14px; font-size: 0.82rem; font-weight: 700;">👑 DEALER (Your Crib)</span>`
          : `<span style="background: rgba(96,165,250,0.18); color: #93c5fd; border: 1px solid rgba(96,165,250,0.4); padding: 4px 12px; border-radius: 14px; font-size: 0.82rem; font-weight: 700;">🛡️ PONE (Opponent Crib)</span>`;

        const cardsHtml = dailyHand.map((c, idx) => {
          const isRed = c.suit === 'H' || c.suit === 'D';
          const sel = selectedDiscardIndices.has(idx) || (discardEvalResult && discardEvalResult.userCombo && discardEvalResult.userCombo.disc.some(d => d.id === c.id));
          const textColor = isRed ? '#dc2626 !important' : '#111827 !important';
          const isLocked = !!discardEvalResult;
          return `
            <button type="button" class="trainer-card-btn ${sel ? 'selected' : ''} ${isRed ? 'card-red' : 'card-black'}" data-idx="${idx}"
                    style="position: relative; width: 72px; height: 104px; box-sizing: border-box; background: #ffffff !important; border: ${sel ? '3px solid #eab308 !important' : '2px solid #cbd5e1 !important'}; border-radius: 8px; cursor: ${isLocked ? 'default' : 'pointer'}; display: flex; flex-direction: column; justify-content: space-between; padding: 6px 7px; box-shadow: ${sel ? '0 0 0 2px #ca8a04, 0 8px 18px rgba(234,179,8,0.45) !important' : '0 4px 10px rgba(0,0,0,0.35), 0 1px 3px rgba(0,0,0,0.15) !important'}; transform: ${sel ? 'translateY(-3px)' : 'none'}; transition: all 0.15s ease;"
                    title="${c.rankName || c.rank}${c.suit}">
              <div style="display: flex; align-items: baseline; justify-content: flex-start; gap: 2px; line-height: 1;">
                <span class="card-val" style="font-size: 1.2rem; font-weight: 900; color: ${textColor}; -webkit-text-fill-color: ${textColor}; line-height: 1;">${c.rankName || c.rank}</span>
                <span class="card-val" style="font-size: 0.9rem; font-weight: 700; color: ${textColor}; -webkit-text-fill-color: ${textColor}; line-height: 1;">${suitSymbols[c.suit] || c.suit}</span>
              </div>
              <div class="card-val" style="font-size: 2rem; color: ${textColor}; -webkit-text-fill-color: ${textColor}; line-height: 1; text-align: center; margin: 2px 0;">${suitSymbols[c.suit] || c.suit}</div>
              <span class="trainer-discard-badge" style="font-size: 0.58rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.03em; background: ${sel ? '#ca8a04 !important' : 'transparent'}; color: ${sel ? '#ffffff !important' : 'transparent'}; -webkit-text-fill-color: ${sel ? '#ffffff !important' : 'transparent'}; border-radius: 4px; padding: 2px 0; line-height: 1.1; text-align: center; display: block; width: 100%; box-sizing: border-box; visibility: ${sel ? 'visible' : 'hidden'};">DISCARD</span>
            </button>
          `;
        }).join('');

        let discardResultsHtml = '';
        if (discardEvalResult) {
          const analysis = DiscardTrainer.analyzeHand(dailyHand, dailyIsDealer);
          const gradeColors = { optimal: '#10b981', great: '#06b6d4', good: '#f59e0b', suboptimal: '#ef4444' };
          const badgeColor = gradeColors[discardEvalResult.grade] || '#f0c64a';

          const tableRows = analysis.combos.map(combo => {
            const isUserPick = combo.id === discardEvalResult.userCombo.id;
            const isOptimal = combo.rank === 1;
            let bg = 'transparent';
            if (isUserPick && isOptimal) bg = 'rgba(16, 185, 129, 0.2)';
            else if (isUserPick) bg = 'rgba(240, 198, 74, 0.15)';
            else if (isOptimal) bg = 'rgba(16, 185, 129, 0.12)';

            const discMiniHtml = combo.disc.map(c => {
              const red = c.suit === 'H' || c.suit === 'D';
              const color = red ? '#dc2626 !important' : '#111827 !important';
              return `<span class="trainer-mini-card ${red ? 'card-red' : 'card-black'}" style="display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: 800; font-size: 0.8rem; background: #ffffff !important; color: ${color}; -webkit-text-fill-color: ${color}; border: 1px solid #cbd5e1; box-shadow: 0 1px 3px rgba(0,0,0,0.2); margin-right: 3px;">${c.rankName || c.rank}${suitSymbols[c.suit] || c.suit}</span>`;
            }).join('');

            return `
              <tr style="background: ${bg}; border-bottom: 1px solid rgba(255,255,255,0.06);">
                <td style="padding: 7px 8px; font-weight: 700; color: ${isOptimal ? '#10b981' : (isUserPick ? '#f0c64a' : '#a8a29e')};">#${combo.rank} ${isUserPick ? '👈 YOU' : ''}</td>
                <td style="padding: 7px 8px;">${discMiniHtml}</td>
                <td style="padding: 7px 8px; font-weight: 800; color: #f0c64a; text-align: right;">${combo.netEV.toFixed(2)}</td>
                <td style="padding: 7px 8px; color: #d6d3d1; text-align: right;">${combo.handEV.toFixed(2)} [${combo.handMin}–${combo.handMax}]</td>
                <td style="padding: 7px 8px; color: #a8a29e; text-align: right;">${combo.cribEV.toFixed(2)}</td>
              </tr>
            `;
          }).join('');

          discardResultsHtml = `
            <div style="margin-top: 18px; padding: 14px; background: rgba(0,0,0,0.4); border-radius: 10px; border: 1.5px solid ${badgeColor}; text-align: center;">
              <div style="display: inline-block; padding: 5px 14px; border-radius: 20px; background: ${badgeColor}; color: #000; font-weight: 900; font-size: 0.95rem; margin-bottom: 8px;">
                ${discardEvalResult.gradeLabel}
              </div>
              <div style="font-size: 0.95rem; color: #f4ead4; margin-bottom: 12px;">
                Your EV: <strong style="color: #f0c64a;">${discardEvalResult.userCombo.netEV.toFixed(2)}</strong> · Optimal EV: <strong style="color: #10b981;">${discardEvalResult.optimalCombo.netEV.toFixed(2)}</strong> (Loss: <strong>${discardEvalResult.diff.toFixed(2)}</strong> pts)
              </div>
              <div style="overflow-x: auto; max-height: 220px; border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; margin-bottom: 14px;">
                <table style="width: 100%; border-collapse: collapse; font-size: 0.8rem; text-align: left;">
                  <thead style="background: rgba(255,255,255,0.08); color: #f0c64a; position: sticky; top: 0; z-index: 2;">
                    <tr>
                      <th style="padding: 6px 8px;">Rank</th>
                      <th style="padding: 6px 8px;">Discards</th>
                      <th style="padding: 6px 8px; text-align: right;">Net EV</th>
                      <th style="padding: 6px 8px; text-align: right;">Hand [Min–Max]</th>
                      <th style="padding: 6px 8px; text-align: right;">Crib</th>
                    </tr>
                  </thead>
                  <tbody>${tableRows}</tbody>
                </table>
              </div>
              <div style="display: flex; gap: 8px; justify-content: center; flex-wrap: wrap;">
                ${!todayHistory.pegging ? `
                  <button type="button" id="btnNextToPegging" class="btn-gold" style="padding: 9px 16px; font-weight: 800; font-size: 0.88rem; border-radius: 8px; cursor: pointer;">
                    Next: Daily Pegging Scenario ➔
                  </button>
                ` : ''}
                <button type="button" id="btnShareDiscardScore" class="btn-secondary" style="padding: 9px 14px; font-weight: 800; font-size: 0.88rem; border-radius: 8px; background: rgba(240,198,74,0.2); color: #f0c64a; border: 1px solid #f0c64a; cursor: pointer;">
                  📋 Share Daily Score
                </button>
              </div>
            </div>
          `;
        }

        tabContentHtml = `
          <div style="text-align: center; margin-bottom: 12px;">
            <div style="margin-bottom: 6px;">${roleBadge}</div>
            <p style="margin: 0; font-size: 0.86rem; color: #a8a29e;">
              ${discardEvalResult ? 'Today’s Hand Analyzed against all 46 unseen cut cards:' : 'Select 2 cards to toss to the crib (' + selectedDiscardIndices.size + '/2 selected):'}
            </p>
          </div>
          <div style="display: flex; justify-content: center; gap: 8px; flex-wrap: wrap; margin-bottom: 14px;">
            ${cardsHtml}
          </div>
          ${!discardEvalResult ? `
            <div style="text-align: center;">
              <button type="button" id="btnSubmitDailyDiscard" class="btn-gold" ${selectedDiscardIndices.size !== 2 ? 'disabled' : ''}
                      style="padding: 10px 22px; font-weight: 800; font-size: 0.95rem; border-radius: 8px; cursor: ${selectedDiscardIndices.size === 2 ? 'pointer' : 'not-allowed'}; opacity: ${selectedDiscardIndices.size === 2 ? '1' : '0.5'};">
                🌟 Submit Daily Discard
              </button>
            </div>
          ` : ''}
          ${discardResultsHtml}
        `;
      } else if (activeTab === 'pegging') {
        const pHand = peggingScenario.playerHand;
        const countPills = peggingScenario.countHistory.map(item => `
          <span style="display: inline-flex; align-items: center; gap: 4px; padding: 3px 8px; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.18); border-radius: 6px; font-size: 0.8rem; font-weight: 700; color: #f4ead4;">
            <span>${item.label}</span>
            <span style="font-size: 0.72rem; color: #f0c64a;">(${item.countAfter})</span>
          </span>
        `).join(' ➔ ');

        const peggingCardsHtml = pHand.map(c => {
          const isRed = c.suit === 'H' || c.suit === 'D';
          const sel = selectedPeggingCardId === c.id;
          const textColor = isRed ? '#dc2626 !important' : '#111827 !important';
          const isLocked = !!peggingEvalResult;

          let cardBorder = sel ? '3px solid #eab308 !important' : '2px solid #cbd5e1 !important';
          let cardShadow = sel ? '0 0 0 2px #ca8a04, 0 8px 18px rgba(234,179,8,0.45) !important' : '0 4px 10px rgba(0,0,0,0.35), 0 1px 3px rgba(0,0,0,0.15) !important';
          let cardTransform = sel ? 'translateY(-3px)' : 'none';
          let badgeText = 'PLAY';
          let badgeBg = sel ? '#ca8a04 !important' : 'transparent';
          let badgeColor = sel ? '#ffffff !important' : 'transparent';
          let badgeVisible = sel;

          if (isLocked) {
            const isOptimal = c.id === peggingScenario.optimalCardId;
            const isChosen = c.id === selectedPeggingCardId;
            if (isOptimal) {
              cardBorder = '3px solid #10b981 !important';
              cardShadow = '0 0 0 2px #059669, 0 8px 18px rgba(16,185,129,0.5) !important';
              cardTransform = 'translateY(-3px)';
              badgeText = 'CORRECT ✔';
              badgeBg = '#059669 !important';
              badgeColor = '#ffffff !important';
              badgeVisible = true;
            } else if (isChosen) {
              cardBorder = '3px solid #ef4444 !important';
              cardShadow = '0 0 0 2px #dc2626, 0 8px 18px rgba(239,68,68,0.45) !important';
              cardTransform = 'translateY(-3px)';
              badgeText = 'YOUR PLAY ✕';
              badgeBg = '#dc2626 !important';
              badgeColor = '#ffffff !important';
              badgeVisible = true;
            } else {
              badgeVisible = false;
            }
          }

          return `
            <button type="button" class="daily-pegging-card-btn ${sel ? 'selected' : ''} ${isRed ? 'card-red' : 'card-black'}" data-card-id="${c.id}"
                    style="position: relative; width: 72px; height: 104px; box-sizing: border-box; background: #ffffff !important; border: ${cardBorder}; border-radius: 8px; cursor: ${isLocked ? 'default' : 'pointer'}; display: flex; flex-direction: column; justify-content: space-between; padding: 6px 7px; box-shadow: ${cardShadow}; transform: ${cardTransform}; transition: all 0.15s ease;"
                    title="${c.rankName || c.rank}${c.suit}">
              <div style="display: flex; align-items: baseline; justify-content: flex-start; gap: 2px; line-height: 1;">
                <span class="card-val" style="font-size: 1.2rem; font-weight: 900; color: ${textColor}; -webkit-text-fill-color: ${textColor}; line-height: 1;">${c.rankName || c.rank}</span>
                <span class="card-val" style="font-size: 0.9rem; font-weight: 700; color: ${textColor}; -webkit-text-fill-color: ${textColor}; line-height: 1;">${suitSymbols[c.suit] || c.suit}</span>
              </div>
              <div class="card-val" style="font-size: 2rem; color: ${textColor}; -webkit-text-fill-color: ${textColor}; line-height: 1; text-align: center; margin: 2px 0;">${suitSymbols[c.suit] || c.suit}</div>
              <span class="trainer-discard-badge" style="font-size: 0.58rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.03em; background: ${badgeBg}; color: ${badgeColor}; -webkit-text-fill-color: ${badgeColor}; border-radius: 4px; padding: 2px 0; line-height: 1.1; text-align: center; display: block; width: 100%; box-sizing: border-box; visibility: ${badgeVisible ? 'visible' : 'hidden'};">${badgeText}</span>
            </button>
          `;
        }).join('');

        let peggingOutcomeHtml = '';
        if (peggingEvalResult) {
          const sol = peggingEvalResult.solution;
          const isCorrect = peggingEvalResult.isCorrect;
          const optimalId = peggingScenario.optimalCardId;
          const optimalSol = peggingScenario.solutions[optimalId] || {};
          const optimalCardObj = pHand.find(c => c.id === optimalId) || { id: optimalId, rank: '', suit: '' };
          const userCardObj = pHand.find(c => c.id === selectedPeggingCardId) || { id: selectedPeggingCardId, rank: '', suit: '' };
          const optCardName = `${optimalCardObj.rankName || optimalCardObj.rank}${suitSymbols[optimalCardObj.suit] || optimalCardObj.suit}`;
          const userCardName = `${userCardObj.rankName || userCardObj.rank}${suitSymbols[userCardObj.suit] || userCardObj.suit}`;
          const optIsRed = optimalCardObj.suit === 'H' || optimalCardObj.suit === 'D';

          if (isCorrect) {
            peggingOutcomeHtml = `
              <div style="margin-top: 18px; padding: 14px; background: rgba(0,0,0,0.4); border-radius: 10px; border: 1.5px solid #10b981; text-align: center;">
                <div style="display: inline-block; padding: 5px 14px; border-radius: 20px; background: #10b981; color: #000; font-weight: 900; font-size: 0.95rem; margin-bottom: 8px;">
                  ${sol.badge}
                </div>
                <div style="font-size: 0.9rem; line-height: 1.55; color: #f4ead4; text-align: left; background: rgba(255,255,255,0.05); padding: 12px; border-radius: 8px; margin-bottom: 14px;">
                  ${sol.explanation}
                </div>
                <div style="display: flex; gap: 8px; justify-content: center; flex-wrap: wrap;">
                  <button type="button" id="btnSharePeggingScore" class="btn-secondary" style="padding: 9px 14px; font-weight: 800; font-size: 0.88rem; border-radius: 8px; background: rgba(240,198,74,0.2); color: #f0c64a; border: 1px solid #f0c64a; cursor: pointer;">
                    📋 Share Daily Score
                  </button>
                </div>
              </div>
            `;
          } else {
            peggingOutcomeHtml = `
              <div style="margin-top: 18px; padding: 16px; background: rgba(0,0,0,0.45); border-radius: 10px; border: 1.5px solid #ef4444; text-align: left;">
                <div style="text-align: center; margin-bottom: 14px;">
                  <div style="display: inline-block; padding: 5px 16px; border-radius: 20px; background: #ef4444; color: #ffffff; font-weight: 900; font-size: 0.92rem; letter-spacing: 0.03em;">
                    ❌ INCORRECT PLAY · ${sol.badge}
                  </div>
                </div>

                <!-- Correct Card & Explanation (Prominent) -->
                <div style="background: rgba(16, 185, 129, 0.12); border: 1.5px solid #10b981; border-radius: 8px; padding: 12px 14px; margin-bottom: 12px;">
                  <div style="display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: wrap; margin-bottom: 8px; border-bottom: 1px solid rgba(16, 185, 129, 0.3); padding-bottom: 6px;">
                    <div style="display: flex; align-items: center; gap: 8px;">
                      <span style="font-size: 1.15rem;">✔</span>
                      <span style="font-size: 0.85rem; font-weight: 800; color: #10b981; text-transform: uppercase; letter-spacing: 0.05em;">Correct Card to Play:</span>
                      <span style="display: inline-flex; align-items: center; justify-content: center; background: #ffffff; color: ${optIsRed ? '#dc2626 !important' : '#111827 !important'}; -webkit-text-fill-color: ${optIsRed ? '#dc2626' : '#111827'}; font-weight: 900; font-size: 0.95rem; padding: 2px 8px; border-radius: 4px; box-shadow: 0 1px 3px rgba(0,0,0,0.3);">
                        ${optCardName}
                      </span>
                    </div>
                    ${optimalSol.badge ? `
                      <span style="background: rgba(16, 185, 129, 0.25); color: #6ee7b7; border: 1px solid rgba(16, 185, 129, 0.5); font-size: 0.72rem; font-weight: 800; padding: 2px 8px; border-radius: 10px;">
                        ${optimalSol.badge}
                      </span>
                    ` : ''}
                  </div>
                  <div style="color: #6ee7b7; font-size: 0.78rem; font-weight: 700; text-transform: uppercase; margin-bottom: 4px;">
                    Why ${optCardName} is the correct card:
                  </div>
                  <div style="color: #f4ead4; font-size: 0.88rem; line-height: 1.5;">
                    ${optimalSol.explanation || 'This card maximizes points and secures the winning peg.'}
                  </div>
                </div>

                <!-- Your Played Card Analysis -->
                <div style="background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.35); border-radius: 8px; padding: 12px 14px; margin-bottom: 14px;">
                  <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px;">
                    <span style="font-size: 1.05rem;">✕</span>
                    <span style="font-size: 0.82rem; font-weight: 800; color: #f87171; text-transform: uppercase; letter-spacing: 0.04em;">Why your play (${userCardName}) was suboptimal:</span>
                  </div>
                  <div style="color: #d6d3d1; font-size: 0.85rem; line-height: 1.48;">
                    ${sol.explanation}
                  </div>
                </div>

                <div style="display: flex; gap: 8px; justify-content: center; flex-wrap: wrap;">
                  <button type="button" id="btnSharePeggingScore" class="btn-secondary" style="padding: 9px 14px; font-weight: 800; font-size: 0.88rem; border-radius: 8px; background: rgba(240,198,74,0.2); color: #f0c64a; border: 1px solid #f0c64a; cursor: pointer;">
                    📋 Share Daily Score
                  </button>
                </div>
              </div>
            `;
          }
        }

        tabContentHtml = `
          <div style="padding: 12px 14px; background: rgba(0,0,0,0.3); border-radius: 10px; border: 1px solid rgba(240,198,74,0.3); margin-bottom: 14px;">
            <div style="display: flex; justify-content: space-between; align-items: baseline; flex-wrap: wrap; gap: 6px; margin-bottom: 4px;">
              <h3 style="margin: 0; color: #f0c64a; font-size: 1.05rem;">${peggingScenario.title}</h3>
              <span style="font-size: 0.78rem; font-weight: 700; color: #fbbf24; text-transform: uppercase;">${peggingScenario.subtitle}</span>
            </div>
            <p style="margin: 0 0 10px; font-size: 0.88rem; color: #d6d3d1; line-height: 1.45;">
              ${peggingScenario.description}
            </p>
            <div style="display: flex; justify-content: space-between; background: rgba(0,0,0,0.4); padding: 8px 12px; border-radius: 8px; font-size: 0.84rem; font-weight: 700; color: #f4ead4; flex-wrap: wrap; gap: 8px;">
              <div>You: <strong style="color: #60a5fa;">${peggingScenario.playerScore}</strong> (${peggingScenario.isDealer ? 'Dealer' : 'Pone'})</div>
              <div>Opponent: <strong style="color: #f87171;">${peggingScenario.opponentScore}</strong></div>
              <div>Count: <strong style="color: #f0c64a; font-size: 1rem;">${peggingScenario.currentCount}</strong> / 31</div>
            </div>
            ${peggingScenario.countHistory.length > 0 ? `
              <div style="margin-top: 8px; font-size: 0.8rem; color: #a8a29e;">
                Sequence: ${countPills}
              </div>
            ` : ''}
          </div>

          <div style="text-align: center; margin-bottom: 8px; font-size: 0.85rem; font-weight: 600; color: #d6d3d1;">
            ${peggingEvalResult ? 'Your Play &amp; Tactical Outcome:' : 'Pick the winning card from your remaining hand:'}
          </div>
          <div style="display: flex; justify-content: center; gap: 8px; flex-wrap: wrap; margin-bottom: 14px;">
            ${peggingCardsHtml}
          </div>
          ${!peggingEvalResult ? `
            <div style="text-align: center;">
              <button type="button" id="btnSubmitDailyPegging" class="btn-gold" ${!selectedPeggingCardId ? 'disabled' : ''}
                      style="padding: 10px 22px; font-weight: 800; font-size: 0.95rem; border-radius: 8px; cursor: ${selectedPeggingCardId ? 'pointer' : 'not-allowed'}; opacity: ${selectedPeggingCardId ? '1' : '0.5'};">
                ⚡ Play Selected Card
              </button>
            </div>
          ` : ''}
          ${peggingOutcomeHtml}
        `;
      } else {
        const shareTextPreview = DailyPuzzleManager.generateShareText(dateKey);
        const perfectPct = stats.totalPlayed > 0 ? Math.round(((stats.perfectDiscards || 0) / stats.totalPlayed) * 100) : 0;
        const peggingPct = stats.totalPlayed > 0 ? Math.round(((stats.peggingWins || 0) / stats.totalPlayed) * 100) : 0;

        tabContentHtml = `
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(110px, 1fr)); gap: 10px; margin-bottom: 18px;">
            <div style="padding: 12px 8px; background: rgba(0,0,0,0.35); border-radius: 10px; border: 1px solid rgba(240,198,74,0.3); text-align: center;">
              <div style="font-size: 1.6rem; line-height: 1;">🔥</div>
              <div style="font-size: 1.4rem; font-weight: 900; color: #f0c64a; margin: 4px 0;">${stats.currentStreak || 0}</div>
              <div style="font-size: 0.72rem; color: #a8a29e; font-weight: 700; text-transform: uppercase;">Current Streak</div>
            </div>
            <div style="padding: 12px 8px; background: rgba(0,0,0,0.35); border-radius: 10px; border: 1px solid rgba(240,198,74,0.3); text-align: center;">
              <div style="font-size: 1.6rem; line-height: 1;">🏆</div>
              <div style="font-size: 1.4rem; font-weight: 900; color: #fbbf24; margin: 4px 0;">${stats.bestStreak || 0}</div>
              <div style="font-size: 0.72rem; color: #a8a29e; font-weight: 700; text-transform: uppercase;">Best Streak</div>
            </div>
            <div style="padding: 12px 8px; background: rgba(0,0,0,0.35); border-radius: 10px; border: 1px solid rgba(240,198,74,0.3); text-align: center;">
              <div style="font-size: 1.6rem; line-height: 1;">📅</div>
              <div style="font-size: 1.4rem; font-weight: 900; color: #60a5fa; margin: 4px 0;">${stats.totalPlayed || 0}</div>
              <div style="font-size: 0.72rem; color: #a8a29e; font-weight: 700; text-transform: uppercase;">Days Played</div>
            </div>
            <div style="padding: 12px 8px; background: rgba(0,0,0,0.35); border-radius: 10px; border: 1px solid rgba(240,198,74,0.3); text-align: center;">
              <div style="font-size: 1.6rem; line-height: 1;">🌟</div>
              <div style="font-size: 1.4rem; font-weight: 900; color: #10b981; margin: 4px 0;">${perfectPct}%</div>
              <div style="font-size: 0.72rem; color: #a8a29e; font-weight: 700; text-transform: uppercase;">Optimal Discard</div>
            </div>
            <div style="padding: 12px 8px; background: rgba(0,0,0,0.35); border-radius: 10px; border: 1px solid rgba(240,198,74,0.3); text-align: center;">
              <div style="font-size: 1.6rem; line-height: 1;">⚡</div>
              <div style="font-size: 1.4rem; font-weight: 900; color: #f59e0b; margin: 4px 0;">${peggingPct}%</div>
              <div style="font-size: 0.72rem; color: #a8a29e; font-weight: 700; text-transform: uppercase;">Pegging Win Rate</div>
            </div>
          </div>

          <div style="background: rgba(0,0,0,0.45); border-radius: 10px; border: 1px solid rgba(255,255,255,0.12); padding: 14px; margin-bottom: 16px;">
            <div style="font-size: 0.8rem; font-weight: 700; color: #f0c64a; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.05em;">
              Wordle-Style Share Card Preview
            </div>
            <pre style="margin: 0; font-family: monospace; font-size: 0.88rem; color: #f4ead4; background: rgba(0,0,0,0.3); padding: 10px; border-radius: 6px; white-space: pre-wrap; line-height: 1.45;">${shareTextPreview}</pre>
          </div>

          <div style="text-align: center; display: flex; flex-direction: column; align-items: center; gap: 8px;">
            <button type="button" id="btnCopyDailyShareText" class="btn-gold" style="padding: 11px 22px; font-weight: 800; font-size: 0.95rem; border-radius: 8px; cursor: pointer;">
              📋 Copy Share Card to Clipboard
            </button>
            ${copyToastMessage ? `<div style="margin-top: 4px; color: ${copyToastMessage.includes('reset') ? '#f87171' : '#22c55e'}; font-weight: 700; font-size: 0.88rem;">${copyToastMessage}</div>` : ''}
            <button type="button" id="btnResetDailyStats" class="btn-ghost" style="margin-top: 10px; padding: 7px 15px; font-size: 0.8rem; font-weight: 700; color: #ef4444; border: 1px solid rgba(239,68,68,0.35); border-radius: 6px; background: rgba(239,68,68,0.08); cursor: pointer; transition: all 0.15s ease;" title="Reset daily streaks and completion history">
              🗑️ Reset Streaks &amp; Stats
            </button>
          </div>
        `;
      }

      const modalHtml = `
        <div class="daily-puzzle-modal" style="max-width: 680px; width: 100%; box-sizing: border-box; text-align: left; padding: 4px 2px;">
          <!-- Header -->
          <div style="display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 1.5px solid rgba(240, 198, 74, 0.4); padding-bottom: 12px; margin-bottom: 14px;">
            <div>
              <div style="display: flex; align-items: center; gap: 8px;">
                <span style="font-size: 1.4rem;">📅</span>
                <h2 style="margin: 0; font-size: 1.35rem; color: #f0c64a; font-weight: 800; letter-spacing: 0.03em;">DAILY PUZZLE</h2>
                <span style="background: rgba(240,198,74,0.2); color: #f0c64a; border: 1px solid rgba(240,198,74,0.4); font-size: 0.75rem; font-weight: 800; padding: 2px 7px; border-radius: 12px;">DAY #${dayNumber}</span>
              </div>
              <p style="margin: 4px 0 0; font-size: 0.82rem; color: #a8a29e;">${dateFormatted} · Fresh puzzle daily at midnight</p>
            </div>
            <button type="button" class="btn-ghost modal-btn-close" title="Close Daily Puzzle" style="padding: 6px 12px; font-size: 0.85rem; border-radius: 6px; background: rgba(255,255,255,0.08); color: #d6d3d1; border: 1px solid rgba(255,255,255,0.2); cursor: pointer;">
              ✕ Close
            </button>
          </div>

          <!-- Tabs -->
          ${tabsHtml}

          <!-- Content -->
          <div class="daily-tab-content">
            ${tabContentHtml}
          </div>
        </div>
      `;

      this.showModal(modalHtml, true);
      bindEvents();
    };

    const bindEvents = () => {
      const tabDiscard = document.getElementById('tabBtnDiscard');
      const tabPegging = document.getElementById('tabBtnPegging');
      const tabStats = document.getElementById('tabBtnStats');

      if (tabDiscard) tabDiscard.onclick = () => { activeTab = 'discard'; copyToastMessage = ''; render(); };
      if (tabPegging) tabPegging.onclick = () => { activeTab = 'pegging'; copyToastMessage = ''; render(); };
      if (tabStats) tabStats.onclick = () => { activeTab = 'stats'; copyToastMessage = ''; render(); };

      if (activeTab === 'discard') {
        const cardBtns = document.querySelectorAll('.trainer-card-btn');
        cardBtns.forEach(btn => {
          btn.onclick = () => {
            if (discardEvalResult) return;
            const idx = parseInt(btn.dataset.idx, 10);
            if (selectedDiscardIndices.has(idx)) {
              selectedDiscardIndices.delete(idx);
            } else {
              if (selectedDiscardIndices.size >= 2) {
                const first = selectedDiscardIndices.values().next().value;
                selectedDiscardIndices.delete(first);
              }
              selectedDiscardIndices.add(idx);
            }
            render();
          };
        });

        const btnSubmitDiscard = document.getElementById('btnSubmitDailyDiscard');
        if (btnSubmitDiscard) {
          btnSubmitDiscard.onclick = () => {
            if (selectedDiscardIndices.size !== 2) return;
            const chosenCards = Array.from(selectedDiscardIndices).map(idx => dailyHand[idx].id);
            discardEvalResult = DailyPuzzleManager.evaluateDailyDiscard(chosenCards, dateKey);
            const sfx = (typeof window !== 'undefined' ? window.sfx : null);
            DailyPuzzleManager.recordCompletion('discard', discardEvalResult, dateKey, { sfx });
            render();
          };
        }

        const btnNextToPegging = document.getElementById('btnNextToPegging');
        if (btnNextToPegging) {
          btnNextToPegging.onclick = () => {
            activeTab = 'pegging';
            render();
          };
        }

        const btnShareDiscard = document.getElementById('btnShareDiscardScore');
        if (btnShareDiscard) {
          btnShareDiscard.onclick = () => {
            const text = DailyPuzzleManager.generateShareText(dateKey);
            if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
              navigator.clipboard.writeText(text).then(() => {
                alert('Daily Score copied to clipboard! 📋\n\n' + text);
              }).catch(() => {
                prompt('Copy your daily score:', text);
              });
            } else {
              prompt('Copy your daily score:', text);
            }
          };
        }
      }

      if (activeTab === 'pegging') {
        const peggingBtns = document.querySelectorAll('.daily-pegging-card-btn');
        peggingBtns.forEach(btn => {
          btn.onclick = () => {
            if (peggingEvalResult) return;
            selectedPeggingCardId = btn.dataset.cardId;
            render();
          };
        });

        const btnSubmitPegging = document.getElementById('btnSubmitDailyPegging');
        if (btnSubmitPegging) {
          btnSubmitPegging.onclick = () => {
            if (!selectedPeggingCardId) return;
            peggingEvalResult = DailyPuzzleManager.evaluateDailyPegging(selectedPeggingCardId, dateKey);
            const sfx = (typeof window !== 'undefined' ? window.sfx : null);
            DailyPuzzleManager.recordCompletion('pegging', peggingEvalResult, dateKey, { sfx });
            render();
          };
        }

        const btnSharePegging = document.getElementById('btnSharePeggingScore');
        if (btnSharePegging) {
          btnSharePegging.onclick = () => {
            const text = DailyPuzzleManager.generateShareText(dateKey);
            if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
              navigator.clipboard.writeText(text).then(() => {
                alert('Daily Score copied to clipboard! 📋\n\n' + text);
              }).catch(() => {
                prompt('Copy your daily score:', text);
              });
            } else {
              prompt('Copy your daily score:', text);
            }
          };
        }
      }

      if (activeTab === 'stats') {
        const btnCopyShare = document.getElementById('btnCopyDailyShareText');
        if (btnCopyShare) {
          btnCopyShare.onclick = () => {
            const text = DailyPuzzleManager.generateShareText(dateKey);
            if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
              navigator.clipboard.writeText(text).then(() => {
                copyToastMessage = 'Copied to clipboard! 📋 Share with friends!';
                render();
              }).catch(() => {
                prompt('Copy your daily share card:', text);
              });
            } else {
              prompt('Copy your daily share card:', text);
            }
          };
        }

        const btnResetStats = document.getElementById('btnResetDailyStats');
        if (btnResetStats) {
          btnResetStats.onclick = () => {
            const doReset = () => {
              DailyPuzzleManager.resetStats();
              stats = DailyPuzzleManager.loadStats();
              todayHistory = {};
              discardEvalResult = null;
              peggingEvalResult = null;
              selectedDiscardIndices.clear();
              selectedPeggingCardId = null;
              copyToastMessage = 'Daily streaks and statistics have been reset.';
              render();
            };

            const showConfirmFn = (typeof window !== 'undefined' && typeof window.showConfirmModal === 'function')
              ? window.showConfirmModal
              : (context && typeof context.showConfirmModal === 'function' ? context.showConfirmModal : null);

            if (typeof showConfirmFn === 'function') {
              showConfirmFn({
                title: 'Reset Daily Streaks & Stats?',
                message: 'Are you sure you want to reset your daily streaks, win counts, and puzzle history? This action cannot be undone.',
                confirmText: 'Reset Stats',
                cancelText: 'Cancel',
                danger: true,
                onConfirm: doReset
              });
            } else if (confirm('Are you sure you want to reset your daily streaks and statistics? This cannot be undone.')) {
              doReset();
            }
          };
        }
      }
    };

    render();
  }

  static showDailyPuzzleModal(context = {}) {
    if (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.showDailyPuzzleModal === 'function') {
      return window.modalManager.showDailyPuzzleModal(context);
    }
    const mm = new ModalManager();
    return mm.showDailyPuzzleModal(context);
  }

  static triggerNativeShare(shareData = {}) {
    if (typeof navigator !== 'undefined' && navigator.share) {
      const data = {
        title: shareData.title || 'Crazy Cribbage',
        text: shareData.text || 'Play Crazy Cribbage — the wild twist on classic cribbage! 50+ point hands, jokers, and custom boards!',
        url: shareData.url || (typeof window !== 'undefined' && window.location ? window.location.href.split('#')[0] : '')
      };
      navigator.share(data).catch(() => {});
    }
  }

  triggerNativeShare(shareData) {
    return ModalManager.triggerNativeShare(shareData);
  }

  /**
   * Displays the brand-specific intro/rules modal.
   */
  static showIntro(brand = null, options = {}) {
    if (typeof document === 'undefined' && !options.showModal) return;
    const resolvedBrand = brand || (typeof window !== 'undefined' && window.getAppBrand ? window.getAppBrand() : (typeof window !== 'undefined' && (window.currentBrand || window.currentLogoBrand) ? (window.currentBrand || window.currentLogoBrand) : 'crazy'));
    const isClash = resolvedBrand === 'clash';
    const isAdult = resolvedBrand === 'adult';
    const isPints = resolvedBrand === 'pints';
    let copyEl;
    if (typeof document !== 'undefined') {
      if (isAdult) {
        copyEl = document.getElementById("introCopyAdult") || document.getElementById("introCopy");
      } else if (isPints) {
        copyEl = document.getElementById("introCopyPints") || document.getElementById("introCopy");
      } else if (isClash) {
        copyEl = document.getElementById("introCopyClash") || document.getElementById("introCopy");
      } else {
        copyEl = document.getElementById("introCopy");
      }
    }
    const html = copyEl ? copyEl.innerHTML : (options.content || '<p>Intro</p>');
    const showModalFn = options.showModal || (typeof window !== 'undefined' && window.showModal ? window.showModal : null);
    if (typeof showModalFn === 'function') {
      showModalFn(html, true);
    }
  }

  showIntro(brand, options) {
    return ModalManager.showIntro(brand, { ...options, showModal: this.showModal.bind(this) });
  }

  /**
   * Binds close buttons, escape key, and header menu shortcuts.
   */
  static setupCloseButtonEvents(handlers = {}) {
    if (typeof document === 'undefined') return;
    const hideModalFn = handlers.hideModal || (typeof window !== 'undefined' && window.hideModal ? window.hideModal : null);
    const showIntroFn = handlers.showIntro || (typeof window !== 'undefined' && window.showIntro ? window.showIntro : null);
    const showRecordsModalFn = handlers.showRecordsModal || (typeof window !== 'undefined' && window.showRecordsModal ? window.showRecordsModal : null);
    const toggleMenuSettingsFn = handlers.toggleMenuSettings || (typeof window !== 'undefined' && window.toggleMenuSettings ? window.toggleMenuSettings : null);
    const showShareModalFn = handlers.showShareModal || (typeof window !== 'undefined' && window.showShareModal ? window.showShareModal : null);

    const modalBar = document.getElementById('modalBar');
    const modalClose = document.getElementById('modalClose');
    if (modalBar && hideModalFn) {
      modalBar.onclick = (e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        hideModalFn();
      };
    }
    if (modalClose && hideModalFn) {
      modalClose.onclick = (e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        hideModalFn();
      };
    }

    document.addEventListener('click', (e) => {
      const closeBtn = e.target && e.target.closest && e.target.closest('button[onclick*="hideModal"], button[onclick*="closeModal"], .modal-btn-close, [data-modal-close]');
      if (closeBtn && hideModalFn) {
        if (e && e.stopPropagation) e.stopPropagation();
        hideModalFn();
      }
    });
  }

  /**
   * Displays the Age Gate modal for age-restricted game brands (pints / adult).
   * @param {string} [brand]
   * @param {Object} [context]
   */
  static showAgeGateModal(brand, context = {}) {
    if (typeof document === 'undefined') return;
    const getAppBrand = context.getAppBrand || (typeof window !== 'undefined' ? window.getAppBrand : () => 'pints');
    brand = brand || getAppBrand();
    const overlay = document.getElementById('ageGateOverlay');
    const modal = document.getElementById('ageGateModal');
    if (!overlay || !modal) return;

    const isPints = brand === 'pints';
    const isAdult = brand === 'adult';

    if (!isPints && !isAdult) {
      overlay.style.display = 'none';
      return;
    }

    modal.classList.remove('age-gate-pints', 'age-gate-adult');
    modal.classList.add(isPints ? 'age-gate-pints' : 'age-gate-adult');

    const iconEl = document.getElementById('ageGateIcon');
    const brandNameEl = document.getElementById('ageGateBrandName');
    const msgEl = document.getElementById('ageGateMessage');
    const confirmBtn = document.getElementById('btnAgeGateConfirm');

    if (iconEl) iconEl.textContent = isPints ? '🍻' : '🔞';
    if (brandNameEl) brandNameEl.textContent = isPints ? 'Pints and Pegs' : 'Skunked and Stripped';
    if (msgEl) {
      msgEl.textContent = isPints
        ? 'This version contains drinking themes and pub games intended solely for individuals of legal drinking age. You must be at least 21 years old to enter and play.'
        : 'This version contains mature adult themes and erotic challenges intended solely for adults. You must be at least 21 years old to enter and play.';
    }
    if (confirmBtn) {
      confirmBtn.textContent = '✔ I Am 21 or Older — Enter';
    }

    overlay.style.display = 'flex';
  }

  /**
   * Hides the Age Gate overlay.
   */
  static hideAgeGateModal() {
    if (typeof document === 'undefined') return;
    const overlay = document.getElementById('ageGateOverlay');
    if (overlay) overlay.style.display = 'none';
  }

  /**
   * Confirms user is 21+, stores verification flag and dismisses modal.
   * @param {Object} [context]
   */
  static confirmAgeGate(context = {}) {
    const getAppBrand = context.getAppBrand || (typeof window !== 'undefined' ? window.getAppBrand : () => 'pints');
    const setAgeVerified = context.setAgeVerified || (typeof window !== 'undefined' ? window.setAgeVerified : () => {});
    const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : null);
    const brand = getAppBrand();
    setAgeVerified(brand, true);
    if (typeof window !== 'undefined' && window.appStorage) {
      try {
        window.appStorage.setItem('cc_age_verified_' + brand, 'true');
      } catch (e) {}
    }
    ModalManager.hideAgeGateModal();
    if (sfx && sfx.snap) sfx.snap();
  }

  /**
   * Safely navigates away to the default Crazy Cribbage brand if age verification declined.
   */
  static exitAgeGateToSafeBrand() {
    if (typeof window === 'undefined') return;
    ModalManager.hideAgeGateModal();
    if (typeof window.setLogoBrand === 'function') {
      try {
        window.setLogoBrand('crazy');
      } catch (e) {}
    }
    const path = (window.location && window.location.pathname) || '';
    if (path.includes('pints') || path.includes('stripped') || path.includes('adult')) {
      window.location.href = '/pages/game-crazy-cribbage';
    } else if (window.location && window.location.search && (window.location.search.includes('pints') || window.location.search.includes('adult') || window.location.search.includes('stripped'))) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('brand', 'crazy');
        window.location.href = url.toString();
      } catch (e) {
        window.location.href = '?brand=crazy';
      }
    }
  }

  showAgeGateModal(brand, context) {
    return ModalManager.showAgeGateModal(brand, context);
  }

  hideAgeGateModal() {
    return ModalManager.hideAgeGateModal();
  }

  confirmAgeGate(context) {
    return ModalManager.confirmAgeGate(context);
  }

  exitAgeGateToSafeBrand() {
    return ModalManager.exitAgeGateToSafeBrand();
  }

  /**
   * Shows the Adult Mode timer challenge modal.
   * @param {Object} optionDef
   * @param {string} playerLabel
   * @param {number} holeNum
   * @param {Object} [context]
   */
  static showAdultTimerModal(optionDef, playerLabel, holeNum, context = {}) {
    if (typeof document === 'undefined') return;
    const overlay = document.getElementById('adultTimerOverlay');
    if (!overlay) return;

    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : null);

    if (state) {
      state.pausedForAdultTimer = true;
      state.paused = true;
      log(`⏱️ ADULT CHALLENGE: ${playerLabel || 'Player'} landed on Hole ${holeNum} (${optionDef.name})! Game is paused until timer challenge is closed.`);
      setStatus(`Landed on Hole ${holeNum} (${optionDef.name})! Game is paused.`);
    }

    const times = [15, 30, 45, 60, 90, 120];
    const chosenTime = times[Math.floor(Math.random() * times.length)];
    ModalManager.adultTimerTotal = chosenTime;
    ModalManager.adultTimerRemaining = chosenTime;

    if (ModalManager.adultTimerInterval) {
      clearInterval(ModalManager.adultTimerInterval);
      ModalManager.adultTimerInterval = null;
    }

    const titleEl = document.getElementById('adultTimerTitle');
    const descEl = document.getElementById('adultTimerDesc');
    const playerEl = document.getElementById('adultTimerPlayer');
    const pickBadgeEl = document.getElementById('adultTimerPickBadge');
    const secEl = document.getElementById('adultTimerSeconds');
    const bannerEl = document.getElementById('adultTimerBanner');
    const startBtn = document.getElementById('btnAdultTimerStart');
    const closeBtn = document.getElementById('btnAdultTimerClose');
    const circle = document.getElementById('adultDialProgress');

    if (titleEl) titleEl.textContent = `${optionDef.icon || '🔥'} ${optionDef.name}`;
    if (descEl) descEl.textContent = `Action: ${optionDef.actionText || optionDef.name}`;
    if (playerEl) playerEl.textContent = `${playerLabel || 'Player'} landed on Hole ${holeNum}!`;
    if (pickBadgeEl) pickBadgeEl.textContent = `⏱️ ${chosenTime} Seconds Challenge`;
    if (secEl) secEl.textContent = String(chosenTime);
    if (bannerEl) bannerEl.style.display = 'none';
    if (closeBtn) closeBtn.textContent = 'Close';
    if (startBtn) {
      startBtn.disabled = false;
      startBtn.textContent = '▶ Start Timer';
      startBtn.style.display = 'inline-flex';
    }
    if (circle) {
      const circum = 2 * Math.PI * 76;
      circle.style.strokeDasharray = `${circum}`;
      circle.style.strokeDashoffset = '0';
    }

    overlay.style.display = 'flex';
    if (sfx && sfx.snap) sfx.snap();
  }

  /**
   * Starts the countdown timer for the Adult Challenge modal.
   * @param {Object} [context]
   */
  static startAdultTimer(context = {}) {
    if (typeof document === 'undefined') return;
    const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : null);
    if (sfx && sfx.ensure) {
      try { sfx.ensure(); } catch (e) {}
    }
    const startBtn = document.getElementById('btnAdultTimerStart');
    if (startBtn) {
      startBtn.disabled = true;
      startBtn.textContent = '⏳ Running...';
    }
    const secEl = document.getElementById('adultTimerSeconds');
    const bannerEl = document.getElementById('adultTimerBanner');
    const circle = document.getElementById('adultDialProgress');
    const circum = 2 * Math.PI * 76;

    if (ModalManager.adultTimerInterval) clearInterval(ModalManager.adultTimerInterval);
    ModalManager.adultTimerInterval = setInterval(() => {
      ModalManager.adultTimerRemaining--;
      if (secEl) secEl.textContent = String(Math.max(0, ModalManager.adultTimerRemaining));
      if (circle && ModalManager.adultTimerTotal > 0) {
        const progress = 1 - (ModalManager.adultTimerRemaining / ModalManager.adultTimerTotal);
        circle.style.strokeDashoffset = `${progress * circum}`;
      }
      if (ModalManager.adultTimerRemaining > 0 && sfx && sfx.tone) {
        sfx.tone(440, 0.04, 'triangle', 0.03);
      }
      if (ModalManager.adultTimerRemaining <= 0) {
        clearInterval(ModalManager.adultTimerInterval);
        ModalManager.adultTimerInterval = null;
        if (secEl) secEl.textContent = '0';
        if (circle) circle.style.strokeDashoffset = `${circum}`;
        if (bannerEl) bannerEl.style.display = 'block';
        if (startBtn) startBtn.style.display = 'none';
        const closeBtn = document.getElementById('btnAdultTimerClose');
        if (closeBtn) closeBtn.textContent = 'Dismiss';
        if (sfx && typeof sfx.alarm === 'function') {
          sfx.alarm(true);
        }
      }
    }, 1000);
  }

  /**
   * Closes the Adult Challenge modal and resumes gameplay.
   * @param {Object} [context]
   */
  static closeAdultTimerModal(context = {}) {
    if (ModalManager.adultTimerInterval) {
      clearInterval(ModalManager.adultTimerInterval);
      ModalManager.adultTimerInterval = null;
    }
    if (typeof document !== 'undefined') {
      const overlay = document.getElementById('adultTimerOverlay');
      if (overlay) overlay.style.display = 'none';
    }
    const resumeFn = context.resumeGameAfterAdultTimer || (typeof window !== 'undefined' ? window.resumeGameAfterAdultTimer : ModalManager.resumeGameAfterAdultTimer);
    resumeFn(context);
  }

  /**
   * Displays the Adult Mode strip challenge modal.
   * @param {Object} optionDef
   * @param {string} playerLabel
   * @param {number} holeNum
   * @param {Object} [context]
   */
  static showAdultStripModal(optionDef, playerLabel, holeNum, context = {}) {
    if (typeof document === 'undefined') return;
    const overlay = document.getElementById('adultBonusOverlay');
    if (!overlay) return;

    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : null);

    if (state) {
      state.pausedForAdultStrip = true;
      state.paused = true;
      log(`👙 STRIP BONUS: ${playerLabel || 'Player'} landed on Hole ${holeNum} (${optionDef.name}) — ${optionDef.actionText}! Game is paused.`);
      setStatus(`Landed on Hole ${holeNum} (${optionDef.name})! Game is paused.`);
    }

    const titleEl = document.getElementById('adultBonusTitle');
    const descEl = document.getElementById('adultBonusDesc');
    const playerEl = document.getElementById('adultBonusPlayer');
    const iconEl = document.getElementById('adultBonusIcon');
    const msgEl = document.getElementById('adultBonusMessage');

    if (iconEl) iconEl.textContent = optionDef.icon || '👙';
    if (titleEl) titleEl.textContent = `${optionDef.icon ? optionDef.icon + ' ' : ''}${optionDef.name}`;
    if (playerEl) playerEl.textContent = `${playerLabel || 'Player'} landed on Hole ${holeNum}!`;
    if (msgEl) msgEl.textContent = optionDef.actionText || optionDef.name;
    if (descEl) descEl.textContent = `Action: ${optionDef.actionText || optionDef.name}`;

    overlay.style.display = 'flex';
    if (sfx && sfx.snap) sfx.snap();
  }

  /**
   * Closes the Adult Mode strip challenge modal and resumes gameplay.
   * @param {Object} [context]
   */
  static closeAdultStripModal(context = {}) {
    if (typeof document !== 'undefined') {
      const overlay = document.getElementById('adultBonusOverlay');
      if (overlay) overlay.style.display = 'none';
    }
    const resumeFn = context.resumeGameAfterAdultStrip || (typeof window !== 'undefined' ? window.resumeGameAfterAdultStrip : ModalManager.resumeGameAfterAdultStrip);
    resumeFn(context);
  }

  /**
   * Resumes game loop after strip modal dismissal.
   * @param {Object} [context]
   */
  static resumeGameAfterAdultStrip(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;
    state.pausedForAdultStrip = false;
    if (!state.pausedForSign && !state.pausedForAdultTimer && !state.pausedForPintsBonus) {
      state.paused = false;
    }
    const isSeatCpu = context.isSeatCpu || (typeof window !== 'undefined' ? window.isSeatCpu : () => false);
    const scheduleOppPlay = context.scheduleOppPlay || (typeof window !== 'undefined' ? window.scheduleOppPlay : () => {});
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});

    if (typeof state._resumeAfterAdultTimer === 'function' && !state.pausedForAdultTimer && !state.pausedForPintsBonus) {
      const resumeFn = state._resumeAfterAdultTimer;
      state._resumeAfterAdultTimer = null;
      state._resumeAfterSign = null;
      state._resumeAfterPintsBonus = null;
      resumeFn();
    } else if (typeof state._resumeAfterSign === 'function' && !state.pausedForSign && !state.pausedForAdultTimer && !state.pausedForPintsBonus) {
      const resumeFn = state._resumeAfterSign;
      state._resumeAfterAdultTimer = null;
      state._resumeAfterSign = null;
      state._resumeAfterPintsBonus = null;
      resumeFn();
    } else if (state.phase === 'play' && isSeatCpu(state.turn) && !state.paused) {
      scheduleOppPlay(state.turn);
    } else if (state.phase === 'play' && !state.paused) {
      render();
    }
  }

  /**
   * Resumes game loop after timer challenge dismissal.
   * @param {Object} [context]
   */
  static resumeGameAfterAdultTimer(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;
    state.pausedForAdultTimer = false;
    if (!state.pausedForSign && !state.pausedForAdultStrip && !state.pausedForPintsBonus) {
      state.paused = false;
    }
    const isSeatCpu = context.isSeatCpu || (typeof window !== 'undefined' ? window.isSeatCpu : () => false);
    const scheduleOppPlay = context.scheduleOppPlay || (typeof window !== 'undefined' ? window.scheduleOppPlay : () => {});
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});

    if (typeof state._resumeAfterAdultTimer === 'function' && !state.pausedForAdultStrip && !state.pausedForPintsBonus) {
      const resumeFn = state._resumeAfterAdultTimer;
      state._resumeAfterAdultTimer = null;
      state._resumeAfterSign = null;
      state._resumeAfterPintsBonus = null;
      resumeFn();
    } else if (typeof state._resumeAfterSign === 'function' && !state.pausedForSign && !state.pausedForAdultStrip && !state.pausedForPintsBonus) {
      const resumeFn = state._resumeAfterSign;
      state._resumeAfterAdultTimer = null;
      state._resumeAfterSign = null;
      state._resumeAfterPintsBonus = null;
      resumeFn();
    } else if (state.phase === 'play' && isSeatCpu(state.turn) && !state.paused) {
      scheduleOppPlay(state.turn);
    } else if (state.phase === 'play' && !state.paused) {
      render();
    }
  }

  showAdultTimerModal(optionDef, playerLabel, holeNum, context) {
    return ModalManager.showAdultTimerModal(optionDef, playerLabel, holeNum, context);
  }

  startAdultTimer(context) {
    return ModalManager.startAdultTimer(context);
  }

  closeAdultTimerModal(context) {
    return ModalManager.closeAdultTimerModal(context);
  }

  showAdultStripModal(optionDef, playerLabel, holeNum, context) {
    return ModalManager.showAdultStripModal(optionDef, playerLabel, holeNum, context);
  }

  closeAdultStripModal(context) {
    return ModalManager.closeAdultStripModal(context);
  }

  resumeGameAfterAdultStrip(context) {
    return ModalManager.resumeGameAfterAdultStrip(context);
  }

  resumeGameAfterAdultTimer(context) {
    return ModalManager.resumeGameAfterAdultTimer(context);
  }

  /**
   * Prompts device handoff for local pass-and-play matches.
   * @param {string} who - Target seat ID
   * @param {Function} thenFn - Callback when recipient confirms ready
   * @param {Object} [context]
   */
  static passTo(who, thenFn, context = {}) {
    if (who === 'you') who = 'p1';
    if (who === 'cpu') who = 'p2';
    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const isSeatCpu = context.isSeatCpu || (typeof window !== 'undefined' ? window.isSeatCpu : () => false);
    if (isOnline() || isSeatCpu(who)) { if (thenFn) thenFn(); return; }

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    const pCount = settings ? (parseInt(settings.playerCount, 10) || 2) : 2;

    // Check how many active seats are human
    const activeSeats = (typeof context.getActiveSeatIds === 'function')
      ? context.getActiveSeatIds()
      : (Array.isArray(settings?.seats) ? settings.seats.slice(0, pCount).map(s => s.id) : ['p1', 'p2', 'p3', 'p4'].slice(0, pCount));

    const isSeatHuman = context.isSeatHuman || (typeof window !== 'undefined' ? window.isSeatHuman : null);
    const humanSeats = activeSeats.filter(sId => {
      if (typeof isSeatHuman === 'function') return isSeatHuman(sId);
      if (typeof isSeatCpu === 'function' && isSeatCpu(sId)) return false;
      if (Array.isArray(settings?.seats)) {
        const s = settings.seats.find(seat => seat.id === sId);
        if (s && s.type === 'cpu') return false;
      } else if (settings?.seats && typeof settings.seats === 'object') {
        const s = settings.seats[sId];
        if (s && s.type === 'cpu') return false;
      }
      return true;
    });

    // If there is only 1 human player in the game (playing with/against CPUs), never prompt to pass the device!
    if (humanSeats.length <= 1) {
      if (thenFn) thenFn();
      return;
    }

    const getAppBrand = context.getAppBrand || (typeof window !== 'undefined' ? window.getAppBrand : () => 'crazy');
    const appBrand = typeof getAppBrand === 'function' ? getAppBrand() : 'crazy';
    const GameOptionsEngine = (typeof window !== 'undefined' && window.GameOptionsEngine) ? window.GameOptionsEngine : null;
    const allowCpu = GameOptionsEngine && typeof GameOptionsEngine.isCpuAllowed === 'function'
      ? GameOptionsEngine.isCpuAllowed(appBrand)
      : !['pints', 'adult'].includes(appBrand);
    const isHvH = context.isHvH || (typeof window !== 'undefined' ? window.isHvH : () => false);

    if (settings && pCount === 2 && !isHvH() && allowCpu) { if (thenFn) thenFn(); return; }

    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    if (state) {
      state.passingTo = who;
      render();
    }

    const showModal = context.showModal || (typeof window !== 'undefined' && typeof window.showModal === 'function' ? window.showModal : () => {});
    const hideModal = context.hideModal || (typeof window !== 'undefined' && typeof window.hideModal === 'function' ? window.hideModal : () => {});
    const seatColor = context.seatColor || (typeof window !== 'undefined' ? window.seatColor : () => '#d4a017');
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (s) => s);

    showModal(
      `<h2>Pass This Device</h2>
       <p>Pass the device to <strong style="color:${seatColor(who)}">${seatName(who)}</strong>. Other players please look away.</p>
       <button class="btn-gold" id="btnSeat">Ready — ${seatName(who)}</button>`
    );
    if (typeof document !== 'undefined') {
      const b = document.getElementById('btnSeat');
      if (b) b.onclick = () => {
        if (state) state.passingTo = null;
        hideModal();
        if (thenFn) thenFn();
      };
    }
  }

  /**
   * Closes wild card picker dialog.
   * @param {Object} [context]
   */
  static hideWildPick(context = {}) {
    if (typeof document === 'undefined') return;
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const box = document.getElementById('wildPick');
    if (box) {
      box.classList.remove('on');
      box.innerHTML = '';
      const tableLeft = document.querySelector('.table-left');
      if (tableLeft && box.parentElement !== tableLeft) {
        const statusEl = document.getElementById('status');
        if (statusEl && statusEl.nextSibling) {
          tableLeft.insertBefore(box, statusEl.nextSibling);
        } else {
          tableLeft.appendChild(box);
        }
      }
    }
    const backdrop = document.getElementById('wildPickBackdrop');
    if (backdrop) {
      backdrop.classList.remove('on');
      if (typeof backdrop.remove === 'function') backdrop.remove();
    }
    if (state) {
      state.pendingWild = null;
      state.pendingSuit = null;
    }
  }

  /**
   * Applies consistent styling to wild card selection buttons.
   * @param {HTMLElement} b
   * @param {string} [ink='#1a1208']
   */
  static stylePickBtn(b, ink = '#1a1208') {
    if (!b || !b.style) return;
    b.style.setProperty('color', ink || '#1a1208', 'important');
    b.style.setProperty('-webkit-text-fill-color', ink || '#1a1208', 'important');
    b.style.setProperty('background', '#f4ead4', 'important');
    b.style.setProperty('background-color', '#f4ead4', 'important');
    b.style.setProperty('border', '2px solid #1a1208', 'important');
    b.style.setProperty('color-scheme', 'light', 'important');
  }

  /**
   * Opens wild card picker dialog for choosing suit and rank.
   * @param {Object} c - Target card
   * @param {string} [kind='hand']
   * @param {Object} [context]
   */
  static openWildPick(c, kind = 'hand', context = {}) {
    if (typeof document === 'undefined') return;
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const box = document.getElementById('wildPick');
    if (!box) return;

    const backdrop = document.getElementById('wildPickBackdrop');
    if (backdrop) {
      backdrop.classList.remove('on');
      if (typeof backdrop.remove === 'function') backdrop.remove();
    }

    const fsElem = document.fullscreenElement || document.webkitFullscreenElement;
    const b3d = document.getElementById('board3DContainer');
    if (b3d && (fsElem === b3d || b3d.classList.contains('is-fullscreen'))) {
      if (box.parentElement !== b3d) {
        b3d.appendChild(box);
      }
    }

    if (state) {
      state.pendingWild = c;
      state.pendingSuit = null;
      state.pendingKind = kind || 'hand';
    }
    box.classList.add('on');
    ModalManager.drawWildPick(context);
    setStatus('Joker: pick a suit, then a rank. That card is locked in.');
  }

  /**
   * Draws interactive suit or rank buttons in wild card picker overlay.
   * @param {Object} [context]
   */
  static drawWildPick(context = {}) {
    if (typeof document === 'undefined') return;
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const box = document.getElementById('wildPick');
    if (!box || !state) return;
    box.innerHTML = '';

    const SUIT_SYM = context.SUIT_SYM || (typeof window !== 'undefined' ? window.SUIT_SYM : { S: '♠', H: '♥', C: '♣', D: '♦' });
    const SUIT_NAME = { S: 'Spades', H: 'Hearts', C: 'Clubs', D: 'Diamonds' };
    const SUITS = context.SUITS || (typeof window !== 'undefined' ? window.SUITS : ['S', 'H', 'C', 'D']);
    const RANKS = context.RANKS || (typeof window !== 'undefined' ? window.RANKS : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]);
    const RANK_NAME = context.RANK_NAME || (typeof window !== 'undefined' ? window.RANK_NAME : { 1: 'A', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 8: '8', 9: '9', 10: '10', 11: 'J', 12: 'Q', 13: 'K' });
    const confirmWildAs = context.confirmWildAs || (typeof window !== 'undefined' ? window.confirmWildAs : () => {});

    // Header container
    const header = document.createElement('div');
    header.className = 'wild-pick-header';
    header.innerHTML = `
      <div class="wild-pick-badge">🃏 JOKER REPLACEMENT</div>
      <h3 class="wild-pick-title">${state.pendingSuit ? 'Choose Card Rank' : 'Choose Card Suit'}</h3>
      <p class="wild-pick-subtitle">${state.pendingSuit ? `Suit: <strong style="color: ${(state.pendingSuit === 'H' || state.pendingSuit === 'D') ? '#ef4444' : '#f0c64a'};">${SUIT_SYM[state.pendingSuit]} ${SUIT_NAME[state.pendingSuit]}</strong> — pick any rank:` : 'Select which suit your Joker will become:'}</p>
    `;
    box.appendChild(header);

    if (!state.pendingSuit) {
      const suitRow = document.createElement('div');
      suitRow.className = 'wild-pick-suit-grid';
      for (const su of SUITS) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'wild-pick-suit-btn' + ((su === 'H' || su === 'D') ? ' is-red' : ' is-black');
        b.innerHTML = `
          <span class="wild-suit-icon">${SUIT_SYM[su]}</span>
          <span class="wild-suit-name">${SUIT_NAME[su]}</span>
        `;
        const ink = (su === 'H' || su === 'D') ? '#c0392b' : '#1a1208';
        ModalManager.stylePickBtn(b, ink);
        b.onclick = () => {
          state.pendingSuit = su;
          ModalManager.drawWildPick(context);
        };
        suitRow.appendChild(b);
      }
      box.appendChild(suitRow);
      return;
    }

    // Back to suit selection button
    const backBtn = document.createElement('div');
    backBtn.setAttribute('role', 'button');
    backBtn.setAttribute('tabindex', '0');
    backBtn.className = 'wild-pick-back-btn';
    backBtn.innerHTML = '← Choose Different Suit';
    backBtn.onclick = () => {
      state.pendingSuit = null;
      ModalManager.drawWildPick(context);
    };
    backBtn.onkeydown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        state.pendingSuit = null;
        ModalManager.drawWildPick(context);
      }
    };
    box.appendChild(backBtn);

    const isRed = (state.pendingSuit === 'H' || state.pendingSuit === 'D');
    const ink = isRed ? '#c0392b' : '#1a1208';
    const rankGrid = document.createElement('div');
    rankGrid.className = 'wild-pick-rank-grid';
    for (const r of RANKS) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'wild-pick-rank-btn' + (isRed ? ' is-red' : ' is-black');
      b.innerHTML = `
        <span class="wild-rank-val">${RANK_NAME[r]}</span>
        <span class="wild-rank-suit">${SUIT_SYM[state.pendingSuit]}</span>
      `;
      ModalManager.stylePickBtn(b, ink);
      b.onclick = () => confirmWildAs(r, state.pendingSuit);
      rankGrid.appendChild(b);
    }
    box.appendChild(rankGrid);
  }

  passTo(who, thenFn, context) {
    return ModalManager.passTo(who, thenFn, context);
  }

  hideWildPick(context) {
    return ModalManager.hideWildPick(context);
  }

  stylePickBtn(b, ink) {
    return ModalManager.stylePickBtn(b, ink);
  }

  openWildPick(c, kind, context) {
    return ModalManager.openWildPick(c, kind, context);
  }

  drawWildPick(context) {
    return ModalManager.drawWildPick(context);
  }

  setupCloseButtonEvents(handlers) {
    return ModalManager.setupCloseButtonEvents({
      hideModal: this.hideModal.bind(this),
      showIntro: this.showIntro.bind(this),
      ...handlers
    });
  }

  static showModal(html, closable = false) {
    if (typeof window !== 'undefined' && window.modalManager) {
      return window.modalManager.showModal(html, closable);
    }
    const mgr = new ModalManager();
    return mgr.showModal(html, closable);
  }

  static hideModal(state = null) {
    if (typeof window !== 'undefined' && window.modalManager) {
      return window.modalManager.hideModal(state);
    }
    const mgr = new ModalManager();
    return mgr.hideModal(state);
  }

  static showOverlay(html, closable = false) {
    return ModalManager.showModal(html, closable);
  }

  static hideOverlay() {
    return ModalManager.hideModal();
  }

  static closeModal() {
    return ModalManager.hideModal();
  }

  static closeOverlay() {
    return ModalManager.hideModal();
  }

  static showConfirmModal(opts = {}) {
    if (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.showConfirmModal === 'function') {
      return window.modalManager.showConfirmModal(opts);
    }
    const mgr = new ModalManager();
    return mgr.showConfirmModal(opts);
  }

  static showDiscardTrainerModal(context = {}) {
    if (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.showDiscardTrainerModal === 'function') {
      return window.modalManager.showDiscardTrainerModal(context);
    }
    const mgr = new ModalManager();
    return mgr.showDiscardTrainerModal(context);
  }

  static showPeggingTrainerModal(context = {}) {
    if (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.showPeggingTrainerModal === 'function') {
      return window.modalManager.showPeggingTrainerModal(context);
    }
    const mgr = new ModalManager();
    return mgr.showPeggingTrainerModal(context);
  }

  static showCountingTrainerModal(context = {}) {
    if (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.showCountingTrainerModal === 'function') {
      return window.modalManager.showCountingTrainerModal(context);
    }
    const mgr = new ModalManager();
    return mgr.showCountingTrainerModal(context);
  }

  static showCoachPostMortemModal(context = {}) {
    if (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.showCoachPostMortemModal === 'function') {
      return window.modalManager.showCoachPostMortemModal(context);
    }
    const mgr = new ModalManager();
    return mgr.showCoachPostMortemModal(context);
  }

  static showCoachPostMortemShareModal(report) {
    if (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.showCoachPostMortemShareModal === 'function') {
      return window.modalManager.showCoachPostMortemShareModal(report);
    }
    return CoachPostMortem.openShareDialog(report);
  }

  static shareCoachPostMortem(report) {
    return ModalManager.showCoachPostMortemShareModal(report);
  }

  /**
   * Displays the game summary scorecard modal with hand-by-hand points and running totals.
   * @param {Object} [context]
   */
  showGameSummaryModal(context = {}) {
    const showModalFn = this.showModal.bind(this);
    const state = context.state || (typeof window !== 'undefined' && window.state ? window.state : {});
    const settings = context.settings || (typeof window !== 'undefined' && window.settings ? window.settings : {});
    const html = HandScoreTracker.buildScorecardHtml({ state, settings, ...context });
    const isGameOver = Boolean(state && state.over);
    showModalFn(html, {
      showClose: true,
      onClose: () => {
        if (isGameOver) {
          if (typeof window !== 'undefined' && typeof window.showGameWonModal === 'function' && window.showGameWonModal()) {
            return;
          }
          if (typeof this.showGameWonModal === 'function' && this.showGameWonModal()) {
            return;
          }
        }
        this.hideModal();
      }
    });
    if (typeof HandScoreTracker !== 'undefined' && typeof HandScoreTracker.bindEvents === 'function' && typeof document !== 'undefined') {
      const modalEl = document.getElementById('modal');
      HandScoreTracker.bindEvents(modalEl || document);
    }
  }

  static showGameSummaryModal(context = {}) {
    if (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.showGameSummaryModal === 'function') {
      return window.modalManager.showGameSummaryModal(context);
    }
    const mgr = new ModalManager();
    return mgr.showGameSummaryModal(context);
  }

  showGameWonModal() {
    if (typeof GameEngine !== 'undefined' && typeof GameEngine.showGameWonModal === 'function') {
      return GameEngine.showGameWonModal();
    }
    if (typeof window !== 'undefined' && window.GameEngine && typeof window.GameEngine.showGameWonModal === 'function') {
      return window.GameEngine.showGameWonModal();
    }
    if (typeof window !== 'undefined' && typeof window.showGameWonModal === 'function') {
      return window.showGameWonModal();
    }
    return false;
  }

  static showGameWonModal() {
    if (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.showGameWonModal === 'function') {
      return window.modalManager.showGameWonModal();
    }
    const mgr = new ModalManager();
    return mgr.showGameWonModal();
  }

  /**
   * Displays the Hand of Glory card modal for sharing big hands.
   * @param {string|Object} keyOrData
   * @param {Object} [context]
   */
  showHandOfGloryModal(keyOrData, context = {}) {
    return HandOfGloryCard.showModal(keyOrData, context);
  }

  static showHandOfGloryModal(keyOrData, context = {}) {
    return HandOfGloryCard.showModal(keyOrData, context);
  }

  closeHandOfGloryModal() {
    return HandOfGloryCard.closeModal();
  }

  static closeHandOfGloryModal() {
    return HandOfGloryCard.closeModal();
  }

  /**
   * Displays the confirmation modal when a human player disconnects.
   * @param {string} seatId
   * @param {string} seatName
   * @param {Function} onConfirm Callback if user chooses to replace with computer
   * @param {Function} [onWait] Callback if user chooses to wait
   * @param {Function} [onCancel] Callback if user chooses to cancel game
   * @param {Object} [context]
   */
  showPlayerOfflineModal(seatId, seatName, onConfirm, onWait, onCancel, context = {}) {
    const showModalFn = this.showModal.bind(this);
    const hideModalFn = this.hideModal.bind(this);
    const name = seatName || (seatId ? seatId.toUpperCase() : 'A player');

    const html = `
      <div class="offline-modal-wrap">
        <div class="offline-warning-icon">⚠️</div>
        <h3 class="offline-modal-title">Player Went Offline</h3>
        <p class="offline-modal-desc">
          <strong>${name}</strong> has disconnected from the match.<br>
          Would you like to replace them with a computer player so the game can continue?
        </p>
        <div class="ai-replace-actions">
          <button type="button" class="btn-gold btn-ai-choice" id="btnReplaceYes">🤖 Yes, Replace with Computer</button>
          <button type="button" class="btn-ghost btn-ai-choice" id="btnReplaceWait">⏳ Wait for Reconnection</button>
          <button type="button" class="btn-gold btn-ai-choice" id="btnReplaceCancel">✕ Cancel Game</button>
        </div>
      </div>
    `;

    showModalFn(html, false);

    setTimeout(() => {
      if (typeof document === 'undefined') return;
      const btnYes = document.getElementById('btnReplaceYes');
      const btnWait = document.getElementById('btnReplaceWait');
      const btnCancel = document.getElementById('btnReplaceCancel');
      if (btnYes) {
        btnYes.onclick = () => {
          hideModalFn();
          if (typeof onConfirm === 'function') onConfirm(seatId, name);
        };
      }
      if (btnWait) {
        btnWait.onclick = () => {
          hideModalFn();
          if (typeof onWait === 'function') onWait(seatId, name);
        };
      }
      if (btnCancel) {
        btnCancel.onclick = () => {
          hideModalFn();
          if (typeof onCancel === 'function') {
            onCancel(seatId, name);
          } else if (typeof window !== 'undefined' && typeof window.cancelGameAndReturnToMenu === 'function') {
            window.cancelGameAndReturnToMenu();
          } else {
            try {
              const st = context.state || (typeof window !== 'undefined' ? window.state : (typeof globalThis !== 'undefined' ? globalThis.state : null));
              if (st) st.over = true;
              const camMgr = (typeof window !== 'undefined' && (window._ccCameraManager || (window.CrazyCribbage && window.CrazyCribbage.CameraManager && window.CrazyCribbage.CameraManager.getInstance())));
              if (camMgr && camMgr.isBroadcasting) camMgr.stopBroadcast();
              const chatMgr = (typeof window !== 'undefined' && (window._ccChatManager || (window.CrazyCribbage && window.CrazyCribbage.ChatManager && window.CrazyCribbage.ChatManager.getInstance())));
              if (chatMgr) { chatMgr.setChatVisibility(false); chatMgr.clearChat(); }
            } catch (e) {}
            if (typeof document !== 'undefined') {
              const game = document.getElementById('game');
              const menu = document.getElementById('menu');
              if (game) game.classList.add('hidden');
              if (menu) menu.classList.remove('hidden');
              const waitBar = document.getElementById('reconnectWaitBar');
              if (waitBar) waitBar.remove();
            }
            if (typeof window !== 'undefined' && typeof window.renderRecordBox === 'function') {
              window.renderRecordBox();
            }
          }
        };
      }
    }, 10);
  }

  static showPlayerOfflineModal(seatId, seatName, onConfirm, onWait, onCancel, context = {}) {
    if (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.showPlayerOfflineModal === 'function') {
      return window.modalManager.showPlayerOfflineModal(seatId, seatName, onConfirm, onWait, onCancel, context);
    }
    const mgr = new ModalManager();
    return mgr.showPlayerOfflineModal(seatId, seatName, onConfirm, onWait, onCancel, context);
  }

  /**
   * Displays the AI personality selection modal to pick which computer player replaces a disconnected human.
   * @param {string} seatId
   * @param {string} seatName
   * @param {Function} onSelectAi Callback receiving (profileKey, displayName)
   * @param {Object} [context]
   */
  showPickAiReplacementModal(seatId, seatName, onSelectAi, context = {}) {
    const showModalFn = this.showModal.bind(this);
    const hideModalFn = this.hideModal.bind(this);
    const name = seatName || (seatId ? seatId.toUpperCase() : 'Player');

    const aiProfiles = [
      {
        id: 'shark',
        name: 'The Shark',
        avatar: '🦈',
        level: 'Expert',
        badge: 'Expert · GTO / EV Optimal',
        desc: 'Plays mathematically optimal cribbage. Analyzes discard EV permutations and pegging counts for maximum points.'
      },
      {
        id: 'master',
        name: 'The Grandmaster',
        avatar: '👑',
        level: 'Expert',
        badge: 'Expert · Tournament Precision',
        desc: 'Seasoned tournament champion playing disciplined positional cribbage with deep board-position awareness.'
      },
      {
        id: 'turtler',
        name: 'The Turtler',
        avatar: '🐢',
        level: 'Advanced',
        badge: 'Advanced · Defensive Strategy',
        desc: 'Starves opponent cribs of synergy, refuses to give away 15s or 31s, and controls pegging count securely.'
      },
      {
        id: 'maverick',
        name: 'The Maverick',
        avatar: '⚡',
        level: 'Advanced',
        badge: 'Advanced · Tactical Pressure',
        desc: 'Assertive, high-tempo tactician who pushes pegging runs, sets trap cards, and keeps constant pressure on the board.'
      },
      {
        id: 'gambler',
        name: 'The Gambler',
        avatar: '🎲',
        level: 'Intermediate',
        badge: 'Intermediate · Aggressive & Big Hands',
        desc: 'Chases explosive hands, risk-heavy discards, and aggressive pairing during pegging to create high-scoring action.'
      },
      {
        id: 'regular',
        name: 'The Regular',
        avatar: '☕',
        level: 'Intermediate',
        badge: 'Intermediate · Steady Fundamentals',
        desc: 'Dependable cribbage player following time-tested counting principles, balanced crib discards, and safe leads.'
      },
      {
        id: 'rookie',
        name: 'The Rookie',
        avatar: '🌱',
        level: 'Beginner',
        badge: 'Beginner · Friendly & Relaxed',
        desc: 'Friendly, casual opponent focused on straightforward counting. Perfect for a relaxing, stress-free match.'
      },
      {
        id: 'casual',
        name: 'The Casual',
        avatar: '🍺',
        level: 'Beginner',
        badge: 'Beginner · Easygoing & Learning',
        desc: 'Easygoing, social player who plays simple, natural card combinations without laying counter-traps or tracking crib odds.'
      }
    ];

    let selectedProfile = 'shark';

    const renderCardsHtml = () => {
      return aiProfiles.map(p => `
        <div class="ai-card ${p.id === selectedProfile ? 'selected' : ''}" data-ai-id="${p.id}" tabindex="0" role="button" aria-pressed="${p.id === selectedProfile}">
          <div class="ai-card-avatar">${p.avatar}</div>
          <div class="ai-card-name">${p.name}</div>
          <div class="ai-card-badge">${p.badge}</div>
          <div class="ai-card-desc">${p.desc}</div>
        </div>
      `).join('');
    };

    const html = `
      <div class="offline-modal-wrap">
        <h3 class="offline-modal-title">Choose Computer Player</h3>
        <p class="offline-modal-desc">
          Select which computer personality takes over for <strong>${name}</strong>:
        </p>
        <div class="ai-replace-options" id="aiReplaceCards">
          ${renderCardsHtml()}
        </div>
        <div class="ai-replace-actions">
          <button type="button" class="btn-gold btn-ai-choice" id="btnConfirmAiTakeover">Confirm AI Takeover</button>
          <button type="button" class="btn-ghost btn-ai-choice" id="btnCancelAiTakeover">Cancel</button>
        </div>
      </div>
    `;

    showModalFn(html, true);

    setTimeout(() => {
      if (typeof document === 'undefined') return;
      const container = document.getElementById('aiReplaceCards');
      if (container) {
        container.querySelectorAll('.ai-card').forEach(card => {
          card.onclick = () => {
            const pId = card.getAttribute('data-ai-id');
            if (pId) {
              selectedProfile = pId;
              container.querySelectorAll('.ai-card').forEach(c => {
                const match = c.getAttribute('data-ai-id') === pId;
                c.classList.toggle('selected', match);
                c.setAttribute('aria-pressed', match ? 'true' : 'false');
              });
            }
          };
        });
      }

      const btnConfirm = document.getElementById('btnConfirmAiTakeover');
      const btnCancel = document.getElementById('btnCancelAiTakeover');

      if (btnConfirm) {
        btnConfirm.onclick = () => {
          hideModalFn();
          const chosen = aiProfiles.find(x => x.id === selectedProfile) || aiProfiles[0];
          if (typeof onSelectAi === 'function') {
            onSelectAi(chosen.id, chosen.name);
          }
        };
      }
      if (btnCancel) {
        btnCancel.onclick = () => {
          hideModalFn();
        };
      }
    }, 10);
  }

  static showPickAiReplacementModal(seatId, seatName, onSelectAi, context = {}) {
    if (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.showPickAiReplacementModal === 'function') {
      return window.modalManager.showPickAiReplacementModal(seatId, seatName, onSelectAi, context);
    }
    const mgr = new ModalManager();
    return mgr.showPickAiReplacementModal(seatId, seatName, onSelectAi, context);
  }

  /**
   * Prompts the human pone to guess the rank of the starter card before it is revealed.
   * @param {string} poneSeat
   * @param {function} onGuess
   * @param {Object} [context]
   */
  static openGuessStarterPrompt(poneSeat, onGuess, context = {}) {
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (s) => s);
    const showModal = context.showModal || (typeof window !== 'undefined' && window.modalManager && window.modalManager.showModal ? window.modalManager.showModal.bind(window.modalManager) : (html, closable) => {
      ModalManager.showModal(html, closable);
    });
    const hideModal = context.hideModal || (typeof window !== 'undefined' && window.modalManager && window.modalManager.hideModal ? window.modalManager.hideModal.bind(window.modalManager) : () => {
      ModalManager.hideModal();
    });

    const pTitle = seatName(poneSeat);
    const ranks = [
      { r: 1, name: 'A' },
      { r: 2, name: '2' },
      { r: 3, name: '3' },
      { r: 4, name: '4' },
      { r: 5, name: '5' },
      { r: 6, name: '6' },
      { r: 7, name: '7' },
      { r: 8, name: '8' },
      { r: 9, name: '9' },
      { r: 10, name: '10' },
      { r: 11, name: 'J' },
      { r: 12, name: 'Q' },
      { r: 13, name: 'K' }
    ];

    if (typeof window !== 'undefined') {
      window.selectGuessStarter = (rank) => {
        hideModal();
        if (typeof onGuess === 'function') {
          onGuess(Number(rank));
        }
      };
    }

    const buttonsHtml = ranks.map(item => `
      <button type="button" class="guess-starter-chip" data-rank="${item.r}" aria-label="Guess ${item.name}">
        ${item.name}
      </button>
    `).join('');

    const html = `
      <div class="guess-starter-box">
        <div class="guess-starter-badge">🎯 Bonus: Guess Starter</div>
        <h2 style="margin: 4px 0 10px; font-size: 1.4rem; color: #fff;">Guess the Starter Card!</h2>
        <p style="margin: 0 0 6px; font-size: 1rem; color: #e2e8f0;">
          <strong>${pTitle}</strong>, guess the rank of the cut starter card before it is turned over.
        </p>
        <p style="margin: 0 0 16px; font-size: 0.88rem; color: #94a3b8;">
          Any suit matches! A correct guess scores <strong>+3 points</strong>.
        </p>
        <div class="guess-starter-grid">
          ${buttonsHtml}
        </div>
      </div>
    `;

    showModal(html, false);
    if (typeof document === 'undefined') return;

    const chips = document.querySelectorAll('.guess-starter-chip');
    chips.forEach(btn => {
      btn.addEventListener('click', () => {
        const rank = parseInt(btn.getAttribute('data-rank'), 10);
        hideModal();
        if (typeof onGuess === 'function') {
          onGuess(rank);
        }
      });
    });
  }

  openGuessStarterPrompt(poneSeat, onGuess, context) {
    return ModalManager.openGuessStarterPrompt(poneSeat, onGuess, context);
  }
}



